import React from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
  Select,
  FormControl,
  Backdrop,
  Paper,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Drawer,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Close, DragIndicator } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  getEstimateEasyPrzedmiar,
  putEstimateEasyPrzedmiar,
  postEasyEstimatePrzedmiarGoogleSheet,
  listEasyPrzedmiarVersions,
  createEasyPrzedmiarVersionSnapshot,
  getEasyPrzedmiarVersionSnapshot,
  type EasyPrzedmiarSnapshotPayload,
  type EstimateEasyPrzedmiarRowDto,
  type EstimateFull,
} from '../../services/estimateService';
import { useAuth } from '../../contexts/AuthContext';
import {
  emitEasyEstimateSheetCell,
  emitEasyEstimatePrzedmiarBroadcast,
  onEasyEstimatePrzedmiarBroadcast,
  type EasyEstimateRemoteSheetCell,
} from '../../services/realtimeService';
import { colorForUser } from '../board/useBoardPresence';
import EasyEstimateKosztorysPresenceAvatars from './EasyEstimateKosztorysPresenceAvatars';
import { useEasyEstimateKosztorysPresence } from './useEasyEstimateKosztorysPresence';
import { useSearchParams } from 'react-router-dom';
import {
  enqueuePrzedmiarPdf,
  ensureGemmaPrzedmiarJobRunning,
  fetchGemmaQueueJobs,
  pollGemmaPrzedmiarJobUntilTerminal,
} from '../../services/gemmaQueueApi';
import { useNotification } from '../layouts/Layout';
import PozycjaCalcScratchpad, {
  makeEmptyScratchBundle,
  normalizeScratchGridProp,
  SCRATCH_COLS,
  SCRATCH_ROWS,
  type CalcScratchBundle,
  type ScratchCellStyle,
} from './PozycjaCalcScratchpad';
import type { PozycjeKosztorysStats } from '../../utils/sumNetFromEasyPrzedmiarRows';
import { canonicalizeKnrPodstawaForMatch } from '../../utils/knrPodstawaParse';
import { listVerifiedLibrary } from '../../services/easyEstimateVerifiedLibraryService';

type GemmaHubView = 'menu' | 'przedmiar_pdf' | 'ceny_zatwierdzone' | 'pakiety_prac';

export type EasyEstimatePrzedmiarTabProps = {
  estimate: EstimateFull;
  /** Gdy false — użytkownik nie jest na zakładce Kosztorys (brak subskrypcji obecności). */
  kosztorysTabActive?: boolean;
  /** Roboczogodzina (zł) z Karty Info — w arkuszu KNR w kolumnie „Cena jedn.” dla nakładów R. */
  roboczogodzinaZl?: string;
  /** Suma kolumny „Wartość netto” (pozycje) — np. do paska EasyEstimate. */
  onWartoscNettoSumChange?: (sum: number) => void;
  /** Pozycje z ceną jedn. &gt; 0 / wszystkie wiersze pozycji — pasek EasyEstimate (np. 145/146). */
  onPozycjeStatsChange?: (stats: PozycjeKosztorysStats) => void;
  /** Po utworzeniu powiązania z Google Sheet — odświeżenie nagłówka kosztorysu (URL arkusza). */
  onEstimateSaved?: () => void;
  /** Aktualny przedmiar — do zakładki TES (bez ponownego pobierania z API). */
  onTesPrzedmiarSourceChange?: (source: {
    entries: PrzedmiarTableEntry[];
    cellOverrides: Record<string, string>;
    /** Rozwiązane wartości „Cena jedn.” (formuły `=F4` rozwinięte) — zakładka TES używa ich do sum działów. */
    resolvedCenaJednByEntryId?: Map<string, string>;
  }) => void;
  /** Suma pionowego zaznaczenia w kolumnach Ilość / Wartość netto (tylko pozycje) — pasek EasyEstimate. */
  onNumericColumnSelectionSumChange?: (payload: {
    show: boolean;
    sum: number;
    /** `quantity` — kolumna Ilość (bez „zł”); `currency` — Wartość netto. */
    selectionKind: 'quantity' | 'currency';
  }) => void;
  /** Osobna strona „Pozycje zweryfikowane” — tylko Szukaj, bez akcji w wierszach, bez zapisu. */
  layoutVariant?: 'default' | 'verifiedList';
  /**
   * Przedmiar wczytany poza komponentem (np. agregat wielu kosztorysów) — bez GET /estimates/:id/easy-przedmiar i bez listy wersji.
   * Wymaga `layoutVariant="verifiedList"` i `kosztorysTabActive={false}`.
   */
  externalPrzedmiarSnapshot?: {
    entries: PrzedmiarTableEntry[];
    cellOverrides?: Record<string, string>;
    calcScratchBundles?: Record<string, CalcScratchBundle>;
  };
  /**
   * Callback zapisu zmian scratchpada dla pozycji z biblioteki pozycji zweryfikowanych (`id` zaczynający się od `lib:`).
   * Wywoływany po każdej modyfikacji siatki scratchpada w trybie `verifiedList + externalPrzedmiarSnapshot`.
   */
  onExternalScratchBundleChange?: (entryId: string, bundle: CalcScratchBundle) => void;
  /**
   * Callback zapisu nadpisania komórki (np. `cenaJedn`) dla pozycji z biblioteki. Używany przez przycisk „Wstaw do Cena jedn.".
   */
  onExternalCenaJednChange?: (entryId: string, formatted: string) => void;
  /**
   * Wartość wyszukiwania sterowana z zewnątrz (np. toolbar strony). Gdy podane — wewnętrzny input „Szukaj" nie jest renderowany.
   */
  externalSearch?: string;
  /** Wywoływane przy każdej zmianie wyszukiwania, gdy używamy `externalSearch`. */
  onExternalSearchChange?: (next: string) => void;
  /**
   * Callback rejestrujący uchwyt sterowania panelem „Dostosuj kolumny" — pozwala otworzyć panel
   * z zewnętrznego toolbar'a (`verifiedList`). Wywoływany z `null` po unmount'cie.
   */
  onColumnsPanelHandleReady?: (handle: EasyEstimatePrzedmiarColumnsHandle | null) => void;
  /**
   * Callback otwierający edycję branży dla wiersza biblioteki zweryfikowanych (z menu wiersza „⋮").
   * `entryId` zawiera pełny identyfikator wiersza (np. `lib:42`). Opcjonalny — gdy nie podany,
   * pozycja menu „Edytuj branżę…" nie jest pokazywana.
   */
  onMenuEditBranza?: (entryId: string) => void;
};

/** Uchwyt sterowania panelem kolumn, przekazywany przez `onColumnsPanelHandleReady`. */
export type EasyEstimatePrzedmiarColumnsHandle = {
  /** Otwiera panel „Dostosuj kolumny" (z domyślną pozycją — prawy dolny róg). */
  open: () => void;
  /** Zamyka panel i cofa tryb edycji kolumn. */
  close: () => void;
  /** `true`, gdy panel jest aktualnie otwarty (przydatne do zaznaczenia przycisku w toolbar'ze). */
  isOpen: boolean;
};

/** Nagłówek działu — jeden wiersz na całą szerokość tabeli (jak w PDF przedmiaru). */
export type PrzedmiarDzialRow = {
  kind: 'dzial';
  id: string;
  /** Tekst widoczny w arkuszu (np. wielkie litery) */
  tytul: string;
  /** Wartość zgodna z filtrem / listą działów */
  dzialKey: string;
  /** Numeracja hierarchiczna WBS (np. 1, 1.1, 1.2.1) — suma/filtr „gałęzi”; puste = zachowanie jak wcześniej (do następnego nagłówka). */
  outlineNr?: string;
  /** Opcjonalnie: Lp. z importu PDF (np. 1, 1.1, 1.1.1) — inaczej w kolumnie Lp widać lpDzialu 1…n */
  lpZrodlowy?: string;
};

/** Zwykła pozycja przedmiaru */
export type PrzedmiarPozycjaRow = {
  kind: 'pozycja';
  id: string;
  /** Numeracja jak w PDF (np. 1, 1.1, 1.d.1.1) */
  lp: string;
  podstawa: string;
  opis: string;
  jm: string;
  ilosc: string;
  /** Cena jednostkowa — w UI zawsze 2 miejsca po przecinku (pl, np. 125,50) */
  cenaJedn: string;
  uwagi: string;
  /** Branża — etykieta do wyświetlenia (np. „Budowlana"). Ustawiana tylko dla wierszy biblioteki
   * zweryfikowanych; w zwykłym kosztorysie kolumna jest ukryta. Pusty string = brak branży. */
  branza?: string;
  /** Dział (grupa) — do filtrowania; wiersz działu powyżej określa kontekst */
  dzial: string;
  /** Data zatwierdzenia (YYYY-MM-DD) — z bazy / menu kontekstowego. */
  approvedAt?: string;
  approvedByName?: string;
  approvedByUserId?: string;
};

export type PrzedmiarTableEntry = PrzedmiarDzialRow | PrzedmiarPozycjaRow;

const DZIAL_OUTLINE_DB_MAX = 64;

/** Kanoniczna forma WBS: tylko segmenty `\d+(\.\d+)*`, max 64 znaki. */
function normalizeWbsOutlineString(raw: unknown): string {
  const t = String(raw ?? '').trim();
  if (!t || t.length > DZIAL_OUTLINE_DB_MAX) return '';
  if (!/^\d+(?:\.\d+)*$/.test(t)) return '';
  return t;
}

/**
 * Jedna ścieżka WBS na wiersz. Pełny string `1.2.1` — OK. Lista z przecinkami (np. wklejka z PDF) —
 * bierzemy ostatni poprawny token (najgłębszy poziom w typowej enumeracji).
 * @returns `''` = wyczyść WBS, poprawny napis = zapis, `null` = wejście niepuste i nadal niepoprawne
 */
function parseWbsFromLooseUserInput(raw: string): string | '' | null {
  const t0 = raw.trim();
  if (!t0) return '';
  const direct = normalizeWbsOutlineString(t0);
  if (direct) return direct;
  const parts = t0
    .split(/[,;]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  let lastOk = '';
  for (const p of parts) {
    const n = normalizeWbsOutlineString(p);
    if (n) lastOk = n;
  }
  if (lastOk) return lastOk;
  const m = t0.match(/\d+(?:\.\d+)*/);
  if (m) {
    const n = normalizeWbsOutlineString(m[0]);
    if (n) return n;
  }
  return null;
}

function clampDzialOutlineNrDb(s: string): string {
  const n = normalizeWbsOutlineString(s);
  if (!n) return '';
  return n.length <= DZIAL_OUTLINE_DB_MAX ? n : n.slice(0, DZIAL_OUTLINE_DB_MAX);
}

function wbsOutlineSegments(s: string): number[] {
  if (!normalizeWbsOutlineString(s)) return [];
  return s.split('.').map((x) => parseInt(x, 10));
}

/** Czy `leaf` jest `ancestor` lub głębszym potomkiem (porównanie segmentów — nie myli 1.2 z 1.20). */
function wbsIsAncestorOrEqual(ancestor: string, leaf: string): boolean {
  const a = wbsOutlineSegments(ancestor);
  const b = wbsOutlineSegments(leaf);
  if (!a.length || !b.length) return false;
  if (a.length > b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!Number.isFinite(a[i]) || !Number.isFinite(b[i]) || a[i] !== b[i]) return false;
  }
  return true;
}

/** Wyciąga WBS z początku tytułu (np. „1.2 Opis”) albo z pola jawnego `explicit`. */
function inferWbsOutlineFromTytul(rawTitle: string, explicit?: string): { tytul: string; outlineNr?: string } {
  const ex = normalizeWbsOutlineString(explicit);
  const t0 = rawTitle.trim();
  if (ex) {
    const re = new RegExp(`^\\s*${ex.replace(/\./g, '\\.')}\\s+`);
    const stripped = t0.replace(re, '').trim();
    return { tytul: stripped || t0, outlineNr: ex };
  }
  const m = t0.match(/^(\d+(?:\.\d+)*)\s+/);
  if (!m) return { tytul: t0 };
  const o = normalizeWbsOutlineString(m[1]);
  if (!o) return { tytul: t0 };
  const rest = t0.slice(m[0].length).trim();
  return { tytul: rest || t0, outlineNr: o };
}

/** Głębokość w hierarchii (1 = rozdział główny) — z `outlineNr` albo z `lpZrodlowy` w formacie WBS. */
function dzialHierarchyDepth(entry: PrzedmiarDzialRow): number {
  const on = normalizeWbsOutlineString(entry.outlineNr);
  if (on) return Math.min(12, Math.max(1, on.split('.').length));
  const lp = String(entry.lpZrodlowy ?? '').trim();
  if (lp && /^\d+(?:\.\d+)*$/.test(lp)) return Math.min(12, Math.max(1, lp.split('.').length));
  return 1;
}

/** H1…H6 — najgłębszy poziom w dokumencie zawsze H6, wyżej coraz „wyższa” warstwa palety (bottom-up). */
const DZIAL_WBS_LEVEL_SWATCHES = [
  { bg: '#2B3A67', textPrimary: '#FFFFFF' },
  { bg: '#4A7FB5', textPrimary: '#FFFFFF' },
  { bg: '#7BA68A', textPrimary: '#202124' },
  { bg: '#D4B896', textPrimary: '#202124' },
  { bg: '#C5CAE9', textPrimary: '#202124' },
  { bg: '#E8B4A2', textPrimary: '#202124' },
] as const;

type DzialHierarchyVisual = {
  bg: string;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  cellGrid: string;
  gutterRule: string;
  selectionInset: string;
  selectionStroke: string;
  focusOutline: string;
  iconAccent: string;
};

function maxDzialHierarchyDepthInEntries(entries: PrzedmiarTableEntry[]): number {
  let m = 0;
  for (const e of entries) {
    if (e.kind === 'dzial') m = Math.max(m, dzialHierarchyDepth(e));
  }
  return Math.max(1, m);
}

/** Tylko poziom główny (1, 2, 3…) — brak poddziałów w stylu 1.1, 1.2 w WBS / lp źródłowym. */
function przedmiarWbsHierarchyIsFlat(entries: PrzedmiarTableEntry[]): boolean {
  return maxDzialHierarchyDepthInEntries(entries) <= 1;
}

/** Indeks 0…5 w palecie H1…H6: gdy maxDepth=3 → głębokości 1,2,3 → H4,H5,H6. */
function dzialBottomUpPaletteIndex(depth: number, maxDepth: number): number {
  const d = Math.min(Math.max(depth, 1), 12);
  const maxD = Math.max(1, maxDepth);
  return Math.max(0, Math.min(DZIAL_WBS_LEVEL_SWATCHES.length - 1, d + (DZIAL_WBS_LEVEL_SWATCHES.length - 1) - maxD));
}

function dzialHierarchyVisual(depth: number, maxDepth: number): DzialHierarchyVisual {
  const idx = dzialBottomUpPaletteIndex(depth, maxDepth);
  const { bg, textPrimary } = DZIAL_WBS_LEVEL_SWATCHES[idx];
  const lightOnDark = textPrimary === '#FFFFFF';
  return {
    bg,
    textPrimary,
    textSecondary: lightOnDark ? 'rgba(255,255,255,0.88)' : '#5F6368',
    textDisabled: lightOnDark ? 'rgba(255,255,255,0.52)' : '#9aa0a6',
    cellGrid: lightOnDark ? 'rgba(255,255,255,0.16)' : 'rgba(32, 33, 36, 0.13)',
    gutterRule: lightOnDark ? 'rgba(255,255,255,0.22)' : 'rgba(32, 33, 36, 0.16)',
    selectionInset: lightOnDark ? 'inset 0 0 0 9999px rgba(255,255,255,0.1)' : 'inset 0 0 0 9999px rgba(0, 0, 0, 0.055)',
    selectionStroke: lightOnDark ? 'rgba(255,255,255,0.5)' : 'rgba(32, 33, 36, 0.4)',
    focusOutline: lightOnDark ? '1px solid rgba(255,255,255,0.58)' : '1px solid rgba(32, 33, 36, 0.36)',
    iconAccent: lightOnDark ? 'rgba(255,255,255,0.95)' : '#020242',
  };
}

/** Podsekcje (WBS głębiej niż jeden segment): bez koloru hierarchii — białe tło jak zwykły arkusz. */
function dzialSublevelNeutralVisual(): DzialHierarchyVisual {
  return {
    bg: '#ffffff',
    textPrimary: '#202124',
    textSecondary: '#5F6368',
    textDisabled: '#9aa0a6',
    cellGrid: 'rgba(32, 33, 36, 0.13)',
    gutterRule: 'rgba(32, 33, 36, 0.16)',
    selectionInset: 'inset 0 0 0 9999px rgba(0, 0, 0, 0.055)',
    selectionStroke: 'rgba(32, 33, 36, 0.4)',
    focusOutline: '1px solid rgba(32, 33, 36, 0.36)',
    iconAccent: '#020242',
  };
}

/**
 * Kanoniczne nazwy działów przy imporcie (PDF / JSON) — dopasowanie i domyślne „Inne”.
 * W filtrze Select pokazywane są tylko działy występujące w bieżących danych tabeli.
 */
const DZIALY_FILTRU: string[] = [
  'Konstrukcja',
  'Izolacje',
  'Wykończenia',
  'Elektryka',
  'Instalacje sanitarne i gaz',
  'Wentylacja i klimatyzacja',
  'Roboty rozbiórkowe',
  'Ochrona obiektu',
  'Stolarka otworowa',
  'Roboty ziemne',
  'Nawierzchnie i mała architektura',
  'Rusztowania i obudowy',
  'Zieleń',
  'Konserwacja zabytków',
  'Roboty przygotowawcze',
  'Inne',
];

function pozycjaMatchesSearch(p: PrzedmiarPozycjaRow, q: string): boolean {
  if (!q) return true;
  const wn = computeWartoscNettoPl(p);
  const blob = `${p.lp} ${p.podstawa} ${p.opis} ${p.jm} ${p.ilosc} ${p.cenaJedn} ${wn} ${p.uwagi} ${p.branza ?? ''} ${p.dzial}`.toLowerCase();
  return blob.includes(q);
}

/** Etykieta sekcji jak w tabeli (nagłówek) — do filtra, nie kanoniczny słownik typu „Elektryka”. */
function sectionFilterLabel(h: PrzedmiarDzialRow): string {
  return h.tytul.trim() || h.dzialKey.trim() || 'Inne';
}

function firstDzialHeaderBySectionLabel(all: PrzedmiarTableEntry[], label: string): PrzedmiarDzialRow | null {
  for (const e of all) {
    if (e.kind === 'dzial' && sectionFilterLabel(e) === label) return e;
  }
  return null;
}

function lastDzialHeaderBeforePozycjaId(
  all: PrzedmiarTableEntry[],
  pozId: string
): PrzedmiarDzialRow | null {
  let last: PrzedmiarDzialRow | null = null;
  for (const e of all) {
    if (e.kind === 'dzial') last = e;
    if (e.kind === 'pozycja' && e.id === pozId) return last;
  }
  return last;
}

/** Dopasowanie filtra działu: etykieta z nagłówka (tytuł), zapisane pole pozycji lub kanoniczny klucz. */
function pozycjaMatchesDzialFilter(
  all: PrzedmiarTableEntry[],
  p: PrzedmiarPozycjaRow,
  dzialFilter: string
): boolean {
  if (dzialFilter === 'wszystkie') return true;
  const h = lastDzialHeaderBeforePozycjaId(all, p.id);
  const label = h ? sectionFilterLabel(h) : p.dzial.trim() || 'Inne';
  if (label === dzialFilter) return true;
  if (p.dzial.trim() === dzialFilter) return true;
  if (h) {
    if (h.dzialKey.trim() === dzialFilter) return true;
    if (h.tytul.trim() === dzialFilter) return true;
  }
  const root = firstDzialHeaderBySectionLabel(all, dzialFilter);
  const rootOutline = root ? normalizeWbsOutlineString(root.outlineNr) : '';
  const leafOutline = h ? normalizeWbsOutlineString(h.outlineNr) : '';
  if (rootOutline && leafOutline && wbsIsAncestorOrEqual(rootOutline, leafOutline)) return true;
  return false;
}

/** Limit jak kolumna pozycja_dzial w DB (VARCHAR 255). */
function clampPozycjaDzialDb(s: string): string {
  const t = s.trim();
  if (t.length <= 255) return t;
  return t.slice(0, 255);
}

export function filterPrzedmiarTable(
  all: PrzedmiarTableEntry[],
  dzialFilter: string,
  search: string,
  /** Bez wierszy nagłówkowych działów — tylko pozycje (dział nadal w danych wiersza). */
  flatView: boolean
): PrzedmiarTableEntry[] {
  const q = search.trim().toLowerCase();

  if (flatView) {
    const only = all.filter((e): e is PrzedmiarPozycjaRow => e.kind === 'pozycja');
    if (dzialFilter !== 'wszystkie') {
      return only.filter(
        (p) => pozycjaMatchesDzialFilter(all, p, dzialFilter) && pozycjaMatchesSearch(p, q)
      );
    }
    return only.filter((p) => pozycjaMatchesSearch(p, q));
  }

  /** Konkretny dział: rzeczywiste nagłówki z gałęzi (np. 1 → 1.1 → pozycje), nie jeden sztuczny nagłówek + płaska lista. */
  if (dzialFilter !== 'wszystkie') {
    const root = firstDzialHeaderBySectionLabel(all, dzialFilter);
    const rootIdx = root
      ? all.findIndex((e) => e.kind === 'dzial' && (e as PrzedmiarDzialRow).id === root.id)
      : -1;
    if (root && rootIdx >= 0) {
      const rootOutline = normalizeWbsOutlineString(root.outlineNr);
      const out: PrzedmiarTableEntry[] = [];
      for (let i = rootIdx; i < all.length; i++) {
        const e = all[i];
        if (e.kind === 'dzial') {
          const d = e as PrzedmiarDzialRow;
          if (i === rootIdx) {
            out.push(d);
            continue;
          }
          if (rootOutline) {
            const dOutline = normalizeWbsOutlineString(d.outlineNr);
            if (dOutline) {
              if (!wbsIsAncestorOrEqual(rootOutline, dOutline)) break;
            } else if (dzialHierarchyDepth(d) <= 1 && d.id !== root.id) {
              break;
            }
          } else if (dzialHierarchyDepth(d) <= 1 && d.id !== root.id) {
            break;
          }
          out.push(d);
          continue;
        }
        const p = e as PrzedmiarPozycjaRow;
        if (pozycjaMatchesDzialFilter(all, p, dzialFilter) && pozycjaMatchesSearch(p, q)) {
          out.push(p);
        }
      }
      if (out.length > 0) return out;
      return [root];
    }

    const kept = all
      .filter((e): e is PrzedmiarPozycjaRow => e.kind === 'pozycja')
      .filter((p) => pozycjaMatchesDzialFilter(all, p, dzialFilter) && pozycjaMatchesSearch(p, q));
    if (kept.length === 0) return [];
    const header: PrzedmiarDzialRow = {
      kind: 'dzial',
      id: `filter-${dzialFilter}`,
      tytul: dzialFilter.toUpperCase(),
      dzialKey: dzialFilter,
    };
    return [header, ...kept];
  }

  /** Wszystkie — sekcje z oryginalnej kolejności; pod nagłówkiem tylko pozycje pasujące do wyszukiwania. */
  const out: PrzedmiarTableEntry[] = [];
  let i = 0;
  /** Pozycje występujące przed pierwszym działem (np. samo „Dodaj pozycję”) — wcześniej były gubione. */
  while (i < all.length && all[i].kind === 'pozycja') {
    const p = all[i] as PrzedmiarPozycjaRow;
    if (pozycjaMatchesSearch(p, q)) out.push(p);
    i++;
  }
  while (i < all.length) {
    const e = all[i];
    if (e.kind !== 'dzial') {
      i++;
      continue;
    }
    const header = e;
    const pozycje: PrzedmiarPozycjaRow[] = [];
    i++;
    while (i < all.length && all[i].kind === 'pozycja') {
      pozycje.push(all[i] as PrzedmiarPozycjaRow);
      i++;
    }
    const kept = pozycje.filter((p) => pozycjaMatchesSearch(p, q));
    /** Pusty dział (np. świeżo dodany) musi być widoczny mimo braku pozycji. */
    if (kept.length > 0 || pozycje.length === 0) {
      out.push(header);
      kept.forEach((p) => out.push(p));
    }
  }
  return out;
}

/** Tylko pozycje z `approvedAt` (ikona verified w Akcjach); nagłówki działów zostają, jeśli mają ≥1 taką pozycję. */
export function filterEntriesToApprovedPozycjeOnly(entries: PrzedmiarTableEntry[]): PrzedmiarTableEntry[] {
  if (entries.length === 0) return entries;
  if (entries.every((e) => e.kind === 'pozycja')) {
    return entries.filter((e) => Boolean((e as PrzedmiarPozycjaRow).approvedAt));
  }
  const out: PrzedmiarTableEntry[] = [];
  let i = 0;
  while (i < entries.length && entries[i].kind === 'pozycja') {
    const p = entries[i] as PrzedmiarPozycjaRow;
    if (p.approvedAt) out.push(p);
    i++;
  }
  while (i < entries.length) {
    const e = entries[i];
    if (e.kind !== 'dzial') {
      i++;
      continue;
    }
    const header = e as PrzedmiarDzialRow;
    i++;
    const pozycje: PrzedmiarPozycjaRow[] = [];
    while (i < entries.length && entries[i].kind === 'pozycja') {
      const p = entries[i] as PrzedmiarPozycjaRow;
      if (p.approvedAt) pozycje.push(p);
      i++;
    }
    if (pozycje.length > 0) {
      out.push(header);
      pozycje.forEach((p) => out.push(p));
    }
  }
  return out;
}

function newPrzedmiarEntryId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeDzialKeyForImport(raw: string): string {
  const t = String(raw || '').trim();
  if (DZIALY_FILTRU.includes(t)) return t;
  const lower = t.toLowerCase();
  const hit = DZIALY_FILTRU.find((d) => d.toLowerCase() === lower);
  if (hit) return hit;
  return 'Inne';
}

/** Liczba z pola ceny: przecinek lub kropka jako separator dziesiętny, spacje / tys. ignorowane wg ostatniego separatora. */
function parseLoosePlDecimal(raw: string): number | null {
  const t = raw.trim().replace(/\u00a0/g, '').replace(/\s/g, '');
  if (!t) return null;
  const lastComma = t.lastIndexOf(',');
  const lastDot = t.lastIndexOf('.');
  let normalized: string;
  if (lastComma === -1 && lastDot === -1) {
    normalized = t;
  } else if (lastComma > lastDot) {
    const intPart = t.slice(0, lastComma).replace(/\./g, '');
    const fracPart = t.slice(lastComma + 1).replace(/,/g, '');
    normalized = `${intPart}.${fracPart}`;
  } else {
    normalized = t.replace(/,/g, '');
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/** Zaokrąglenie do 2 miejsc po przecinku (jak wartość wiersza w kosztorysie). */
function roundMoney2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Kwota w PL: 2 miejsca po przecinku, separator tysięcy jako zwykła spacja (np. 6 400,00). */
function formatPlMoney2(n: number): string {
  const s = n.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });
  return s.replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ');
}

/** Cena jedn. z wartości i ilości: wartość / ilość, potem 2 miejsca — potem wartość wiersza = ilość × ta cena (round2). */
function cenaJednFromWartoscIlosc(wartosc: number, ilosc: number): number {
  if (!Number.isFinite(ilosc) || ilosc === 0) return NaN;
  return roundMoney2(wartosc / ilosc);
}

/** Po zapisie / imporcie: pusto albo liczba z dokładnie 2 miejscami po przecinku (pl-PL); nieparsowalne — bez zmian. */
function formatCenaJednCommitted(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  const n = parseLoosePlDecimal(t);
  if (n === null) return t;
  return formatPlMoney2(n);
}

/** Wyświetlanie / start edycji: pusto albo sformatowana cena; nieparsowalne — oryginał. */
function formatCenaJednDisplayed(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  const n = parseLoosePlDecimal(t);
  if (n === null) return raw.trim();
  return formatPlMoney2(n);
}

const ILOSC_DISPLAY_DECIMALS = 3;

/** Ilość: zawsze 3 miejsca po przecinku, pl-PL (spacja tys., przecinek dziesiętny), np. 2 940,00 → 2 940,000. */
function formatIloscDisplayed(raw: string): string {
  const t0 = raw.trim();
  if (!t0) return '';
  const n = parseLoosePlDecimal(t0);
  if (n === null) return t0;
  const d = ILOSC_DISPLAY_DECIMALS;
  const factor = 10 ** d;
  const rounded = Math.round(n * factor) / factor;
  const s = rounded.toLocaleString('pl-PL', {
    useGrouping: true,
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
  return s.replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ');
}

/** Suma pionowego zaznaczenia kolumny „Ilość” w dolnym pasku — ta sama skala co komórki. */
export function formatPrzedmiarSelectionIloscSum(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const d = ILOSC_DISPLAY_DECIMALS;
  const factor = 10 ** d;
  const rounded = Math.round(n * factor) / factor;
  return rounded
    .toLocaleString('pl-PL', {
      useGrouping: true,
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    })
    .replace(/\u202f/g, ' ')
    .replace(/\u00a0/g, ' ');
}

function formatIloscCommitted(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  if (parseLoosePlDecimal(t) === null) return t;
  return formatIloscDisplayed(t);
}

/**
 * Formuły komórek w kolumnie „Cena jedn.” (prosty tryb Excel-like):
 * `=<Litera><NrWiersza>` np. `=F4` — wartość z kolumny F (Cena jedn.) wiersza nr 4 (gutter).
 * Litery odpowiadają stałemu schematowi kolumn przedmiaru (A..H), niezależnie od aktualnej widoczności kolumn.
 * Wiersz = numer z lewego „gutter” (aktualna kolejność dokumentu po filtrze). Wspierany jest łańcuch referencji
 * (=F4, gdzie F4 też ma formułę) i detekcja cyklu (zwraca pusty ciąg).
 */
const PRZEDMIAR_FORMULA_COL_LETTER_TO_KEY: Record<string, PrzedmiarColKey> = {
  A: 'lp',
  B: 'podstawa',
  C: 'opis',
  D: 'jm',
  E: 'ilosc',
  F: 'cenaJedn',
  G: 'wartoscNetto',
  H: 'uwagi',
};

const PRZEDMIAR_FORMULA_SINGLE_REF_RE = /^=\s*([A-Ha-h])\s*(\d{1,6})\s*$/;

export function isPrzedmiarCellFormula(raw: string | null | undefined): boolean {
  return typeof raw === 'string' && raw.trim().startsWith('=');
}

function parsePrzedmiarCellFormula(
  raw: string
): { col: PrzedmiarColKey; rowNr: number } | null {
  if (!isPrzedmiarCellFormula(raw)) return null;
  const m = raw.trim().match(PRZEDMIAR_FORMULA_SINGLE_REF_RE);
  if (!m) return null;
  const letter = m[1].toUpperCase();
  const col = PRZEDMIAR_FORMULA_COL_LETTER_TO_KEY[letter];
  const rowNr = Number(m[2]);
  if (!col || !Number.isFinite(rowNr) || rowNr <= 0) return null;
  return { col, rowNr };
}

/** Kolumny dostępne w narzędziu „Znajdź i zamień” (tylko pozycje; bez Lp. i Wartości netto). */
const PRZEDMIAR_FIND_REPLACE_COLS = ['podstawa', 'opis', 'jm', 'ilosc', 'cenaJedn', 'uwagi'] as const;
type PrzedmiarFindReplaceCol = (typeof PRZEDMIAR_FIND_REPLACE_COLS)[number];
const PRZEDMIAR_FIND_REPLACE_COL_LABEL: Record<PrzedmiarFindReplaceCol, string> = {
  podstawa: 'Podstawa',
  opis: 'Opis',
  jm: 'J.m.',
  ilosc: 'Ilość',
  cenaJedn: 'Cena jedn.',
  uwagi: 'Uwagi',
};

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Zamiana wszystkich wystąpień `find` → `replace` w łańcuchu, z uwzględnieniem wielkości liter. */
function replaceAllInString(src: string, find: string, replace: string, caseSensitive: boolean): string {
  if (!find) return src;
  if (caseSensitive) return src.split(find).join(replace);
  return src.replace(new RegExp(escapeRegex(find), 'gi'), replace);
}

/**
 * Buduje mapę `entryId → rozwinięta „Cena jedn.”` (bez formuł, gotowa do wyświetlenia i obliczeń).
 * Zwraca pusty ciąg dla wpisów, których formuła nie rozwiązuje się (brak wiersza, cykl, nieparsowalna referencja).
 */
export function buildPrzedmiarCenaJednResolver(
  tableEntries: PrzedmiarTableEntry[],
  cellOverrides: Record<string, string>,
  docRowNrToEntryId: Map<number, string>
): Map<string, string> {
  const byId = new Map<string, PrzedmiarTableEntry>();
  for (const e of tableEntries) byId.set(e.id, e);

  const readRawCenaJedn = (e: PrzedmiarPozycjaRow): string => {
    const ov = cellOverrides[`${e.id}:cenaJedn`];
    return (ov !== undefined ? ov : e.cenaJedn) ?? '';
  };
  const readRawIlosc = (e: PrzedmiarPozycjaRow): string => {
    const ov = cellOverrides[`${e.id}:ilosc`];
    return (ov !== undefined ? ov : e.ilosc) ?? '';
  };

  /** Rozwiązuje wartość „Cena jedn.” dla podanego wpisu — obsługuje łańcuchy referencji i cykle. */
  const resolveCena = (entryId: string, visited: Set<string>): string => {
    if (visited.has(entryId)) return '';
    const entry = byId.get(entryId);
    if (!entry || entry.kind !== 'pozycja') return '';
    const raw = readRawCenaJedn(entry);
    if (!isPrzedmiarCellFormula(raw)) return raw;
    const parsed = parsePrzedmiarCellFormula(raw);
    if (!parsed) return '';
    const refId = docRowNrToEntryId.get(parsed.rowNr);
    if (!refId) return '';
    const refEntry = byId.get(refId);
    if (!refEntry || refEntry.kind !== 'pozycja') return '';

    const nextVisited = new Set(visited);
    nextVisited.add(entryId);

    if (parsed.col === 'cenaJedn') {
      return resolveCena(refId, nextVisited);
    }
    if (parsed.col === 'ilosc') {
      return readRawIlosc(refEntry);
    }
    if (parsed.col === 'wartoscNetto') {
      const iloscRaw = readRawIlosc(refEntry).trim();
      const cenaRaw = resolveCena(refId, nextVisited).trim();
      if (!iloscRaw || !cenaRaw) return '';
      const iq = parseLoosePlDecimal(iloscRaw);
      const cq = parseLoosePlDecimal(cenaRaw);
      if (iq === null || cq === null) return '';
      return String(roundMoney2(iq * cq));
    }
    switch (parsed.col) {
      case 'lp':
        return String(refEntry.lp ?? '');
      case 'podstawa':
        return refEntry.podstawa ?? '';
      case 'opis':
        return refEntry.opis ?? '';
      case 'jm':
        return refEntry.jm ?? '';
      case 'uwagi':
        return refEntry.uwagi ?? '';
      default:
        return '';
    }
  };

  const out = new Map<string, string>();
  for (const e of tableEntries) {
    if (e.kind !== 'pozycja') continue;
    out.set(e.id, resolveCena(e.id, new Set()));
  }
  return out;
}

function effectivePozycjaField(
  entry: PrzedmiarPozycjaRow,
  field: 'ilosc' | 'cenaJedn',
  cellOverrides?: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): string {
  if (field === 'cenaJedn' && resolvedCenaJednByEntryId) {
    const r = resolvedCenaJednByEntryId.get(entry.id);
    if (r !== undefined) return r;
  }
  if (cellOverrides) {
    const o = cellOverrides[`${entry.id}:${field}`];
    if (o !== undefined) return o;
  }
  return field === 'ilosc' ? entry.ilosc : entry.cenaJedn;
}

/** Ilość × cena jedn., zaokrąglenie do 2 miejsc po przecinku (pl-PL); pusto gdy brak obu składników lub nie da się sparsować. */
function computeWartoscNettoPl(
  entry: PrzedmiarPozycjaRow,
  cellOverrides?: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): string {
  const iloscRaw = effectivePozycjaField(entry, 'ilosc', cellOverrides).trim();
  const cenaRaw = effectivePozycjaField(entry, 'cenaJedn', cellOverrides, resolvedCenaJednByEntryId).trim();
  if (!iloscRaw || !cenaRaw) return '';
  const iq = parseLoosePlDecimal(iloscRaw);
  const cq = parseLoosePlDecimal(cenaRaw);
  if (iq === null || cq === null) return '';
  const product = iq * cq;
  const rounded = roundMoney2(product);
  return formatPlMoney2(rounded);
}

/** Wartość liczbowa do sortowania po „Wartość netto” (ta sama logika co wyświetlana kwota z ilości × cena). */
function pozycjaNettoNumericForSort(
  e: PrzedmiarPozycjaRow,
  cellOverrides: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): number | null {
  const iloscRaw = effectivePozycjaField(e, 'ilosc', cellOverrides).trim();
  const cenaRaw = effectivePozycjaField(e, 'cenaJedn', cellOverrides, resolvedCenaJednByEntryId).trim();
  if (!iloscRaw || !cenaRaw) return null;
  const iq = parseLoosePlDecimal(iloscRaw);
  const cq = parseLoosePlDecimal(cenaRaw);
  if (iq === null || cq === null) return null;
  return roundMoney2(iq * cq);
}

function sortPozycjaBlockByNetto(
  block: PrzedmiarPozycjaRow[],
  cellOverrides: Record<string, string>,
  dir: 'asc' | 'desc',
  resolvedCenaJednByEntryId?: Map<string, string>
): PrzedmiarPozycjaRow[] {
  const mult = dir === 'asc' ? 1 : -1;
  return [...block].sort((a, b) => {
    const na = pozycjaNettoNumericForSort(a, cellOverrides, resolvedCenaJednByEntryId);
    const nb = pozycjaNettoNumericForSort(b, cellOverrides, resolvedCenaJednByEntryId);
    const aEmpty = na === null;
    const bEmpty = nb === null;
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (na !== nb) return mult * (na - nb);
    return 0;
  });
}

/**
 * Sortuje wyłącznie ciągi wierszy pozycji (nagłówki działów zostają; pod każdym nagłówkiem — osobno),
 * tak jak w strukturze widoku po `filterPrzedmiarTable`.
 */
function sortPrzedmiarTableEntriesByNetto(
  rows: PrzedmiarTableEntry[],
  cellOverrides: Record<string, string>,
  dir: 'asc' | 'desc',
  resolvedCenaJednByEntryId?: Map<string, string>
): PrzedmiarTableEntry[] {
  const out: PrzedmiarTableEntry[] = [];
  let i = 0;
  while (i < rows.length) {
    const e = rows[i];
    if (e.kind === 'dzial') {
      out.push(e);
      i++;
      const block: PrzedmiarPozycjaRow[] = [];
      while (i < rows.length && rows[i].kind === 'pozycja') {
        block.push(rows[i] as PrzedmiarPozycjaRow);
        i++;
      }
      out.push(...sortPozycjaBlockByNetto(block, cellOverrides, dir, resolvedCenaJednByEntryId));
    } else {
      const block: PrzedmiarPozycjaRow[] = [];
      while (i < rows.length && rows[i].kind === 'pozycja') {
        block.push(rows[i] as PrzedmiarPozycjaRow);
        i++;
      }
      out.push(...sortPozycjaBlockByNetto(block, cellOverrides, dir, resolvedCenaJednByEntryId));
    }
  }
  return out;
}

type KosztorysRowSortMode = 'default' | 'nettoDesc' | 'nettoAsc';

/** Suma „Wartość netto” po wszystkich pozycjach (jak suma zaokrąglonych wierszy w tabeli). */
function sumWartoscNettoPozycji(
  entries: PrzedmiarTableEntry[],
  cellOverrides: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): number {
  let sum = 0;
  for (const e of entries) {
    if (e.kind !== 'pozycja') continue;
    const iloscRaw = effectivePozycjaField(e, 'ilosc', cellOverrides).trim();
    const cenaRaw = effectivePozycjaField(e, 'cenaJedn', cellOverrides, resolvedCenaJednByEntryId).trim();
    if (!iloscRaw || !cenaRaw) continue;
    const iq = parseLoosePlDecimal(iloscRaw);
    const cq = parseLoosePlDecimal(cenaRaw);
    if (iq === null || cq === null) continue;
    sum += roundMoney2(iq * cq);
  }
  return roundMoney2(sum);
}

/** Zgodnie z `countPozycjeWithCenaJednInEasyPrzedmiar` / kolumną „Pozycje” na liście kosztorysów (cena &gt; 0). */
function pozycjeStatsFromEntries(
  entries: PrzedmiarTableEntry[],
  cellOverrides: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): PozycjeKosztorysStats {
  let total = 0;
  let withCenaJedn = 0;
  let uniqueTotal = 0;
  const seenKeys = new Set<string>();
  const normText = (s: string) =>
    (s ?? '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('pl-PL');
  for (const e of entries) {
    if (e.kind !== 'pozycja') continue;
    total++;
    const cenaRaw = effectivePozycjaField(e, 'cenaJedn', cellOverrides, resolvedCenaJednByEntryId).trim();
    if (cenaRaw) {
      const v = parseLoosePlDecimal(cenaRaw);
      if (v !== null && v > 0) withCenaJedn++;
    }
    const podstawa = normText(cellOverrides[`${e.id}:podstawa`] ?? e.podstawa);
    const opis = normText(cellOverrides[`${e.id}:opis`] ?? e.opis);
    if (!podstawa && !opis) {
      uniqueTotal++;
      continue;
    }
    const key = `${podstawa}\u001f${opis}`;
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      uniqueTotal++;
    }
  }
  return { withCenaJedn, total, uniqueTotal };
}

/**
 * Pozycje przypisane do nagłówka działu (ta sama logika co suma w komórce „Wartość netto” wiersza działu).
 * Gdy nagłówek ma `outlineNr` (WBS) — wszystkie pozycje, których ostatni nagłówek działu ma WBS w tej samej gałęzi.
 * Gdy brak WBS — legacja: tylko pozycje do następnego wiersza `dzial` w kolejności dokumentu.
 */
function pozycjeUnderDzialHeader(rows: PrzedmiarTableEntry[], headerIndex: number): PrzedmiarPozycjaRow[] {
  const header = rows[headerIndex];
  if (!header || header.kind !== 'dzial') return [];
  const oRoot = normalizeWbsOutlineString(header.outlineNr);
  if (oRoot) {
    const pozycje: PrzedmiarPozycjaRow[] = [];
    for (const e of rows) {
      if (e.kind !== 'pozycja') continue;
      const L = lastDzialHeaderBeforePozycjaId(rows, e.id);
      if (!L) continue;
      const oLeaf = normalizeWbsOutlineString(L.outlineNr);
      if (oLeaf && wbsIsAncestorOrEqual(oRoot, oLeaf)) pozycje.push(e);
    }
    return pozycje;
  }
  const pozycje: PrzedmiarPozycjaRow[] = [];
  for (let j = headerIndex + 1; j < rows.length; j++) {
    const e = rows[j];
    if (e.kind === 'dzial') break;
    if (e.kind === 'pozycja') pozycje.push(e);
  }
  return pozycje;
}

/** Suma „Wartość netto” pod nagłówkiem działu — jak `pozycjeUnderDzialHeader` + suma pozycji. */
function sumWartoscNettoDzialUnderHeader(
  rows: PrzedmiarTableEntry[],
  headerIndex: number,
  cellOverrides: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): number {
  return sumWartoscNettoPozycji(
    pozycjeUnderDzialHeader(rows, headerIndex),
    cellOverrides,
    resolvedCenaJednByEntryId
  );
}

/** Wiersz tabeli TES (Tabela Elementów Scalonych) — jeden nagłówek działu / poddziału. */
export type EasyEstimateTesTableRow = {
  dzialEntryId: string;
  /**
   * Lp. jak w kolumnie „Lp.” wiersza działu w kosztorysie (WBS / lp z importu / lpDzialu 1…n),
   * z uwzględnieniem nadpisania komórki `…:dzialLp`.
   */
  lpWyswietlane: string;
  nazwaDzialu: string;
  /** Zakres Lp. z kolumny kosztorysu (np. „1 - 20”), zgodnie z kolejnością pozycji w grupie. */
  pozycjeLpZakres: string;
  netto: number;
  /** Jak pozycje w menu filtra działu (`dzialFilterMenuItemVizByLabel`): WBS głęb. ≤ 1 = „rodzic”. */
  isParentDzial: boolean;
  /** Tło wiersza — z palety H1–H6 dla rodzica, `#fff` dla poddziału. */
  rowBg: string;
  /** Kolor tekstu wiersza (np. biel na ciemnym tle rodzica). */
  rowTextPrimary: string;
};

/** Ta sama treść co kolumna Lp. w `PrzedmiarSheetDzialBodyRowMemo` (rola `dl`). */
function dzialLpColumnDisplayForTes(
  entry: PrzedmiarDzialRow,
  lpDzialu: number,
  cellOverrides: Record<string, string>
): string {
  const ovr = cellOverrides[`${entry.id}:dzialLp`];
  if (ovr !== undefined) return ovr.trim();
  const on = normalizeWbsOutlineString(entry.outlineNr);
  if (on) return on;
  const z = entry.lpZrodlowy;
  return z != null && z !== '' ? z : String(lpDzialu);
}

function effectiveLpDisplayForTes(p: PrzedmiarPozycjaRow, cellOverrides: Record<string, string>): string {
  const k = `${p.id}:lp`;
  if (cellOverrides[k] !== undefined) return cellOverrides[k].trim();
  return String(p.lp).trim();
}

function formatTesLpZakres(pozycje: PrzedmiarPozycjaRow[], cellOverrides: Record<string, string>): string {
  if (pozycje.length === 0) return '—';
  const lps = pozycje.map((x) => effectiveLpDisplayForTes(x, cellOverrides)).filter((s) => s.length > 0);
  if (lps.length === 0) return '—';
  const a = lps[0];
  const b = lps[lps.length - 1];
  return a === b ? a : `${a} - ${b}`;
}

/** Wiersze TES: po jednym na każdy wiersz `dzial` w dokumencie (działy i poddziały). */
export function buildEasyEstimateTesTableRows(
  rows: PrzedmiarTableEntry[],
  cellOverrides: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): EasyEstimateTesTableRow[] {
  const out: EasyEstimateTesTableRow[] = [];
  const maxDepth = maxDzialHierarchyDepthInEntries(rows);
  let lpDzialu = 0;
  const flatWbs = przedmiarWbsHierarchyIsFlat(rows);
  for (let i = 0; i < rows.length; i++) {
    const e = rows[i];
    if (e.kind !== 'dzial') continue;
    lpDzialu += 1;
    const depth = dzialHierarchyDepth(e);
    const isParentDzial = depth <= 1;
    const viz = flatWbs
      ? dzialSublevelNeutralVisual()
      : isParentDzial
        ? dzialHierarchyVisual(1, maxDepth)
        : dzialSublevelNeutralVisual();
    const pozycje = pozycjeUnderDzialHeader(rows, i);
    out.push({
      dzialEntryId: e.id,
      lpWyswietlane: dzialLpColumnDisplayForTes(e, lpDzialu, cellOverrides),
      nazwaDzialu: sectionFilterLabel(e),
      pozycjeLpZakres: formatTesLpZakres(pozycje, cellOverrides),
      netto: sumWartoscNettoDzialUnderHeader(rows, i, cellOverrides, resolvedCenaJednByEntryId),
      isParentDzial,
      rowBg: viz.bg,
      rowTextPrimary: viz.textPrimary,
    });
  }
  return out;
}

/** Suma pod nagłówkiem działu po `id` wiersza — zawsze wg pełnej kolejności dokumentu (`fullRows`). */
function sumWartoscNettoForDzialEntryId(
  fullRows: PrzedmiarTableEntry[],
  dzialEntryId: string,
  cellOverrides: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): number {
  const headerIndex = fullRows.findIndex((e) => e.id === dzialEntryId && e.kind === 'dzial');
  if (headerIndex < 0) return 0;
  return sumWartoscNettoDzialUnderHeader(fullRows, headerIndex, cellOverrides, resolvedCenaJednByEntryId);
}

/**
 * Suma „Wartość netto” dla etykiety filtra działu — jak w komórce sumy wiersza nagłówka (`dwn`),
 * a gdy brak nagłówka: suma pozycji „sierot” z tym `pozycja.dzial` / „Inne”.
 */
function nettoForDzialFilterLabel(
  rows: PrzedmiarTableEntry[],
  label: string,
  cellOverrides: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): number {
  for (let i = 0; i < rows.length; i++) {
    const e = rows[i];
    if (e.kind !== 'dzial') continue;
    if (sectionFilterLabel(e) === label) {
      return sumWartoscNettoDzialUnderHeader(rows, i, cellOverrides, resolvedCenaJednByEntryId);
    }
  }
  let sum = 0;
  let lastHeader: PrzedmiarDzialRow | null = null;
  for (const e of rows) {
    if (e.kind === 'dzial') {
      lastHeader = e;
      continue;
    }
    if (e.kind !== 'pozycja') continue;
    if (lastHeader) continue;
    const lab = e.dzial.trim() || 'Inne';
    if (lab === label) sum += sumWartoscNettoPozycji([e], cellOverrides, resolvedCenaJednByEntryId);
  }
  return Math.round(sum * 100) / 100;
}

function formatKwotaPl2(n: number): string {
  return formatPlMoney2(n);
}

const DZIAL_FILTER_NAME_MAX_CHARS = 32;

function truncateDzialFilterName(name: string): string {
  if (name.length <= DZIAL_FILTER_NAME_MAX_CHARS) return name;
  return `${name.slice(0, DZIAL_FILTER_NAME_MAX_CHARS)}…`;
}

function scratchStylesPayloadNonEmpty(styles: ScratchCellStyle[][]): boolean {
  return styles.some((row) =>
    row.some((s) => !!(s.bold || s.italic || s.underline || s.backgroundColor || s.color))
  );
}

function buildEasyPrzedmiarRowsPayload(
  entries: PrzedmiarTableEntry[],
  cellOverrides: Record<string, string>,
  calcScratchBundles: Record<string, CalcScratchBundle>
): unknown[] {
  const rows: unknown[] = [];
  for (const e of entries) {
    if (e.kind === 'dzial') {
      const on = normalizeWbsOutlineString(e.outlineNr);
      rows.push({
        rowType: 'dzial',
        dzialTitle: e.tytul,
        dzialKey: e.dzialKey,
        ...(on ? { dzialOutlineNr: clampDzialOutlineNrDb(on) } : {}),
        extras: {
          clientId: e.id,
          ...(e.lpZrodlowy ? { lpZrodlowy: e.lpZrodlowy } : {}),
        },
      });
    } else {
      const prefix = `${e.id}:`;
      const patch: Record<string, string> = {};
      for (const [k, v] of Object.entries(cellOverrides)) {
        if (k.startsWith(prefix)) {
          const field = k.slice(prefix.length);
          if (field) patch[field] = v;
        }
      }
      const extras: Record<string, unknown> = { clientId: e.id, cenaJedn: e.cenaJedn };
      if (Object.keys(patch).length) extras.cellPatch = patch;
      const bundle = calcScratchBundles[e.id];
      if (bundle) {
        extras.calcScratchGrid = bundle.wlasne.grid;
        if (scratchStylesPayloadNonEmpty(bundle.wlasne.cellStyles)) {
          extras.calcScratchCellStyles = bundle.wlasne.cellStyles;
        }
        extras.calcScratchSheets = {
          wlasne: {
            grid: bundle.wlasne.grid,
            ...(scratchStylesPayloadNonEmpty(bundle.wlasne.cellStyles)
              ? { cellStyles: bundle.wlasne.cellStyles }
              : {}),
          },
          knr: {
            grid: bundle.knr.grid,
            ...(scratchStylesPayloadNonEmpty(bundle.knr.cellStyles) ? { cellStyles: bundle.knr.cellStyles } : {}),
          },
          gemma: {
            grid: bundle.gemma.grid,
            ...(scratchStylesPayloadNonEmpty(bundle.gemma.cellStyles)
              ? { cellStyles: bundle.gemma.cellStyles }
              : {}),
          },
        };
      }
      rows.push({
        rowType: 'pozycja',
        lp: e.lp,
        podstawa: e.podstawa,
        opis: e.opis,
        jm: e.jm,
        ilosc: e.ilosc,
        uwagi: e.uwagi,
        pozycjaDzial: e.dzial,
        pozycjaApprovedAt: e.approvedAt ?? null,
        pozycjaApprovedByName: e.approvedByName ?? null,
        pozycjaApprovedByUserId: e.approvedByUserId ?? null,
        extras,
      });
    }
  }
  return rows;
}

function parseScratchCellStylesArray(raw: unknown): ScratchCellStyle[][] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const slice = raw.slice(0, SCRATCH_ROWS);
  const out: ScratchCellStyle[][] = [];
  for (let r = 0; r < slice.length; r++) {
    const row = slice[r];
    if (!Array.isArray(row) || row.length !== SCRATCH_COLS) return null;
    const outRow: ScratchCellStyle[] = [];
    for (let c = 0; c < SCRATCH_COLS; c++) {
      const o = row[c];
      if (!o || typeof o !== 'object') {
        outRow.push({});
        continue;
      }
      const rec = o as Record<string, unknown>;
      const s: ScratchCellStyle = {};
      if (rec.bold === true) s.bold = true;
      if (rec.italic === true) s.italic = true;
      if (rec.underline === true) s.underline = true;
      if (typeof rec.backgroundColor === 'string' && rec.backgroundColor) s.backgroundColor = rec.backgroundColor;
      if (typeof rec.color === 'string' && rec.color) s.color = rec.color;
      outRow.push(s);
    }
    out.push(outRow);
  }
  while (out.length < SCRATCH_ROWS) {
    out.push(Array.from({ length: SCRATCH_COLS }, () => ({})));
  }
  return out;
}

function parseScratchCellStylesFromExtras(ex: Record<string, unknown>): ScratchCellStyle[][] | null {
  return parseScratchCellStylesArray(ex.calcScratchCellStyles);
}

/** Data zatwierdzenia z API (DATE / ISO) → YYYY-MM-DD. */
function apiPozycjaApprovedDateToYyyyMmDd(v: unknown): string | undefined {
  if (v == null || v === '') return undefined;
  const s = String(v).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : undefined;
}

function localIsoDateOnly(d = new Date()): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

function hydrateCalcScratchBundleFromExtras(ex: Record<string, unknown>): CalcScratchBundle {
  const bundle = makeEmptyScratchBundle();
  const legacyGrid = ex.calcScratchGrid;
  if (Array.isArray(legacyGrid) && legacyGrid.length) {
    bundle.wlasne.grid = normalizeScratchGridProp(legacyGrid as string[][]);
  }
  const legacySt = parseScratchCellStylesFromExtras(ex);
  if (legacySt) bundle.wlasne.cellStyles = legacySt;

  const nested = ex.calcScratchSheets;
  if (nested && typeof nested === 'object' && nested !== null && !Array.isArray(nested)) {
    const obj = nested as Record<string, unknown>;
    for (const sid of ['wlasne', 'knr', 'gemma'] as const) {
      const part = obj[sid];
      if (!part || typeof part !== 'object' || Array.isArray(part)) continue;
      const rec = part as Record<string, unknown>;
      if (Array.isArray(rec.grid) && rec.grid.length) {
        bundle[sid].grid = normalizeScratchGridProp(rec.grid as string[][]);
      }
      const st = parseScratchCellStylesArray(rec.cellStyles);
      if (st) bundle[sid].cellStyles = st;
    }
  }
  return bundle;
}

export function parseEasyPrzedmiarApiRows(rows: EstimateEasyPrzedmiarRowDto[]): {
  entries: PrzedmiarTableEntry[];
  cellOverrides: Record<string, string>;
  calcScratchBundles: Record<string, CalcScratchBundle>;
} {
  const entries: PrzedmiarTableEntry[] = [];
  const cellOverrides: Record<string, string> = {};
  const calcScratchBundles: Record<string, CalcScratchBundle> = {};
  /** Gdy w DB brak pozycja_dzial — pozycje przypisujemy do ostatniego nagłówka działu (kolejność sort_order). */
  let currentDzialFromHeader = 'Inne';
  for (const r of rows) {
    const rt = String(r.rowType ?? r.row_type ?? '')
      .trim()
      .toLowerCase();
    const ex = (r.extras && typeof r.extras === 'object' ? r.extras : {}) as Record<string, unknown>;
    if (rt === 'dzial') {
      const id = typeof ex.clientId === 'string' && ex.clientId ? ex.clientId : newPrzedmiarEntryId('dzial');
      const lpZ = ex.lpZrodlowy != null ? String(ex.lpZrodlowy).trim() : '';
      const title = String(r.dzialTitle ?? r.dzial_title ?? '').trim();
      const keyRaw = String(r.dzialKey ?? r.dzial_key ?? '').trim();
      const dzialKey = keyRaw || title || 'Inne';
      const outlineRaw = String(r.dzialOutlineNr ?? r.dzial_outline_nr ?? ex.dzialOutlineNr ?? '').trim();
      const outlineNr = normalizeWbsOutlineString(outlineRaw) || '';
      /** W filtrze i na pozycjach wolisz widzieć nagłówek z PDF, nie skrót ze słownika (np. Elektryka). */
      currentDzialFromHeader = clampPozycjaDzialDb(title || dzialKey);
      entries.push({
        kind: 'dzial',
        id,
        tytul: title || dzialKey.toUpperCase(),
        dzialKey,
        ...(outlineNr ? { outlineNr } : {}),
        ...(lpZ ? { lpZrodlowy: lpZ } : {}),
      });
    } else if (rt === 'pozycja') {
      const id = typeof ex.clientId === 'string' && ex.clientId ? ex.clientId : newPrzedmiarEntryId('poz');
      const cenaRaw = ex.cenaJedn != null ? String(ex.cenaJedn) : '';
      const pdRaw = String(r.pozycjaDzial ?? r.pozycja_dzial ?? '').trim();
      const approvedAt = apiPozycjaApprovedDateToYyyyMmDd(r.pozycjaApprovedAt ?? r.pozycja_approved_at);
      const abnRaw = r.pozycjaApprovedByName ?? r.pozycja_approved_by_name;
      const abuRaw = r.pozycjaApprovedByUserId ?? r.pozycja_approved_by_user_id;
      const approvedByName =
        typeof abnRaw === 'string' && abnRaw.trim() ? abnRaw.trim() : undefined;
      const approvedByUserId =
        typeof abuRaw === 'string' && abuRaw.trim() ? abuRaw.trim() : undefined;
      entries.push({
        kind: 'pozycja',
        id,
        lp: r.lp != null && String(r.lp).trim() ? String(r.lp).trim() : '1',
        podstawa: String(r.podstawa ?? ''),
        opis: String(r.opis ?? ''),
        jm: String(r.jm ?? ''),
        ilosc: String(r.ilosc ?? ''),
        cenaJedn: formatCenaJednCommitted(cenaRaw),
        uwagi: String(r.uwagi ?? ''),
        dzial: clampPozycjaDzialDb(pdRaw || currentDzialFromHeader),
        ...(approvedAt
          ? {
              approvedAt,
              ...(approvedByName ? { approvedByName } : {}),
              ...(approvedByUserId ? { approvedByUserId } : {}),
            }
          : {}),
      });
      const patch = ex.cellPatch;
      if (patch && typeof patch === 'object' && patch !== null) {
        for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
          if (typeof v === 'string') cellOverrides[`${id}:${k}`] = v;
        }
      }
      calcScratchBundles[id] = hydrateCalcScratchBundleFromExtras(ex);
    }
  }
  return { entries, cellOverrides, calcScratchBundles };
}

/** Wycina pierwszy kompletny obiekt JSON `{...}` z uwzględnieniem stringów (nie używa lastIndexOf — produkcyjne modele zwracają zagnieżdżenia). */
function sliceFirstBalancedJsonObject(s: string, from = 0): string | null {
  const start = s.indexOf('{', from);
  if (start < 0) return null;
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === '\\') esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') {
      inStr = true;
      continue;
    }
    if (c === '{') depth += 1;
    else if (c === '}') {
      depth -= 1;
      if (depth === 0) return s.slice(start, i + 1);
    }
  }
  return null;
}

function tryParseJsonLenient(jsonSlice: string): unknown | null {
  const t = jsonSlice.trim();
  if (!t) return null;
  try {
    return JSON.parse(t);
  } catch {
    try {
      return JSON.parse(t.replace(/,\s*([}\]])/g, '$1'));
    } catch {
      return null;
    }
  }
}

/**
 * Gdy model osiągnie max_tokens, JSON bywa ucięty w środku — pełny parse pada.
 * Wyciągamy kolejne kompletne obiekty z tablicy `entries` (lub aliasów), żeby zaimportować choć prefiks wierszy.
 */
function extractEntryObjectsFromTruncatedEntriesArray(s: string): unknown[] | null {
  const re =
    /"(entries|items|rows|lines|pozycje|data|przedmiar|wiersze)"\s*:\s*\[/i;
  const m = re.exec(s);
  if (!m || m.index === undefined) return null;
  let i = m.index + m[0].length;
  const out: unknown[] = [];
  while (i < s.length) {
    while (i < s.length && /[\s,\n\r]/.test(s[i])) i++;
    if (i >= s.length) break;
    if (s[i] !== '{') break;
    const sl = sliceFirstBalancedJsonObject(s, i);
    if (!sl) break;
    const obj = tryParseJsonLenient(sl);
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) out.push(obj);
    i += sl.length;
  }
  return out.length ? out : null;
}

/** Rozpoznaje tablicę wierszy niezależnie od nazwy klucza (produkcja / inne modele). */
function pickPrzedmiarEntriesArray(parsed: unknown): unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (!parsed || typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;
  const keys = [
    'entries',
    'items',
    'rows',
    'lines',
    'pozycje',
    'data',
    'przedmiar',
    'wiersze',
    'result',
    'response',
  ];
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = pickPrzedmiarEntriesArray(v);
      if (inner) return inner;
    }
  }
  return null;
}

function normalizePrzedmiarImportKind(raw: unknown): 'dzial' | 'pozycja' | null {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (['dzial', 'section', 'chapter', 'naglowek', 'header', 'sekcja'].includes(s)) return 'dzial';
  if (['pozycja', 'item', 'line', 'wiersz', 'row', 'poz'].includes(s)) return 'pozycja';
  return null;
}

function inferKindFromShape(o: Record<string, unknown>): 'dzial' | 'pozycja' | null {
  const hasTytul = String(o.tytul ?? o.title ?? o.naglowek ?? '').trim().length > 0;
  const hasDzialKey = String(o.dzialKey ?? o.dzial_key ?? '').trim().length > 0;
  const hasOutlineField =
    normalizeWbsOutlineString(o.outlineNr ?? o.outline_nr ?? o.wbs ?? o.numeracja) !== '';
  const hasLp = o.lp != null && String(o.lp).trim() !== '';
  const hasOpis = String(o.opis ?? o.description ?? o.opis_pozycji ?? '').trim().length > 0;
  const hasPodst = String(o.podstawa ?? o.podst ?? o.basis ?? '').trim().length > 0;
  const hasQty = String(o.ilosc ?? o.quantity ?? o.ilosc_pozycji ?? '').trim().length > 0;
  const hasJm = String(o.jm ?? o.j_m ?? o.unit ?? o.jednostka ?? '').trim().length > 0;
  const hasCena = String(o.cenaJedn ?? o.cena_jedn ?? o.cenaJednostkowa ?? o.unit_price ?? o.cena ?? '').trim().length > 0;
  const hasWart = String(
    o.wartoscNetto ?? o.wartosc_netto ?? o.wartosc ?? o.wartoscBrutto ?? o.value ?? o.total ?? o.razem ?? ''
  ).trim().length > 0;
  if (
    hasOpis ||
    hasPodst ||
    hasQty ||
    hasJm ||
    hasCena ||
    hasWart ||
    (hasLp && !hasTytul && !hasDzialKey && !hasOutlineField)
  )
    return 'pozycja';
  if (hasTytul || hasDzialKey || hasOutlineField) return 'dzial';
  return null;
}

type PrzedmiarGemmaParseResult = {
  entries: PrzedmiarTableEntry[] | null;
  /** True gdy pełny JSON się nie sparsował, ale udało się wyciągnąć kompletne obiekty z tablicy (np. ucięty stream). */
  recoveredPartial: boolean;
};

function mapGemmaRawArrayToPrzedmiarEntries(arr: unknown[]): PrzedmiarTableEntry[] | null {
  const out: PrzedmiarTableEntry[] = [];
  let pozIdx = 0;
  /** Etykieta sekcji dla pozycji (nagłówek z PDF, nie skrót ze słownika). */
  let inheritLabelForPozycja = 'Inne';
  for (const item of arr) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    let kind = normalizePrzedmiarImportKind(o.kind ?? o.type ?? o.typ ?? o.rowType);
    if (!kind) kind = inferKindFromShape(o);
    if (!kind) continue;

    if (kind === 'dzial') {
      const dzialKey = normalizeDzialKeyForImport(
        String(o.dzialKey ?? o.dzial_key ?? o.kategoria ?? o.category ?? '')
      );
      const rawTytul =
        String(o.tytul ?? o.title ?? o.naglowek ?? o.name ?? '').trim() || dzialKey.toUpperCase();
      const explicitOut = String(o.outlineNr ?? o.outline_nr ?? o.wbs ?? o.numeracja ?? '').trim();
      const { tytul, outlineNr: inferredOutline } = inferWbsOutlineFromTytul(rawTytul, explicitOut);
      const finalTytul = (tytul || dzialKey.toUpperCase()).trim();
      inheritLabelForPozycja = clampPozycjaDzialDb(finalTytul || dzialKey);
      const lpZ = o.lp != null ? String(o.lp).trim() : '';
      const outlineStore = clampDzialOutlineNrDb(inferredOutline || '');
      out.push({
        kind: 'dzial',
        id: newPrzedmiarEntryId('dzial'),
        tytul: finalTytul,
        dzialKey,
        ...(outlineStore ? { outlineNr: outlineStore } : {}),
        ...(lpZ ? { lpZrodlowy: lpZ } : {}),
      });
    } else {
      const rawDzialField = String(o.dzial ?? o.dzialKey ?? o.dzial_key ?? o.section ?? '').trim();
      const normalizedFromField = rawDzialField ? normalizeDzialKeyForImport(rawDzialField) : '';
      const dzial = clampPozycjaDzialDb(normalizedFromField || inheritLabelForPozycja);
      const lpRaw = o.lp != null ? String(o.lp).trim() : '';
      const lp = lpRaw || String(pozIdx + 1);
      pozIdx += 1;
      const iloscStr = String(o.ilosc ?? o.quantity ?? o.ilosc_pozycji ?? '');
      let cenaIn = String(
        o.cenaJedn ?? o.cena_jedn ?? o.cenaJednostkowa ?? o.unit_price ?? o.cena ?? ''
      ).trim();
      const wartIn = String(
        o.wartoscNetto ?? o.wartosc_netto ?? o.wartosc ?? o.wartoscBrutto ?? o.value ?? o.total ?? o.razem ?? ''
      ).trim();
      if (!cenaIn && wartIn) {
        const w = parseLoosePlDecimal(wartIn);
        const q = parseLoosePlDecimal(iloscStr);
        if (w != null && q != null && q !== 0) {
          const cj = cenaJednFromWartoscIlosc(w, q);
          if (Number.isFinite(cj)) cenaIn = formatPlMoney2(cj);
        }
      }
      out.push({
        kind: 'pozycja',
        id: newPrzedmiarEntryId('poz'),
        lp,
        podstawa: String(o.podstawa ?? o.podst ?? o.basis ?? ''),
        opis: String(o.opis ?? o.description ?? o.opis_pozycji ?? ''),
        jm: String(o.jm ?? o.j_m ?? o.unit ?? o.jednostka ?? ''),
        ilosc: iloscStr,
        cenaJedn: formatCenaJednCommitted(cenaIn),
        uwagi: '',
        dzial,
      });
    }
  }
  return out.length > 0 ? out : null;
}

/** Odpowiedź Gemmy → wiersze + informacja, czy import był z uciętego JSON. */
function parsePrzedmiarGemmaJsonResult(rawText: string): PrzedmiarGemmaParseResult {
  let unfenced = String(rawText || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '');

  let jsonStr = sliceFirstBalancedJsonObject(unfenced, 0);
  if (!jsonStr) {
    const start = unfenced.indexOf('{');
    const end = unfenced.lastIndexOf('}');
    if (start < 0 || end <= start) {
      const looseOnly = extractEntryObjectsFromTruncatedEntriesArray(unfenced);
      const entries = looseOnly?.length ? mapGemmaRawArrayToPrzedmiarEntries(looseOnly) : null;
      return { entries, recoveredPartial: Boolean(entries?.length) };
    }
    jsonStr = unfenced.slice(start, end + 1);
  }

  let parsed: unknown = tryParseJsonLenient(jsonStr);
  if (!parsed && jsonStr) {
    const second = sliceFirstBalancedJsonObject(unfenced, unfenced.indexOf('{', 1));
    if (second) parsed = tryParseJsonLenient(second);
  }

  let arr: unknown[] | null = null;
  let recoveredPartial = false;

  if (parsed != null) {
    arr = pickPrzedmiarEntriesArray(parsed);
  }

  if (!arr || arr.length === 0) {
    const loose = extractEntryObjectsFromTruncatedEntriesArray(unfenced);
    if (loose && loose.length) {
      arr = loose;
      recoveredPartial = true;
    }
  }

  if (!arr || arr.length === 0) return { entries: null, recoveredPartial: false };

  const entries = mapGemmaRawArrayToPrzedmiarEntries(arr);
  if (!entries?.length) return { entries: null, recoveredPartial: false };
  return { entries, recoveredPartial };
}

function inferDzialForNewPozycja(prev: PrzedmiarTableEntry[]): string {
  for (let i = prev.length - 1; i >= 0; i--) {
    const e = prev[i];
    if (e.kind === 'pozycja') return e.dzial;
    if (e.kind === 'dzial') return clampPozycjaDzialDb(sectionFilterLabel(e));
  }
  return 'Inne';
}

function nextPozycjaLp(prev: PrzedmiarTableEntry[]): string {
  const poz = prev.filter((e): e is PrzedmiarPozycjaRow => e.kind === 'pozycja');
  let maxN = 0;
  for (const p of poz) {
    const s = String(p.lp).trim().replace(',', '.');
    if (/^\d+(\.0+)?$/.test(s)) {
      const n = Math.floor(Number(s));
      if (Number.isFinite(n) && n > maxN) maxN = n;
    }
  }
  if (maxN > 0) return String(maxN + 1);
  return String(Math.max(1, poz.length + 1));
}

/** Wstawia nową pozycję zaraz pod wierszem o `anchorId` (dział lub pozycja). */
function insertPozycjaRowBelowAnchor(entries: PrzedmiarTableEntry[], anchorId: string): PrzedmiarTableEntry[] {
  const idx = entries.findIndex((e) => e.id === anchorId);
  if (idx < 0) return entries;
  const anchor = entries[idx];
  const dzial =
    anchor.kind === 'pozycja' ? anchor.dzial : clampPozycjaDzialDb(sectionFilterLabel(anchor));
  const newRow: PrzedmiarPozycjaRow = {
    kind: 'pozycja',
    id: newPrzedmiarEntryId('poz'),
    lp: nextPozycjaLp(entries),
    podstawa: '',
    opis: '',
    jm: '',
    ilosc: '',
    cenaJedn: '',
    uwagi: '',
    dzial,
  };
  const next = entries.slice();
  next.splice(idx + 1, 0, newRow);
  return next;
}

/** Unikalna nazwa działu względem istniejących kluczy (filtr + przypisanie pozycji). */
function uniqueDzialName(prev: PrzedmiarTableEntry[], base: string): string {
  const used = new Set<string>();
  for (const e of prev) {
    if (e.kind === 'dzial') used.add(e.dzialKey);
    if (e.kind === 'pozycja') used.add(e.dzial);
  }
  let name = base;
  let n = 2;
  while (used.has(name)) {
    name = `${base} (${n})`;
    n += 1;
  }
  return name;
}

/** Zmiana tytułu działu + zsynchronizowanie `dzial` u pozycji w tym samym bloku (do następnego działu). */
function updateDzialSectionTitle(entries: PrzedmiarTableEntry[], dzialEntryId: string, newTitle: string): PrzedmiarTableEntry[] {
  const idx = entries.findIndex((e) => e.id === dzialEntryId && e.kind === 'dzial');
  if (idx < 0) return entries;
  const header = entries[idx];
  if (header.kind !== 'dzial') return entries;
  const oldKey = header.dzialKey;
  const next = entries.slice();
  next[idx] = { ...header, tytul: newTitle, dzialKey: newTitle };
  for (let j = idx + 1; j < next.length; j++) {
    const e = next[j];
    if (e.kind === 'dzial') break;
    if (e.kind === 'pozycja' && e.dzial === oldKey) {
      next[j] = { ...e, dzial: newTitle };
    }
  }
  return next;
}

const MAX_PRZEDMIAR_UNDO = 80;

type PrzedmiarEditorSnapshot = {
  tableEntries: PrzedmiarTableEntry[];
  cellOverrides: Record<string, string>;
  calcScratchBundles: Record<string, CalcScratchBundle>;
};

function przedmiarEditorSnapshotSig(s: PrzedmiarEditorSnapshot): string {
  return JSON.stringify({
    e: s.tableEntries,
    o: Object.fromEntries(Object.entries(s.cellOverrides).sort(([a], [b]) => a.localeCompare(b))),
    b: Object.fromEntries(
      Object.entries(s.calcScratchBundles).sort(([a], [b]) => a.localeCompare(b))
    ),
  });
}

function isLikelyRemotePrzedmiarBroadcastShape(raw: {
  tableEntries: unknown;
  cellOverrides: Record<string, string>;
  calcScratchBundles: unknown;
}): raw is {
  tableEntries: PrzedmiarTableEntry[];
  cellOverrides: Record<string, string>;
  calcScratchBundles: Record<string, CalcScratchBundle>;
} {
  if (!Array.isArray(raw.tableEntries)) return false;
  return typeof raw.calcScratchBundles === 'object' && raw.calcScratchBundles !== null && !Array.isArray(raw.calcScratchBundles);
}

/** Ikony toolbaru przedmiaru — jeden rozmiar glyfu i opsz (Material Symbols Outlined). */
const PRZEDMIAR_MS_ICON_PX = 20;

/** Obszar kliknięcia 32×32; glyph w środku 20px — jak pozostałe ikony MUI small. */
const PRZEDMIAR_TOOLBAR_ICON_BTN_SX = {
  width: 32,
  height: 32,
  minWidth: 32,
  p: 0,
  borderRadius: '4px',
  color: '#202124',
  transition: 'background-color 0.15s ease',
  '&:hover': { color: '#202124', bgcolor: 'rgba(0, 0, 0, 0.06)' },
  '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' },
} as const;

/** Material Symbols (Google Fonts) — public/index.html; `opsz` dopasowany do px dla ostrego rasteru. */
const MaterialSymbol: React.FC<{
  name: string;
  color?: string;
  fill?: boolean;
  sx?: SxProps<Theme>;
}> = ({ name, color = '#202124', fill = false, sx: sxExtra }) => (
  <Box
    component="span"
    className="material-symbols-outlined"
    sx={
      [
        {
          fontFamily: '"Material Symbols Outlined"',
          fontSize: PRZEDMIAR_MS_ICON_PX,
          width: PRZEDMIAR_MS_ICON_PX,
          height: PRZEDMIAR_MS_ICON_PX,
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          fontVariationSettings: `"FILL" ${fill ? 1 : 0}, "wght" 400, "GRAD" 0, "opsz" ${PRZEDMIAR_MS_ICON_PX}`,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'geometricPrecision',
          userSelect: 'none',
        },
        ...(sxExtra ? [sxExtra] : []),
      ] as SxProps<Theme>
    }
    aria-hidden
  >
    {name}
  </Box>
);

/** Ten sam rozmiar co widoczne `verified` — ukryty, żeby w poziomie wyrównać kalkulator i menu między wierszami. */
function PrzedmiarActionsVerifiedPlaceholder() {
  return (
    <Box
      aria-hidden
      sx={{
        visibility: 'hidden',
        pointerEvents: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
      }}
    >
      <MaterialSymbol name="verified" color="#2e7d32" fill />
    </Box>
  );
}

/**
 * Trzy równe podkolumny w komórce Akcje — wypełniają szerokość komórki (bez overflow),
 * ikony wyśrodkowane: kebab pod kebabem, kalkulator pod kalkulatorem.
 */
const PRZEDMIAR_ACTIONS_ROW_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  alignItems: 'center',
  justifyItems: 'center',
  columnGap: 0.5,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
} as const;

const PRZEDMIAR_ACTIONS_GRID_CELL_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minWidth: 0,
  minHeight: 34,
} as const;

/** Domyślny `minWidth` MUI IconButton psuje wąską kolumnę siatki — mieszczą się w komórce. */
const PRZEDMIAR_ACTIONS_ICON_BTN_SX = { p: 0.5, minWidth: 0, maxWidth: '100%' } as const;

/** Widok wiersza: dział ma `lpDzialu` (numeracja 1…n w obrębie widoku), pozycja tylko dane. */
type PrzedmiarRowView =
  | { kind: 'dzial'; entry: PrzedmiarDzialRow; lpDzialu: number }
  | { kind: 'pozycja'; entry: PrzedmiarPozycjaRow };

function withDzialLp(rows: PrzedmiarTableEntry[]): PrzedmiarRowView[] {
  let lpDzialu = 0;
  const out: PrzedmiarRowView[] = [];
  for (const entry of rows) {
    if (entry.kind === 'dzial') {
      lpDzialu += 1;
      out.push({ kind: 'dzial', entry, lpDzialu });
    } else {
      out.push({ kind: 'pozycja', entry });
    }
  }
  return out;
}

/** Kolumny arkusza przedmiaru — kolejność / widoczność / szerokość jak w EasyEstimate kosztorysach. */
export type PrzedmiarColKey =
  | 'lp'
  | 'podstawa'
  | 'opis'
  | 'jm'
  | 'ilosc'
  | 'cenaJedn'
  | 'wartoscNetto'
  | 'uwagi'
  | 'branza'
  | 'actions';

/** Kolumny dostępne w typowym kosztorysie (bez `branza` — ta jest tylko w widoku biblioteki zweryfikowanych). */
const ALL_PRZEDMIAR_COLS: PrzedmiarColKey[] = [
  'lp',
  'podstawa',
  'opis',
  'jm',
  'ilosc',
  'cenaJedn',
  'wartoscNetto',
  'uwagi',
  'actions',
];

/** Kolumny dostępne w widoku zweryfikowanych — dodatkowo `branza` przed kolumną `actions`. */
const ALL_PRZEDMIAR_COLS_VERIFIED: PrzedmiarColKey[] = [
  'lp',
  'podstawa',
  'opis',
  'jm',
  'ilosc',
  'cenaJedn',
  'wartoscNetto',
  'uwagi',
  'branza',
  'actions',
];

const PRZEDMIAR_COL_LABELS: Record<PrzedmiarColKey, string> = {
  lp: 'Lp.',
  podstawa: 'Podstawa',
  opis: 'Opis',
  jm: 'J.m.',
  ilosc: 'Ilość',
  cenaJedn: 'Cena jedn.',
  wartoscNetto: 'Wartość netto',
  uwagi: 'Uwagi',
  branza: 'Branża',
  actions: 'Akcje',
};

const DEFAULT_PRZEDMIAR_COL_WIDTHS: Record<PrzedmiarColKey, number> = {
  lp: 72,
  podstawa: 200,
  opis: 320,
  jm: 72,
  ilosc: 96,
  cenaJedn: 112,
  wartoscNetto: 120,
  uwagi: 180,
  branza: 130,
  actions: 112,
};

const PRZEDMIAR_STORAGE_WIDTHS = 'easyEstimatePrzedmiarColWidths';
const PRZEDMIAR_STORAGE_PREFIX = 'easyEstimatePrzedmiarTableCol';
const PRZEDMIAR_VISIBLE_KEY = `${PRZEDMIAR_STORAGE_PREFIX}:visible`;
const PRZEDMIAR_ORDER_KEY = `${PRZEDMIAR_STORAGE_PREFIX}:order`;
/** Oddzielne ustawienia kolumn dla widoku „Zweryfikowane" — ta widokównia ma dodatkową kolumnę `branza`,
 * więc trzymamy jej preferencje osobno, żeby nie mieszać z kosztorysem. */
const PRZEDMIAR_VISIBLE_KEY_VERIFIED = `${PRZEDMIAR_STORAGE_PREFIX}:visible:verified`;
const PRZEDMIAR_ORDER_KEY_VERIFIED = `${PRZEDMIAR_STORAGE_PREFIX}:order:verified`;
const PRZEDMIAR_COLUMNS_DIALOG = { width: 305, height: 460 } as const;

/**
 * Kolumny „danych” w wierszu działu (nie lp / nie suma / nie akcje).
 * Przed pierwszą „Wartość netto” w kolejności widoku → scalone z tytułem (`dt`).
 * Po „Wartość netto” → scalona pusta komórka (`df`), żeby nie powielać tytułu (np. w „Uwagi”).
 */
const PRZEDMIAR_DZIAL_DATA_KEYS: PrzedmiarColKey[] = ['podstawa', 'opis', 'jm', 'ilosc', 'cenaJedn', 'uwagi', 'branza'];

type PrzedmiarColRole =
  | { t: 'p'; key: PrzedmiarColKey }
  | { t: 'dl' }
  | { t: 'dt'; keys: PrzedmiarColKey[] }
  /** Wiersz działu: puste scalenie kolumn danych *po* kolumnie sumy (np. Uwagi za Wartość netto). */
  | { t: 'df'; keys: PrzedmiarColKey[] }
  /** Wiersz działu: suma wartości netto pozycji pod tym działem */
  | { t: 'dwn' }
  | { t: 'da' };

function colRolesForRow(rv: PrzedmiarRowView, keys: PrzedmiarColKey[]): PrzedmiarColRole[] {
  if (rv.kind === 'pozycja') {
    return keys.map((key) => ({ t: 'p', key }));
  }
  const roles: PrzedmiarColRole[] = [];
  let afterWartoscNetto = false;
  let i = 0;
  while (i < keys.length) {
    const k = keys[i];
    if (k === 'lp') {
      roles.push({ t: 'dl' });
      i++;
    } else if (k === 'actions') {
      roles.push({ t: 'da' });
      i++;
    } else if (k === 'wartoscNetto') {
      roles.push({ t: 'dwn' });
      afterWartoscNetto = true;
      i++;
    } else if (PRZEDMIAR_DZIAL_DATA_KEYS.includes(k)) {
      const block: PrzedmiarColKey[] = [];
      while (i < keys.length && PRZEDMIAR_DZIAL_DATA_KEYS.includes(keys[i])) {
        block.push(keys[i]);
        i++;
      }
      roles.push(afterWartoscNetto ? { t: 'df', keys: block } : { t: 'dt', keys: block });
    } else {
      i++;
    }
  }
  return roles;
}

function maxColIndex(rv: PrzedmiarRowView, visibleKeys: PrzedmiarColKey[]): number {
  return Math.max(0, colRolesForRow(rv, visibleKeys).length - 1);
}

/** Kolumna numerów wiersza (lewy „gutter”) — indeks -1 w `SheetCursor.c`. */
const SHEET_COL_GUTTER = -1;

function sheetCellDomKey(r: number, c: number): string {
  return c === SHEET_COL_GUTTER ? `g:${r}` : `${r}-${c}`;
}

function clampSheetCol(rv: PrzedmiarRowView, c: number, visibleKeys: PrzedmiarColKey[]): number {
  return Math.min(Math.max(SHEET_COL_GUTTER, c), maxColIndex(rv, visibleKeys));
}

function makeCellOverrideKeyForRole(entryId: string, rv: PrzedmiarRowView, role: PrzedmiarColRole): string | null {
  if (rv.kind === 'dzial') {
    if (role.t === 'dl') return `${entryId}:dzialLp`;
    if (role.t === 'dt') return `${entryId}:dzialTitle`;
    return null;
  }
  if (role.t !== 'p') return null;
  if (role.key === 'actions') return null;
  /** `wartoscNetto` nie zapisujemy jako tekst komórki — tylko wyliczamy `cenaJedn` z wartości; klucz używany przy starcie edycji (brak override). */
  if (role.key === 'wartoscNetto') return null;
  /** `branza` nie jest edytowalna inline — wartość bierzemy zawsze z `entry.branza`, bez override. */
  if (role.key === 'branza') return null;
  return `${entryId}:${role.key}`;
}

/** Id komórki do sync. kursora (entry + rola logiczna — nie zależy od indeksu wiersza przy tym samym filtrze). */
function stableSheetCellId(rv: PrzedmiarRowView, role: PrzedmiarColRole): string {
  const id = rv.entry.id;
  if (rv.kind === 'dzial') {
    switch (role.t) {
      case 'dl':
        return `${id}#dzialLp`;
      case 'dt':
        return `${id}#dzialTitle`;
      case 'dwn':
        return `${id}#dwn`;
      case 'df':
        return `${id}#df`;
      case 'da':
        return `${id}#da`;
      default:
        return `${id}#dzial`;
    }
  }
  if (role.t === 'p') {
    return `${id}#p:${role.key}`;
  }
  return `${id}#?`;
}

type RemoteSheetMap = Record<string, { cellId: string | null; displayName?: string }>;

function remoteCollaboratorCellOverlay(
  remoteSheetCells: RemoteSheetMap,
  rv: PrzedmiarRowView,
  role: PrzedmiarColRole
): { sx: Record<string, unknown>; title?: string } {
  const cid = stableSheetCellId(rv, role);
  const matches = Object.entries(remoteSheetCells).filter(([, v]) => v?.cellId === cid);
  if (matches.length === 0) return { sx: {} };
  const names = matches
    .map(([, v]) => v.displayName)
    .filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
  const firstUid = matches[0][0];
  return {
    sx: {
      boxShadow: `inset 0 0 0 2px ${colorForUser(firstUid)}`,
      position: 'relative' as const,
      zIndex: 3,
    },
    title: names.length > 0 ? `Kursor: ${names.join(', ')}` : 'Inny użytkownik przy tym kosztorysie',
  };
}

function getCellRawValueFromRole(
  rv: PrzedmiarRowView,
  role: PrzedmiarColRole,
  cellOverrides?: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): string {
  if (rv.kind === 'dzial') {
    if (role.t === 'dl') {
      const on = normalizeWbsOutlineString(rv.entry.outlineNr);
      if (on) return on;
      const z = rv.entry.lpZrodlowy;
      return z != null && z !== '' ? z : String(rv.lpDzialu);
    }
    if (role.t === 'dt') return rv.entry.tytul;
    if (role.t === 'df') return '';
    if (role.t === 'dwn') return '';
    return '';
  }
  if (role.t !== 'p') return '';
  const e = rv.entry;
  switch (role.key) {
    case 'lp':
      return String(e.lp);
    case 'podstawa':
      return e.podstawa;
    case 'opis':
      return e.opis;
    case 'jm':
      return e.jm;
    case 'ilosc':
      return e.ilosc;
    case 'cenaJedn':
      return e.cenaJedn;
    case 'wartoscNetto':
      return computeWartoscNettoPl(e, cellOverrides, resolvedCenaJednByEntryId);
    case 'uwagi':
      return e.uwagi;
    case 'branza':
      return e.branza ?? '';
    default:
      return '';
  }
}

function isCellEditableForRole(rv: PrzedmiarRowView, role: PrzedmiarColRole): boolean {
  if (rv.kind === 'dzial') {
    return role.t === 'dl' || role.t === 'dt';
  }
  if (role.t !== 'p') return false;
  /* Branża edytowana tylko z menu wiersza (dialog z listą wartości) — nie edytujemy inline, żeby
   * nie wpisać dowolnego tekstu niezgodnego z enum bazy. */
  if (role.key === 'branza') return false;
  return role.key !== 'actions';
}

function getDisplayTextForRole(
  rv: PrzedmiarRowView,
  role: PrzedmiarColRole,
  cellOverrides: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): string {
  const k = makeCellOverrideKeyForRole(rv.entry.id, rv, role);
  if (k) {
    const o = cellOverrides[k];
    if (o !== undefined) {
      if (rv.kind === 'pozycja' && role.t === 'p' && role.key === 'uwagi' && o === '') return '—';
      if (rv.kind === 'pozycja' && role.t === 'p' && role.key === 'cenaJedn') {
        if (o === '') return '';
        if (isPrzedmiarCellFormula(o)) {
          const resolved = resolvedCenaJednByEntryId?.get(rv.entry.id) ?? '';
          // Gdy formuła się nie rozwiązuje (brak wiersza, cykl, błędna składnia) — pokaż tekst formuły,
          // żeby widać było, że to nie jest „zwykła” wartość liczbowa. Normalny przypadek: liczba 2 m. po przecinku.
          return resolved ? formatCenaJednDisplayed(resolved) : o;
        }
        return formatCenaJednDisplayed(o);
      }
      if (rv.kind === 'pozycja' && role.t === 'p' && role.key === 'ilosc') return o === '' ? '' : formatIloscDisplayed(o);
      return o;
    }
  }
  const raw = getCellRawValueFromRole(rv, role, cellOverrides, resolvedCenaJednByEntryId);
  if (rv.kind === 'dzial' && role.t === 'df') return '—';
  if (rv.kind === 'pozycja' && role.t === 'p' && role.key === 'uwagi' && !raw) return '—';
  if (rv.kind === 'pozycja' && role.t === 'p' && role.key === 'cenaJedn' && raw) return formatCenaJednDisplayed(raw);
  if (rv.kind === 'pozycja' && role.t === 'p' && role.key === 'ilosc' && raw) return formatIloscDisplayed(raw);
  return raw;
}

function editTextAlignForRole(role: PrzedmiarColRole): 'left' | 'right' {
  if (role.t === 'dl') return 'right';
  if (role.t === 'dwn') return 'right';
  if (role.t === 'dt' || role.t === 'df') return 'left';
  if (role.t === 'p') {
    if (role.key === 'lp' || role.key === 'ilosc' || role.key === 'cenaJedn' || role.key === 'wartoscNetto') return 'right';
    return 'left';
  }
  return 'left';
}

const PRZEDMIAR_NUMERIC_SUM_COLUMN_KEYS = new Set<PrzedmiarColKey>(['ilosc', 'wartoscNetto']);

/**
 * Suma wartości w jednej kolumnie (pionowe zaznaczenie) — tylko Ilość / Wartość netto (bez „Cena jedn.”),
 * wyłącznie wiersze pozycji (bez nagłówków działów).
 */
function computePrzedmiarNumericColumnSelectionSum(
  bounds: { r0: number; r1: number; c0: number; c1: number } | null,
  rowsView: PrzedmiarRowView[],
  rolesPerRow: PrzedmiarColRole[][],
  cellOverrides: Record<string, string>,
  resolvedCenaJednByEntryId?: Map<string, string>
): { show: boolean; sum: number; selectionKind: 'quantity' | 'currency' } {
  const empty = { show: false, sum: 0, selectionKind: 'currency' as const };
  if (!bounds) return empty;
  const { r0, r1, c0, c1 } = bounds;
  if (c0 !== c1) return empty;
  const c = c0;
  if (c < 0) return empty;
  let sum = 0;
  let anyPozycjaNumeric = false;
  let columnKey: PrzedmiarColKey | null = null;
  for (let r = r0; r <= r1; r++) {
    const rv = rowsView[r];
    if (!rv || rv.kind !== 'pozycja') continue;
    const roles = rolesPerRow[r];
    if (!roles || c >= roles.length) continue;
    const role = roles[c];
    if (role.t !== 'p' || !PRZEDMIAR_NUMERIC_SUM_COLUMN_KEYS.has(role.key)) continue;
    if (columnKey === null) columnKey = role.key;
    anyPozycjaNumeric = true;
    const raw = getCellRawValueFromRole(rv, role, cellOverrides, resolvedCenaJednByEntryId).trim();
    if (!raw) continue;
    const n = parseLoosePlDecimal(raw);
    if (n !== null && Number.isFinite(n)) sum += n;
  }
  if (!anyPozycjaNumeric || columnKey === null) return empty;
  const selectionKind = columnKey === 'ilosc' ? ('quantity' as const) : ('currency' as const);
  return { show: true, sum, selectionKind };
}

/** Kursor arkusza: wiersz w `rowsView`, kolumna wizualna wg `visibleKeys`. */
export type SheetCursor = { r: number; c: number };

/** Współrzędne komórki z id stabilnego syncu (entry + rola). */
function sheetCursorFromStableCellId(
  cellId: string,
  rowsView: PrzedmiarRowView[],
  rolesPerRow: PrzedmiarColRole[][]
): SheetCursor | null {
  for (let r = 0; r < rowsView.length; r++) {
    const roles = rolesPerRow[r];
    const rv = rowsView[r];
    if (!roles || !rv) continue;
    for (let c = 0; c < roles.length; c++) {
      if (stableSheetCellId(rv, roles[c]) === cellId) return { r, c };
    }
  }
  return null;
}

/** Pozycja komórki arkusza pod współrzędnymi (przeciąganie zaznaczenia myszą / touchpad / dotyk). */
function trySheetCellHitFromClientPoint(
  clientX: number,
  clientY: number,
  rowsView: PrzedmiarRowView[],
  visibleKeys: PrzedmiarColKey[]
): SheetCursor | null {
  const el = document.elementFromPoint(clientX, clientY);
  const node = el?.closest?.('[data-przedmiar-sheet-cell]');
  if (!(node instanceof HTMLElement)) return null;
  const r = Number(node.dataset.przedmiarSheetR);
  const c = Number(node.dataset.przedmiarSheetC);
  if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
  const rv = rowsView[r];
  if (!rv) return null;
  return { r, c: clampSheetCol(rv, c, visibleKeys) };
}

function navigateSheet(
  rowsView: PrzedmiarRowView[],
  visibleKeys: PrzedmiarColKey[],
  cur: SheetCursor,
  key: string
): SheetCursor {
  const { r, c } = cur;
  const rv = rowsView[r];
  if (!rv) return cur;
  const maxC = maxColIndex(rv, visibleKeys);

  switch (key) {
    case 'ArrowRight': {
      if (c === SHEET_COL_GUTTER) return { r, c: 0 };
      if (c < maxC) return { r, c: c + 1 };
      return cur;
    }
    case 'ArrowLeft': {
      if (c > 0) return { r, c: c - 1 };
      if (c === 0) return { r, c: SHEET_COL_GUTTER };
      return cur;
    }
    case 'ArrowDown': {
      if (r < rowsView.length - 1) {
        const nr = r + 1;
        return { r: nr, c: clampSheetCol(rowsView[nr], c, visibleKeys) };
      }
      return cur;
    }
    case 'ArrowUp': {
      if (r > 0) {
        const nr = r - 1;
        return { r: nr, c: clampSheetCol(rowsView[nr], c, visibleKeys) };
      }
      return cur;
    }
    default:
      return cur;
  }
}

function isCellInSheetRange(
  rowsView: PrzedmiarRowView[],
  visibleKeys: PrzedmiarColKey[],
  a: SheetCursor,
  b: SheetCursor,
  r: number,
  c: number,
  /** Precomputed max col per row — unika wielokrotnego `colRolesForRow` przy każdej komórce. */
  maxColByRow?: number[]
): boolean {
  const r0 = Math.min(a.r, b.r);
  const r1 = Math.max(a.r, b.r);
  const c0 = Math.min(a.c, b.c);
  const c1 = Math.max(a.c, b.c);
  if (r < r0 || r > r1) return false;
  const rv = rowsView[r];
  if (!rv) return false;
  const maxC = maxColByRow?.[r] ?? maxColIndex(rv, visibleKeys);
  /** Kolumna numerów (c = -1) nigdy nie wchodzi w zaznaczenie arkusza — jak nagłówki wierszy Excel. */
  if (c < 0 || c > maxC) return false;
  if (c < c0 || c > c1) return false;
  return true;
}

function cellInSheetRangeForRowIndex(
  rowIndex: number,
  c: number,
  sheetCursor: SheetCursor | null,
  rangeCornerA: SheetCursor | null,
  rangeIsMultiple: boolean,
  rowsView: PrzedmiarRowView[],
  visibleKeys: PrzedmiarColKey[],
  maxColByRow: number[]
): boolean {
  if (sheetCursor === null || rangeCornerA === null) return false;
  if (!rangeIsMultiple && rangeCornerA.r === sheetCursor.r && rangeCornerA.c === sheetCursor.c) {
    return rowIndex === sheetCursor.r && c === sheetCursor.c;
  }
  return isCellInSheetRange(rowsView, visibleKeys, rangeCornerA, sheetCursor, rowIndex, c, maxColByRow);
}

function computeRowSelectionSig(
  rowIndex: number,
  rolesLen: number,
  sheetCursor: SheetCursor | null,
  rangeCornerA: SheetCursor | null,
  rangeIsMultiple: boolean,
  rowsView: PrzedmiarRowView[],
  visibleKeys: PrzedmiarColKey[],
  maxColByRow: number[]
): string {
  const inR = (r: number, col: number) =>
    cellInSheetRangeForRowIndex(r, col, sheetCursor, rangeCornerA, rangeIsMultiple, rowsView, visibleKeys, maxColByRow);
  const parts: string[] = [];
  for (let c = 0; c < rolesLen; c++) {
    const ir = inR(rowIndex, c) ? '1' : '0';
    const fc = sheetCursor !== null && sheetCursor.r === rowIndex && sheetCursor.c === c ? '1' : '0';
    const t = inR(rowIndex - 1, c) ? '1' : '0';
    const b = inR(rowIndex + 1, c) ? '1' : '0';
    const l = inR(rowIndex, c - 1) ? '1' : '0';
    const rg = inR(rowIndex, c + 1) ? '1' : '0';
    parts.push(`${ir}${fc}${t}${b}${l}${rg}`);
  }
  return parts.join('|');
}

function przedmiarSheetEditRowSig(
  rowEntryId: string,
  editingCell: { entryId: string; col: number } | null,
  editDraft: string
): string {
  if (!editingCell || editingCell.entryId !== rowEntryId) return '-';
  return `${editingCell.col}\u001f${editDraft}`;
}

/** Jak podświetlenie zaznaczenia w Arkuszach Google */
const SHEET_SELECTION_FILL = '#e8f0fe';
const SHEET_SELECTION_STROKE = '#1a73e8';
const SHEET_GRID_LINE = '#e8eaed';

type SheetSelectionBorderPalette = { thickStroke: string; thinGrid: string };

/**
 * Obramowanie obwodu zaznaczenia (gruby zewnętrzny, cienki wewnątrz zakresu) — tylko gdy `multiRange`.
 */
function sheetSelectionPerimeterBorderSx(
  rowsView: PrzedmiarRowView[],
  visibleKeys: PrzedmiarColKey[],
  a: SheetCursor,
  b: SheetCursor,
  r: number,
  c: number,
  multiRange: boolean,
  maxColByRow: number[],
  palette?: SheetSelectionBorderPalette
): Record<string, string | number> {
  if (!multiRange || !isCellInSheetRange(rowsView, visibleKeys, a, b, r, c, maxColByRow)) {
    return {};
  }

  const neighborInRange = (nr: number, nc: number): boolean => {
    if (nr < 0 || nr >= rowsView.length) return false;
    const maxNc = maxColByRow[nr];
    if (nc < 0 || nc > maxNc) return false;
    return isCellInSheetRange(rowsView, visibleKeys, a, b, nr, nc, maxColByRow);
  };

  const thin = `1px solid ${palette?.thinGrid ?? SHEET_GRID_LINE}`;
  const thick = `2px solid ${palette?.thickStroke ?? SHEET_SELECTION_STROKE}`;

  return {
    borderTop: neighborInRange(r - 1, c) ? thin : thick,
    borderBottom: neighborInRange(r + 1, c) ? thin : thick,
    borderLeft: neighborInRange(r, c - 1) ? thin : thick,
    borderRight: neighborInRange(r, c + 1) ? thin : thick,
    boxSizing: 'border-box',
    position: 'relative' as const,
    zIndex: 1,
    borderRadius: 0,
  };
}

/** `scrollIntoView` tylko gdy komórka wychodzi poza kontener — szybsza nawigacja strzałkami niż przy każdym kroku. */
function scrollSheetCellIntoViewIfNeeded(container: HTMLElement | null, cell: HTMLElement, margin = 4): void {
  if (!container) {
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return;
  }
  const cRect = container.getBoundingClientRect();
  const eRect = cell.getBoundingClientRect();
  const fitsVert = eRect.top >= cRect.top + margin && eRect.bottom <= cRect.bottom - margin;
  const fitsHor = eRect.left >= cRect.left + margin && eRect.right <= cRect.right - margin;
  if (fitsVert && fitsHor) return;
  cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

const sheetCellFocusOutline = {
  outline: '2px solid #1a73e8',
  outlineOffset: '-2px',
  position: 'relative' as const,
  zIndex: 2,
  /** Bez zaokrągleń — inaczej obrys (w tym „drugi” kolor przy zakresie) wygląda jak miękki prostokąt. */
  borderRadius: 0,
};

const sheetEditInputSx = {
  width: '100%',
  minWidth: 0,
  margin: 0,
  padding: '4px 6px',
  border: 'none',
  borderRadius: 0,
  outline: 'none',
  boxSizing: 'border-box' as const,
  fontFamily: 'Roboto, sans-serif',
  fontSize: '13px',
  color: '#202124',
  /** Tło jak komórka — ramkę wyznacza wyłącznie `sheetCellFocusOutline` na `<td>`, bez drugiej obwódki na `<input>`. */
  backgroundColor: 'transparent',
  boxShadow: 'none',
};

/** Max wysokość pola edycji (zsynchronizuj z logiką dopasowania `scrollHeight` poniżej). */
const SHEET_EDIT_TEXTAREA_MAX_PX = 320;

/** Edycja długich pól: zawijanie jak w komórce tylko do odczytu; Enter zatwierdza, Shift+Enter — nowa linia. */
const sheetEditTextareaSx: React.CSSProperties = {
  ...sheetEditInputSx,
  minHeight: '4.5rem',
  maxHeight: `min(70vh, ${SHEET_EDIT_TEXTAREA_MAX_PX}px)`,
  /** Wysokość ustawia `useLayoutEffect` (scrollHeight); `vertical` kłóciłoby się z odświeżaniem przy edycji. */
  resize: 'none',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  lineHeight: 1.35,
  display: 'block',
  overflow: 'auto',
};

function isMultilinePrzedmiarEditRole(rv: PrzedmiarRowView, role: PrzedmiarColRole): boolean {
  if (role.t === 'dt') return true;
  if (role.t === 'p' && (role.key === 'opis' || role.key === 'podstawa' || role.key === 'uwagi')) return true;
  return false;
}

/** Dopasowanie wysokości textarea bez `useLayoutEffect` na każdy znak — tylko DOM + rAF. */
function resizeSheetEditingTextareaFromRef(el: HTMLInputElement | HTMLTextAreaElement | null): void {
  if (!el || el.tagName !== 'TEXTAREA') return;
  const ta = el as HTMLTextAreaElement;
  ta.style.height = 'auto';
  const maxPx = Math.min(window.innerHeight * 0.7, SHEET_EDIT_TEXTAREA_MAX_PX);
  const minPx = 72;
  const h = Math.min(Math.max(ta.scrollHeight, minPx), maxPx);
  ta.style.height = `${h}px`;
}

/** Mniejszy niż Karta Info — maksymalna szerokość na tabelę przedmiaru (main i tak ma offset od sidebara). */
const pageGutterSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box' as const,
  pl: { xs: 1.5, sm: 2 },
  pr: { xs: 1.5, sm: 2 },
};

const sheetHeaderCellSx = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 600,
  fontSize: '12px',
  color: '#202124',
  bgcolor: '#F3F3F3',
  borderBottom: '2px solid #dadce0',
  borderRight: '1px solid #dadce0',
  py: 1,
  px: 1.25,
  whiteSpace: 'nowrap',
  lineHeight: 1.25,
};

const sheetBodyCellSx = {
  fontFamily: 'Roboto, sans-serif',
  fontSize: '13px',
  color: '#202124',
  borderRight: '1px solid #e8eaed',
  borderBottom: '1px solid #e8eaed',
  py: 0.75,
  px: 1,
  verticalAlign: 'middle',
  borderRadius: 0,
};

/** Szerokość kolumny z numerem wiersza (jak w Arkuszach Google). */
const SHEET_ROW_GUTTER_PX = 36;
/**
 * Tło wiersza z literami kolumn (A, B, C…) — ta sama barwa co kolumna numerów wiersza,
 * żeby linie siatki (`borderBottom` #e8eaed) były widoczne (na #e8eaed znikały).
 */
const SHEET_COL_LETTER_HEADER_BG = '#f1f3f4';
/** Tło komórek numeracji wierszy w tbody — jak nagłówek liter, nie kolor wiersza działu. */
const SHEET_GUTTER_ROWHEADER_BG = SHEET_COL_LETTER_HEADER_BG;
/** Wysokość wiersza z literami A, B, C… pod sticky nagłówkiem. */
const SHEET_LETTERS_HEADER_ROW_PX = 22;

/** Indeks kolumny danych 0→A, 25→Z, 26→AA (jak w arkuszu). */
function sheetColumnLetter(zeroBasedIndex: number): string {
  let n = zeroBasedIndex + 1;
  let result = '';
  while (n > 0) {
    n -= 1;
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }
  return result || 'A';
}

const sheetGutterCornerSx = {
  fontFamily: 'Roboto, sans-serif',
  width: SHEET_ROW_GUTTER_PX,
  minWidth: SHEET_ROW_GUTTER_PX,
  maxWidth: SHEET_ROW_GUTTER_PX,
  minHeight: SHEET_LETTERS_HEADER_ROW_PX,
  boxSizing: 'border-box' as const,
  p: '4px 2px',
  textAlign: 'center' as const,
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: 1.2,
  color: '#80868b',
  bgcolor: SHEET_COL_LETTER_HEADER_BG,
  borderRight: '1px solid #dadce0',
  borderBottom: '1px solid #dadce0',
  userSelect: 'none',
  position: 'sticky' as const,
  left: 0,
  top: 0,
  zIndex: 12,
  boxShadow: '1px 0 0 #dadce0',
};

const sheetLettersHeaderCellSx = {
  fontFamily: 'Roboto, sans-serif',
  py: 0.5,
  px: 0.5,
  minHeight: SHEET_LETTERS_HEADER_ROW_PX,
  boxSizing: 'border-box' as const,
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: 1.2,
  color: '#80868b',
  bgcolor: SHEET_COL_LETTER_HEADER_BG,
  borderRight: '1px solid #dadce0',
  borderBottom: '1px solid #dadce0',
  userSelect: 'none',
  textAlign: 'center' as const,
  position: 'sticky' as const,
  top: 0,
  zIndex: 5,
};

const sheetGutterHeaderRowNumSx = {
  fontFamily: 'Roboto, sans-serif',
  width: SHEET_ROW_GUTTER_PX,
  minWidth: SHEET_ROW_GUTTER_PX,
  maxWidth: SHEET_ROW_GUTTER_PX,
  py: 0.75,
  px: 2,
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: 1.2,
  color: '#80868b',
  bgcolor: SHEET_COL_LETTER_HEADER_BG,
  borderRight: '1px solid #dadce0',
  borderBottom: '2px solid #dadce0',
  userSelect: 'none',
  textAlign: 'center' as const,
  position: 'sticky' as const,
  left: 0,
  top: SHEET_LETTERS_HEADER_ROW_PX,
  zIndex: 11,
  boxShadow: '1px 0 0 #dadce0',
};

const dzialSectionRowSx = {
  '& td:not(:first-of-type)': {
    py: 0.75,
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 700,
    fontSize: '13px',
    letterSpacing: '0.02em',
    verticalAlign: 'middle',
  },
  '& td:first-of-type': {
    verticalAlign: 'middle',
  },
};

function sheetGutterBodyCellSx(stripeBg: string): Record<string, unknown> {
  return {
    ...sheetBodyCellSx,
    width: SHEET_ROW_GUTTER_PX,
    minWidth: SHEET_ROW_GUTTER_PX,
    maxWidth: SHEET_ROW_GUTTER_PX,
    py: 0.5,
    px: 0.5,
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: 1.2,
    color: '#80868b',
    textAlign: 'center' as const,
    bgcolor: stripeBg,
    borderRight: '1px solid #dadce0',
    position: 'sticky' as const,
    left: 0,
    zIndex: 3,
    boxShadow: '1px 0 0 #dadce0',
    userSelect: 'none',
    cursor: 'default',
    verticalAlign: 'middle',
  };
}

/** Jak AddProjectForm / AddEstimateOfferDrawer — obramowanie, hover, focus #2020AF */
const filterTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 32,
    backgroundColor: '#fff',
    borderRadius: '4px',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
    boxSizing: 'border-box',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '16px',
    color: '#2D2D32',
  },
  '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 },
} as const;

const filterSelectSx = {
  '& .MuiOutlinedInput-root': {
    height: 32,
    backgroundColor: '#fff',
    borderRadius: '4px',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
  '&:has(.MuiSelect-root[aria-expanded="true"]) .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
  '& .MuiSelect-select': {
    padding: '8px 12px',
    boxSizing: 'border-box',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '16px',
    color: '#2D2D32',
  },
} as const;

/** Rozmiar tekstu w rozwijanej liście filtra działu (mniejszy niż pole zamknięte). */
const dzialFilterMenuListPaperSx = {
  /**
   * Domyślne Menu MUI: `maxHeight: calc(100% - 96px)` — przy `%` od złego kontenera lista ma ~wysokość
   * viewportu i Popover przesuwa ją w górę, nachodząc na Select. Ograniczenie w `vh` wymusza miejsce
   * pod polem i przewijanie wewnątrz listy.
   */
  maxHeight: 'min(560px, calc(100vh - 160px))',
  overflowY: 'auto',
  '& .MuiMenuItem-root': {
    fontSize: '12px',
    lineHeight: 1.35,
    fontFamily: 'Roboto, sans-serif',
    py: 0.75,
  },
} as const;

/** Ten sam rozmiar co filterTextFieldSx / filterSelectSx (OutlinedInput height 32). */
const filterVersionMenuButtonSx = {
  height: 32,
  minHeight: 32,
  maxHeight: 32,
  boxSizing: 'border-box' as const,
  borderRadius: '4px',
  py: 0,
  px: '12px',
  justifyContent: 'space-between' as const,
  textTransform: 'none' as const,
  fontFamily: 'Roboto, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  color: '#2D2D32',
  borderColor: '#d4d4d9',
  backgroundColor: '#fff',
  lineHeight: 1.25,
  '&:hover': { borderColor: '#595966', bgcolor: '#fff' },
  '& .MuiButton-endIcon': { mr: 0, ml: 0.5 },
} as const;

const STRIPE_BG = ['#ffffff', '#fafafa'] as const;

/** Obramowanie wiersza pozycji przy otwartym panelu mini-arkusza (calculate). */
const CALC_PANEL_ROW_OUTLINE = '#1a73e8';

type PrzedmiarSheetSelBounds = {
  rangeCornerA: SheetCursor | null;
  rangeIsMultiple: boolean;
  bounds: { r0: number; r1: number; c0: number; c1: number } | null;
};

type PrzedmiarSheetDzialRowMemoProps = {
  rowRenderStamp: string;
  rv: Extract<PrzedmiarRowView, { kind: 'dzial' }>;
  index: number;
  roles: PrzedmiarColRole[];
  /** Pełna kolejność dokumentu — sumy WBS i przypisanie pozycji zawsze z tej listy. */
  tableEntriesFull: PrzedmiarTableEntry[];
  rowsView: PrzedmiarRowView[];
  visibleKeysEffective: PrzedmiarColKey[];
  sheetMaxColByRow: number[];
  sheetCursor: SheetCursor | null;
  sheetSelBounds: PrzedmiarSheetSelBounds;
  cellOverrides: Record<string, string>;
  /** Rozwinięte wartości „Cena jedn.” (bez formuł) — do obliczenia sum działów i wyświetlania. */
  resolvedCenaJednByEntryId: Map<string, string>;
  eeRemoteSheetCells: Record<string, EasyEstimateRemoteSheetCell>;
  editingCell: { entryId: string; col: number } | null;
  editDraft: string;
  onEditDraftChange: (value: string) => void;
  getColW: (key: PrzedmiarColKey) => string;
  getWidthNum: (key: PrzedmiarColKey) => number;
  setSheetCellRef: (r: number, c: number, el: unknown) => void;
  editInputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  handleSheetCellKeyDown: (e: React.KeyboardEvent, rowIdx: number, colIdx: number) => void;
  applySheetCellPointer: (r: number, c: number, e: { shiftKey: boolean }) => void;
  /** Mousedown / touch — zaznaczenie przeciąganiem + Shift; przed `onFocus` jak przy Shift+strzałka. */
  handleSheetCellPointerDown: (r: number, c: number, e: React.PointerEvent) => void;
  handleSheetCellFocus: (r: number, c: number) => void;
  handleEditInputKeyDown: (e: React.KeyboardEvent, rowIdx: number) => void;
  handleEditBlur: () => void;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, entryId: string) => void;
  onGutterInsertBelow: (rowIndex: number) => void;
  onGutterDeleteRow: (rowIndex: number) => void;
  /** Maks. głębokość WBS wśród działów w całym przedmiarze — skala bottom-up (najgłębszy = H6). */
  maxDzialHierarchyDepth: number;
  /** Widok tylko do odczytu (strona zweryfikowanych) — bez +/- w gutterze. */
  hideGutterRowActions: boolean;
  /** Stały nr wiersza w kolejności dokumentu (przed sortowaniem po necie); nie zmienia się przy sortowaniu widoku. */
  gutterDocumentNr: number;
};

function przedmiarSheetDzialRowMemoEqual(
  prev: PrzedmiarSheetDzialRowMemoProps,
  next: PrzedmiarSheetDzialRowMemoProps
): boolean {
  if (prev.gutterDocumentNr !== next.gutterDocumentNr) return false;
  if (prev.index !== next.index) return false;
  if (prev.rowRenderStamp !== next.rowRenderStamp) return false;
  if (prev.rv !== next.rv || prev.roles !== next.roles) return false;
  if (prev.tableEntriesFull !== next.tableEntriesFull) return false;
  if (prev.rowsView !== next.rowsView) return false;
  if (prev.cellOverrides !== next.cellOverrides) return false;
  if (prev.resolvedCenaJednByEntryId !== next.resolvedCenaJednByEntryId) return false;
  if (prev.visibleKeysEffective !== next.visibleKeysEffective) return false;
  if (prev.sheetMaxColByRow !== next.sheetMaxColByRow) return false;
  if (prev.eeRemoteSheetCells !== next.eeRemoteSheetCells) return false;
  if (prev.handleSheetCellKeyDown !== next.handleSheetCellKeyDown) return false;
  if (prev.applySheetCellPointer !== next.applySheetCellPointer) return false;
  if (prev.handleSheetCellPointerDown !== next.handleSheetCellPointerDown) return false;
  if (prev.handleSheetCellFocus !== next.handleSheetCellFocus) return false;
  if (prev.handleEditInputKeyDown !== next.handleEditInputKeyDown) return false;
  if (prev.handleEditBlur !== next.handleEditBlur) return false;
  if (prev.handleMenuOpen !== next.handleMenuOpen) return false;
  if (prev.onGutterInsertBelow !== next.onGutterInsertBelow) return false;
  if (prev.onGutterDeleteRow !== next.onGutterDeleteRow) return false;
  if (prev.setSheetCellRef !== next.setSheetCellRef) return false;
  if (prev.getColW !== next.getColW) return false;
  if (prev.getWidthNum !== next.getWidthNum) return false;
  if (prev.onEditDraftChange !== next.onEditDraftChange) return false;
  if (prev.editInputRef !== next.editInputRef) return false;
  if (dzialHierarchyDepth(prev.rv.entry) !== dzialHierarchyDepth(next.rv.entry)) return false;
  if (prev.maxDzialHierarchyDepth !== next.maxDzialHierarchyDepth) return false;
  if (prev.hideGutterRowActions !== next.hideGutterRowActions) return false;
  return true;
}

const PrzedmiarSheetDzialBodyRowMemo = React.memo(function PrzedmiarSheetDzialBodyRowMemo(
  props: PrzedmiarSheetDzialRowMemoProps
) {
  const {
    rv,
    index,
    roles,
    tableEntriesFull,
    rowsView,
    visibleKeysEffective,
    sheetMaxColByRow,
    sheetCursor,
    sheetSelBounds,
    cellOverrides,
    resolvedCenaJednByEntryId,
    eeRemoteSheetCells,
    editingCell,
    editDraft,
    onEditDraftChange,
    getColW,
    getWidthNum,
    setSheetCellRef,
    editInputRef,
    handleSheetCellKeyDown,
    applySheetCellPointer,
    handleSheetCellPointerDown,
    handleSheetCellFocus,
    handleEditInputKeyDown,
    handleEditBlur,
    handleMenuOpen,
    onGutterInsertBelow,
    onGutterDeleteRow,
    maxDzialHierarchyDepth,
    hideGutterRowActions,
    gutterDocumentNr,
  } = props;
  const entryId = rv.entry.id;
  const { rangeCornerA, rangeIsMultiple } = sheetSelBounds;
  const isFocused = (c: number) => sheetCursor?.r === index && sheetCursor?.c === c;
  const gutterFocused = sheetCursor?.r === index && sheetCursor?.c === SHEET_COL_GUTTER;
  const isEditing = (c: number) => editingCell?.entryId === entryId && editingCell?.col === c;
  const inSheetRange = (c: number) => {
    if (c === SHEET_COL_GUTTER) return false;
    if (sheetCursor === null || rangeCornerA === null) return false;
    if (!rangeIsMultiple && rangeCornerA.r === sheetCursor.r && rangeCornerA.c === sheetCursor.c) {
      return index === sheetCursor.r && c === sheetCursor.c;
    }
    return isCellInSheetRange(
      rowsView,
      visibleKeysEffective,
      rangeCornerA,
      sheetCursor,
      index,
      c,
      sheetMaxColByRow
    );
  };
  const viz = dzialHierarchyVisual(dzialHierarchyDepth(rv.entry), maxDzialHierarchyDepth);
  /** Mieszanka tła zaznaczenia z tłem wiersza działu (H1–H6), żeby widać oba odcienie naraz. */
  const selectionBg = (c: number) =>
    !isEditing(c) && inSheetRange(c)
      ? { bgcolor: `color-mix(in srgb, ${SHEET_SELECTION_FILL} 50%, ${viz.bg} 50%)` }
      : {};
  const selectionBorders = (c: number) =>
    !isEditing(c) && inSheetRange(c) && rangeCornerA !== null && sheetCursor !== null
      ? sheetSelectionPerimeterBorderSx(
          rowsView,
          visibleKeysEffective,
          rangeCornerA,
          sheetCursor,
          index,
          c,
          rangeIsMultiple,
          sheetMaxColByRow
        )
      : {};
  const cellOutline = (c: number) => {
    if (isEditing(c)) return sheetCellFocusOutline;
    if (rangeIsMultiple && inSheetRange(c)) return {};
    if (isFocused(c)) return sheetCellFocusOutline;
    return {};
  };

  const dzialRowBg = viz.bg;

  return (
    <TableRow
      role="row"
      sx={{
        bgcolor: dzialRowBg,
        ...dzialSectionRowSx,
        ...(!hideGutterRowActions
          ? {
              '& > td:first-of-type:hover .gutter-row-actions, & > td:first-of-type:focus-within .gutter-row-actions': {
                opacity: 1,
                pointerEvents: 'auto',
              },
            }
          : {}),
      }}
    >
      <TableCell
        sx={{
          ...sheetGutterBodyCellSx(SHEET_GUTTER_ROWHEADER_BG),
          borderRight: `1px solid ${viz.gutterRule}`,
          boxShadow: `1px 0 0 ${viz.cellGrid}`,
          cursor: 'default',
          ...(gutterFocused ? sheetCellFocusOutline : {}),
        }}
        ref={(el) => setSheetCellRef(index, SHEET_COL_GUTTER, el)}
        tabIndex={gutterFocused ? 0 : -1}
        role="rowheader"
        aria-label={`Wiersz dokumentu nr ${gutterDocumentNr}. Pozycja na ekranie: ${index + 1}.`}
        data-przedmiar-sheet-cell=""
        data-przedmiar-sheet-r={index}
        data-przedmiar-sheet-c={SHEET_COL_GUTTER}
        onKeyDown={(e) => handleSheetCellKeyDown(e, index, SHEET_COL_GUTTER)}
        onPointerDown={(e) => handleSheetCellPointerDown(index, SHEET_COL_GUTTER, e)}
        onClick={(e) => applySheetCellPointer(index, SHEET_COL_GUTTER, e)}
        onFocus={() => handleSheetCellFocus(index, SHEET_COL_GUTTER)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: viz.textSecondary, lineHeight: 1 }}>
            {gutterDocumentNr}
          </Typography>
          {!hideGutterRowActions ? (
          <Box
            className="gutter-row-actions"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0,
              transform: 'scale(0.82)',
              transformOrigin: 'center center',
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity 0.12s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              size="small"
              aria-label="Wstaw wiersz pozycji poniżej"
              onClick={() => onGutterInsertBelow(index)}
              sx={{ p: 0, minWidth: 22, width: 22, height: 22, color: viz.iconAccent }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Usuń wiersz pozycji"
              onClick={() => onGutterDeleteRow(index)}
              sx={{
                p: 0,
                minWidth: 22,
                width: 22,
                height: 22,
                color: '#d32f2f',
                '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          ) : null}
        </Box>
      </TableCell>
      {roles.map((role, roleIdx) => {
        const isLast = roleIdx === roles.length - 1;
        const rem = remoteCollaboratorCellOverlay(eeRemoteSheetCells, rv, role);
        if (role.t === 'dl') {
          return (
            <TableCell
              key={`dl-${roleIdx}`}
              role="gridcell"
              data-przedmiar-sheet-cell=""
              data-przedmiar-sheet-r={index}
              data-przedmiar-sheet-c={roleIdx}
              ref={(el) => setSheetCellRef(index, roleIdx, el)}
              tabIndex={isEditing(roleIdx) ? -1 : isFocused(roleIdx) ? 0 : -1}
              onKeyDown={(e) => handleSheetCellKeyDown(e, index, roleIdx)}
              onPointerDown={(e) => handleSheetCellPointerDown(index, roleIdx, e)}
              onClick={(e) => applySheetCellPointer(index, roleIdx, e)}
              onFocus={() => handleSheetCellFocus(index, roleIdx)}
              title={rem.title}
              sx={{
                ...sheetBodyCellSx,
                textAlign: 'right',
                color: viz.textPrimary,
                width: getColW('lp'),
                fontWeight: 700,
                bgcolor: 'inherit',
                cursor: 'cell',
                userSelect: 'none',
                p: isEditing(roleIdx) ? 0.25 : undefined,
                borderBottom: `1px solid ${viz.cellGrid}`,
                borderRight: isLast ? 'none' : `1px solid ${viz.cellGrid}`,
                ...rem.sx,
                ...selectionBg(roleIdx),
                ...selectionBorders(roleIdx),
                ...cellOutline(roleIdx),
              }}
            >
              {isEditing(roleIdx) ? (
                <input
                  ref={editInputRef as React.Ref<HTMLInputElement>}
                  value={editDraft}
                  onChange={(e) => onEditDraftChange(e.target.value)}
                  onKeyDown={(e) => handleEditInputKeyDown(e, index)}
                  onBlur={handleEditBlur}
                  aria-label="Edycja komórki"
                  style={{ ...sheetEditInputSx, color: viz.textPrimary, textAlign: editTextAlignForRole(role) }}
                />
              ) : (
                getDisplayTextForRole(rv, role, cellOverrides, resolvedCenaJednByEntryId)
              )}
            </TableCell>
          );
        }
        if (role.t === 'dt') {
          const wPx = role.keys.reduce((s, kk) => s + getWidthNum(kk), 0);
          return (
            <TableCell
              key={`dt-${roleIdx}`}
              role="gridcell"
              colSpan={role.keys.length}
              data-przedmiar-sheet-cell=""
              data-przedmiar-sheet-r={index}
              data-przedmiar-sheet-c={roleIdx}
              ref={(el) => setSheetCellRef(index, roleIdx, el)}
              tabIndex={isEditing(roleIdx) ? -1 : isFocused(roleIdx) ? 0 : -1}
              onKeyDown={(e) => handleSheetCellKeyDown(e, index, roleIdx)}
              onPointerDown={(e) => handleSheetCellPointerDown(index, roleIdx, e)}
              onClick={(e) => applySheetCellPointer(index, roleIdx, e)}
              onFocus={() => handleSheetCellFocus(index, roleIdx)}
              title={rem.title}
              sx={{
                borderRight: isLast ? 'none' : `1px solid ${viz.cellGrid}`,
                borderBottom: `1px solid ${viz.cellGrid}`,
                fontWeight: 700,
                bgcolor: 'inherit',
                color: viz.textPrimary,
                fontFamily: 'Roboto, sans-serif',
                fontSize: '13px',
                cursor: 'cell',
                userSelect: 'none',
                width: `${wPx}px`,
                p: isEditing(roleIdx) ? 0.25 : undefined,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                ...rem.sx,
                ...selectionBg(roleIdx),
                ...selectionBorders(roleIdx),
                ...cellOutline(roleIdx),
              }}
            >
              {isEditing(roleIdx) ? (
                <textarea
                  ref={editInputRef as React.Ref<HTMLTextAreaElement>}
                  value={editDraft}
                  onChange={(e) => onEditDraftChange(e.target.value)}
                  onKeyDown={(e) => handleEditInputKeyDown(e, index)}
                  onBlur={handleEditBlur}
                  aria-label="Edycja nagłówka działu"
                  style={{ ...sheetEditTextareaSx, color: viz.textPrimary, textAlign: editTextAlignForRole(role) }}
                />
              ) : (
                getDisplayTextForRole(rv, role, cellOverrides, resolvedCenaJednByEntryId)
              )}
            </TableCell>
          );
        }
        if (role.t === 'df') {
          const wPx = role.keys.reduce((s, kk) => s + getWidthNum(kk), 0);
          return (
            <TableCell
              key={`df-${roleIdx}`}
              role="gridcell"
              colSpan={role.keys.length}
              data-przedmiar-sheet-cell=""
              data-przedmiar-sheet-r={index}
              data-przedmiar-sheet-c={roleIdx}
              ref={(el) => setSheetCellRef(index, roleIdx, el)}
              tabIndex={-1}
              onKeyDown={(e) => handleSheetCellKeyDown(e, index, roleIdx)}
              onPointerDown={(e) => handleSheetCellPointerDown(index, roleIdx, e)}
              onClick={(e) => applySheetCellPointer(index, roleIdx, e)}
              onFocus={() => handleSheetCellFocus(index, roleIdx)}
              title={rem.title}
              sx={{
                borderRight: isLast ? 'none' : `1px solid ${viz.cellGrid}`,
                borderBottom: `1px solid ${viz.cellGrid}`,
                fontWeight: 700,
                bgcolor: 'inherit',
                cursor: 'default',
                userSelect: 'none',
                width: `${wPx}px`,
                color: viz.textDisabled,
                ...rem.sx,
                ...selectionBg(roleIdx),
                ...selectionBorders(roleIdx),
                ...cellOutline(roleIdx),
              }}
            >
              {getDisplayTextForRole(rv, role, cellOverrides, resolvedCenaJednByEntryId)}
            </TableCell>
          );
        }
        if (role.t === 'dwn') {
          const sumDzial = sumWartoscNettoForDzialEntryId(tableEntriesFull, entryId, cellOverrides, resolvedCenaJednByEntryId);
          return (
            <TableCell
              key={`dwn-${roleIdx}`}
              role="gridcell"
              data-przedmiar-sheet-cell=""
              data-przedmiar-sheet-r={index}
              data-przedmiar-sheet-c={roleIdx}
              ref={(el) => setSheetCellRef(index, roleIdx, el)}
              tabIndex={isFocused(roleIdx) ? 0 : -1}
              onKeyDown={(e) => handleSheetCellKeyDown(e, index, roleIdx)}
              onPointerDown={(e) => handleSheetCellPointerDown(index, roleIdx, e)}
              onClick={(e) => applySheetCellPointer(index, roleIdx, e)}
              onFocus={() => handleSheetCellFocus(index, roleIdx)}
              title={rem.title}
              sx={{
                ...sheetBodyCellSx,
                textAlign: 'right',
                fontWeight: 700,
                color: viz.textPrimary,
                width: getColW('wartoscNetto'),
                bgcolor: 'inherit',
                cursor: 'cell',
                userSelect: 'none',
                borderBottom: `1px solid ${viz.cellGrid}`,
                borderRight: isLast ? 'none' : `1px solid ${viz.cellGrid}`,
                ...rem.sx,
                ...selectionBg(roleIdx),
                ...selectionBorders(roleIdx),
                ...cellOutline(roleIdx),
              }}
            >
              {formatKwotaPl2(sumDzial)}
            </TableCell>
          );
        }
        if (role.t === 'da') {
          return (
            <TableCell
              key={`da-${roleIdx}`}
              role="gridcell"
              align="center"
              data-przedmiar-sheet-cell=""
              data-przedmiar-sheet-r={index}
              data-przedmiar-sheet-c={roleIdx}
              ref={(el) => setSheetCellRef(index, roleIdx, el)}
              tabIndex={isFocused(roleIdx) ? 0 : -1}
              onKeyDown={(e) => handleSheetCellKeyDown(e, index, roleIdx)}
              onPointerDown={(e) => handleSheetCellPointerDown(index, roleIdx, e)}
              onClick={(e) => applySheetCellPointer(index, roleIdx, e)}
              onFocus={() => handleSheetCellFocus(index, roleIdx)}
              title={rem.title}
              sx={{
                ...sheetBodyCellSx,
                borderRight: 'none',
                borderBottom: `1px solid ${viz.cellGrid}`,
                p: 0,
                width: getColW('actions'),
                bgcolor: 'inherit',
                cursor: 'cell',
                userSelect: 'none',
                ...rem.sx,
                ...selectionBg(roleIdx),
                ...selectionBorders(roleIdx),
                ...cellOutline(roleIdx),
              }}
            >
              <Box sx={PRZEDMIAR_ACTIONS_ROW_SX}>
                <Box sx={PRZEDMIAR_ACTIONS_GRID_CELL_SX}>
                  <PrzedmiarActionsVerifiedPlaceholder />
                </Box>
                <Box sx={PRZEDMIAR_ACTIONS_GRID_CELL_SX}>
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    aria-hidden
                    sx={{ ...PRZEDMIAR_ACTIONS_ICON_BTN_SX, visibility: 'hidden', pointerEvents: 'none' }}
                  >
                    <MaterialSymbol name="calculate" />
                  </IconButton>
                </Box>
                <Box sx={PRZEDMIAR_ACTIONS_GRID_CELL_SX}>
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    aria-label="Więcej"
                    aria-haspopup="true"
                    onClick={(e) => handleMenuOpen(e, rv.entry.id)}
                    sx={PRZEDMIAR_ACTIONS_ICON_BTN_SX}
                  >
                    <MoreVertIcon fontSize="small" sx={{ color: viz.textSecondary }} />
                  </IconButton>
                </Box>
              </Box>
            </TableCell>
          );
        }
        return null;
      })}
    </TableRow>
  );
}, przedmiarSheetDzialRowMemoEqual);

type PrzedmiarSheetPozycjaRowMemoProps = {
  rowRenderStamp: string;
  rv: Extract<PrzedmiarRowView, { kind: 'pozycja' }>;
  index: number;
  stripeBg: string;
  roles: PrzedmiarColRole[];
  rowsView: PrzedmiarRowView[];
  visibleKeysEffective: PrzedmiarColKey[];
  sheetMaxColByRow: number[];
  sheetCursor: SheetCursor | null;
  sheetSelBounds: PrzedmiarSheetSelBounds;
  cellOverrides: Record<string, string>;
  /** Rozwinięte wartości „Cena jedn.” (bez formuł) — do wyświetlania i liczenia wartości netto w wierszu. */
  resolvedCenaJednByEntryId: Map<string, string>;
  eeRemoteSheetCells: Record<string, EasyEstimateRemoteSheetCell>;
  editingCell: { entryId: string; col: number } | null;
  editDraft: string;
  onEditDraftChange: (value: string) => void;
  calcPanelEntryId: string | null;
  getColW: (key: PrzedmiarColKey) => string;
  setSheetCellRef: (r: number, c: number, el: unknown) => void;
  editInputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  handleSheetCellKeyDown: (e: React.KeyboardEvent, rowIdx: number, colIdx: number) => void;
  applySheetCellPointer: (r: number, c: number, e: { shiftKey: boolean }) => void;
  /** Mousedown / touch — zaznaczenie przeciąganiem + Shift; przed `onFocus` jak przy Shift+strzałka. */
  handleSheetCellPointerDown: (r: number, c: number, e: React.PointerEvent) => void;
  handleSheetCellFocus: (r: number, c: number) => void;
  handleEditInputKeyDown: (e: React.KeyboardEvent, rowIdx: number) => void;
  handleEditBlur: () => void;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, entryId: string) => void;
  openCalcPanelForPozycja: (entryId: string) => void;
  onGutterInsertBelow: (rowIndex: number) => void;
  onGutterDeleteRow: (rowIndex: number) => void;
  hideGutterRowActions: boolean;
  /** Stały nr wiersza w kolejności dokumentu (przed sortowaniem po necie); nie zmienia się przy sortowaniu widoku. */
  gutterDocumentNr: number;
};

function przedmiarSheetPozycjaRowMemoEqual(
  prev: PrzedmiarSheetPozycjaRowMemoProps,
  next: PrzedmiarSheetPozycjaRowMemoProps
): boolean {
  if (prev.gutterDocumentNr !== next.gutterDocumentNr) return false;
  if (prev.index !== next.index) return false;
  if (prev.rowRenderStamp !== next.rowRenderStamp) return false;
  if (prev.rv !== next.rv || prev.roles !== next.roles) return false;
  if (prev.rowsView !== next.rowsView) return false;
  if (prev.cellOverrides !== next.cellOverrides) return false;
  if (prev.resolvedCenaJednByEntryId !== next.resolvedCenaJednByEntryId) return false;
  if (prev.visibleKeysEffective !== next.visibleKeysEffective) return false;
  if (prev.sheetMaxColByRow !== next.sheetMaxColByRow) return false;
  if (prev.eeRemoteSheetCells !== next.eeRemoteSheetCells) return false;
  if (prev.stripeBg !== next.stripeBg) return false;
  if (prev.handleSheetCellKeyDown !== next.handleSheetCellKeyDown) return false;
  if (prev.applySheetCellPointer !== next.applySheetCellPointer) return false;
  if (prev.handleSheetCellPointerDown !== next.handleSheetCellPointerDown) return false;
  if (prev.handleSheetCellFocus !== next.handleSheetCellFocus) return false;
  if (prev.handleEditInputKeyDown !== next.handleEditInputKeyDown) return false;
  if (prev.handleEditBlur !== next.handleEditBlur) return false;
  if (prev.handleMenuOpen !== next.handleMenuOpen) return false;
  if (prev.onGutterInsertBelow !== next.onGutterInsertBelow) return false;
  if (prev.onGutterDeleteRow !== next.onGutterDeleteRow) return false;
  if (prev.openCalcPanelForPozycja !== next.openCalcPanelForPozycja) return false;
  if (prev.hideGutterRowActions !== next.hideGutterRowActions) return false;
  if (prev.setSheetCellRef !== next.setSheetCellRef) return false;
  if (prev.getColW !== next.getColW) return false;
  if (prev.onEditDraftChange !== next.onEditDraftChange) return false;
  if (prev.editInputRef !== next.editInputRef) return false;
  return true;
}

const PrzedmiarSheetPozycjaBodyRowMemo = React.memo(function PrzedmiarSheetPozycjaBodyRowMemo(
  props: PrzedmiarSheetPozycjaRowMemoProps
) {
  const {
    rv,
    index,
    stripeBg,
    roles,
    rowsView,
    visibleKeysEffective,
    sheetMaxColByRow,
    sheetCursor,
    sheetSelBounds,
    cellOverrides,
    resolvedCenaJednByEntryId,
    eeRemoteSheetCells,
    editingCell,
    editDraft,
    onEditDraftChange,
    calcPanelEntryId,
    getColW,
    setSheetCellRef,
    editInputRef,
    handleSheetCellKeyDown,
    applySheetCellPointer,
    handleSheetCellPointerDown,
    handleSheetCellFocus,
    handleEditInputKeyDown,
    handleEditBlur,
    handleMenuOpen,
    openCalcPanelForPozycja,
    onGutterInsertBelow,
    onGutterDeleteRow,
    hideGutterRowActions,
    gutterDocumentNr,
  } = props;
  const entryId = rv.entry.id;
  const { rangeCornerA, rangeIsMultiple } = sheetSelBounds;
  const isFocused = (c: number) => sheetCursor?.r === index && sheetCursor?.c === c;
  const gutterFocused = sheetCursor?.r === index && sheetCursor?.c === SHEET_COL_GUTTER;
  const isEditing = (c: number) => editingCell?.entryId === entryId && editingCell?.col === c;
  const inSheetRange = (c: number) => {
    if (c === SHEET_COL_GUTTER) return false;
    if (sheetCursor === null || rangeCornerA === null) return false;
    if (!rangeIsMultiple && rangeCornerA.r === sheetCursor.r && rangeCornerA.c === sheetCursor.c) {
      return index === sheetCursor.r && c === sheetCursor.c;
    }
    return isCellInSheetRange(
      rowsView,
      visibleKeysEffective,
      rangeCornerA,
      sheetCursor,
      index,
      c,
      sheetMaxColByRow
    );
  };
  const selectionBg = (c: number) =>
    !isEditing(c) && inSheetRange(c) ? { bgcolor: SHEET_SELECTION_FILL } : {};
  const selectionBorders = (c: number) =>
    !isEditing(c) && inSheetRange(c) && rangeCornerA !== null && sheetCursor !== null
      ? sheetSelectionPerimeterBorderSx(
          rowsView,
          visibleKeysEffective,
          rangeCornerA,
          sheetCursor,
          index,
          c,
          rangeIsMultiple,
          sheetMaxColByRow
        )
      : {};
  const cellOutline = (c: number) => {
    if (isEditing(c)) return sheetCellFocusOutline;
    if (rangeIsMultiple && inSheetRange(c)) return {};
    if (isFocused(c)) return sheetCellFocusOutline;
    return {};
  };

  const uwagiColor = (cellOverrides[`${entryId}:uwagi`] ?? rv.entry.uwagi) ? '#202124' : '#9aa0a6';
  const rowHoverBg = index % 2 === 1 ? '#f3f3f3' : '#f5f5f5';
  const calcRowActive = calcPanelEntryId === rv.entry.id;
  const calcRowEdgeSx = calcRowActive
    ? {
        borderTop: `2px solid ${CALC_PANEL_ROW_OUTLINE}`,
        borderBottom: `2px solid ${CALC_PANEL_ROW_OUTLINE}`,
      }
    : {};

  return (
    <TableRow
      role="row"
      hover
      sx={{
        bgcolor: stripeBg,
        ...(calcRowActive ? { position: 'relative', zIndex: 2 } : {}),
        '&:hover': { bgcolor: rowHoverBg },
        ...(!hideGutterRowActions
          ? {
              '& > td:first-of-type:hover .gutter-row-actions, & > td:first-of-type:focus-within .gutter-row-actions': {
                opacity: 1,
                pointerEvents: 'auto',
              },
            }
          : {}),
      }}
    >
      <TableCell
        sx={{
          ...sheetGutterBodyCellSx(SHEET_GUTTER_ROWHEADER_BG),
          ...calcRowEdgeSx,
          ...(calcRowActive ? { borderLeft: `2px solid ${CALC_PANEL_ROW_OUTLINE}` } : {}),
          cursor: 'default',
          ...(gutterFocused ? sheetCellFocusOutline : {}),
        }}
        ref={(el) => setSheetCellRef(index, SHEET_COL_GUTTER, el)}
        tabIndex={gutterFocused ? 0 : -1}
        role="rowheader"
        aria-label={`Wiersz dokumentu nr ${gutterDocumentNr}. Pozycja na ekranie: ${index + 1}.`}
        data-przedmiar-sheet-cell=""
        data-przedmiar-sheet-r={index}
        data-przedmiar-sheet-c={SHEET_COL_GUTTER}
        onKeyDown={(e) => handleSheetCellKeyDown(e, index, SHEET_COL_GUTTER)}
        onPointerDown={(e) => handleSheetCellPointerDown(index, SHEET_COL_GUTTER, e)}
        onClick={(e) => applySheetCellPointer(index, SHEET_COL_GUTTER, e)}
        onFocus={() => handleSheetCellFocus(index, SHEET_COL_GUTTER)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: '#5F6368', lineHeight: 1 }}>
            {gutterDocumentNr}
          </Typography>
          {!hideGutterRowActions ? (
          <Box
            className="gutter-row-actions"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0,
              transform: 'scale(0.82)',
              transformOrigin: 'center center',
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity 0.12s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              size="small"
              aria-label="Wstaw wiersz pozycji poniżej"
              onClick={() => onGutterInsertBelow(index)}
              sx={{ p: 0, minWidth: 22, width: 22, height: 22, color: '#020242' }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Usuń wiersz pozycji"
              onClick={() => onGutterDeleteRow(index)}
              sx={{
                p: 0,
                minWidth: 22,
                width: 22,
                height: 22,
                color: '#d32f2f',
                '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          ) : null}
        </Box>
      </TableCell>
      {roles.map((role, roleIdx) => {
        const isLast = roleIdx === roles.length - 1;
        if (role.t !== 'p') return null;
        const rem = remoteCollaboratorCellOverlay(eeRemoteSheetCells, rv, role);
        const { key } = role;
        if (key === 'actions') {
          return (
            <TableCell
              key={`act-${roleIdx}`}
              role="gridcell"
              align="center"
              data-przedmiar-sheet-cell=""
              data-przedmiar-sheet-r={index}
              data-przedmiar-sheet-c={roleIdx}
              ref={(el) => setSheetCellRef(index, roleIdx, el)}
              tabIndex={isFocused(roleIdx) ? 0 : -1}
              onKeyDown={(e) => handleSheetCellKeyDown(e, index, roleIdx)}
              onPointerDown={(e) => handleSheetCellPointerDown(index, roleIdx, e)}
              onClick={(e) => applySheetCellPointer(index, roleIdx, e)}
              onFocus={() => handleSheetCellFocus(index, roleIdx)}
              title={rem.title}
              sx={{
                ...sheetBodyCellSx,
                borderRight: isLast && calcRowActive ? `2px solid ${CALC_PANEL_ROW_OUTLINE}` : 'none',
                p: 0,
                width: getColW('actions'),
                cursor: 'cell',
                userSelect: 'none',
                ...calcRowEdgeSx,
                ...selectionBg(roleIdx),
                ...selectionBorders(roleIdx),
                ...cellOutline(roleIdx),
                ...rem.sx,
              }}
            >
              <Box sx={PRZEDMIAR_ACTIONS_ROW_SX}>
                <Box sx={PRZEDMIAR_ACTIONS_GRID_CELL_SX}>
                  {rv.entry.approvedAt ? (
                    <Tooltip
                      title={`Zatwierdzono${rv.entry.approvedByName ? `: ${rv.entry.approvedByName}` : ''}${
                        rv.entry.approvedAt ? ` (${rv.entry.approvedAt})` : ''
                      }`}
                      placement="top"
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.25,
                          lineHeight: 0,
                        }}
                        aria-label="Pozycja zatwierdzona"
                      >
                        <MaterialSymbol name="verified" color="#2e7d32" fill />
                      </Box>
                    </Tooltip>
                  ) : (
                    <PrzedmiarActionsVerifiedPlaceholder />
                  )}
                </Box>
                <Box sx={PRZEDMIAR_ACTIONS_GRID_CELL_SX}>
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    aria-label="Mini arkusz obliczeń przy tej pozycji"
                    title="Obliczenia (mini arkusz)"
                    aria-pressed={calcPanelEntryId === rv.entry.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      openCalcPanelForPozycja(rv.entry.id);
                    }}
                    sx={PRZEDMIAR_ACTIONS_ICON_BTN_SX}
                  >
                    <MaterialSymbol name="calculate" />
                  </IconButton>
                </Box>
                <Box sx={PRZEDMIAR_ACTIONS_GRID_CELL_SX}>
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    aria-label="Więcej"
                    aria-haspopup="true"
                    onClick={(e) => handleMenuOpen(e, rv.entry.id)}
                    sx={PRZEDMIAR_ACTIONS_ICON_BTN_SX}
                  >
                    <MoreVertIcon fontSize="small" sx={{ color: '#5F6368' }} />
                  </IconButton>
                </Box>
              </Box>
            </TableCell>
          );
        }
        const ta =
          key === 'lp' || key === 'ilosc' || key === 'cenaJedn' || key === 'wartoscNetto' ? 'right' : 'left';
        const extraSx: Record<string, unknown> = {
          ...sheetBodyCellSx,
          textAlign: ta,
          cursor: 'cell',
          userSelect: 'none',
          p: isEditing(roleIdx) ? 0.25 : undefined,
          borderRight: isLast
            ? calcRowActive
              ? `2px solid ${CALC_PANEL_ROW_OUTLINE}`
              : 'none'
            : sheetBodyCellSx.borderRight,
          width: getColW(key),
          ...calcRowEdgeSx,
          ...selectionBg(roleIdx),
          ...selectionBorders(roleIdx),
          ...cellOutline(roleIdx),
        };
        if (key === 'lp') Object.assign(extraSx, { color: '#5F6368' });
        if (key === 'wartoscNetto') Object.assign(extraSx, { color: '#202124' });
        if (key === 'opis' || key === 'podstawa' || key === 'uwagi') {
          Object.assign(extraSx, {
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            verticalAlign: 'top',
          });
        }
        if (key === 'uwagi') Object.assign(extraSx, { color: uwagiColor });
        Object.assign(extraSx, rem.sx);

        return (
          <TableCell
            key={`${key}-${roleIdx}`}
            role="gridcell"
            data-przedmiar-sheet-cell=""
            data-przedmiar-sheet-r={index}
            data-przedmiar-sheet-c={roleIdx}
            ref={(el) => setSheetCellRef(index, roleIdx, el)}
            tabIndex={isEditing(roleIdx) ? -1 : isFocused(roleIdx) ? 0 : -1}
            onKeyDown={(e) => handleSheetCellKeyDown(e, index, roleIdx)}
            onPointerDown={(e) => handleSheetCellPointerDown(index, roleIdx, e)}
            onClick={(e) => applySheetCellPointer(index, roleIdx, e)}
            onFocus={() => handleSheetCellFocus(index, roleIdx)}
            title={rem.title}
            sx={extraSx}
          >
            {isEditing(roleIdx) ? (
              isMultilinePrzedmiarEditRole(rv, role) ? (
                <textarea
                  ref={editInputRef as React.Ref<HTMLTextAreaElement>}
                  value={editDraft}
                  onChange={(e) => onEditDraftChange(e.target.value)}
                  onKeyDown={(e) => handleEditInputKeyDown(e, index)}
                  onBlur={handleEditBlur}
                  aria-label="Edycja komórki"
                  style={{ ...sheetEditTextareaSx, textAlign: editTextAlignForRole(role) }}
                />
              ) : (
                <input
                  ref={editInputRef as React.Ref<HTMLInputElement>}
                  value={editDraft}
                  onChange={(e) => onEditDraftChange(e.target.value)}
                  onKeyDown={(e) => handleEditInputKeyDown(e, index)}
                  onBlur={handleEditBlur}
                  aria-label="Edycja komórki"
                  placeholder={key === 'cenaJedn' || key === 'wartoscNetto' ? '0,00' : undefined}
                  inputMode={key === 'cenaJedn' || key === 'wartoscNetto' ? 'decimal' : undefined}
                  style={{ ...sheetEditInputSx, textAlign: editTextAlignForRole(role) }}
                />
              )
            ) : (
              getDisplayTextForRole(rv, role, cellOverrides, resolvedCenaJednByEntryId)
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}, przedmiarSheetPozycjaRowMemoEqual);

const EasyEstimatePrzedmiarTab: React.FC<EasyEstimatePrzedmiarTabProps> = ({
  estimate,
  kosztorysTabActive = true,
  roboczogodzinaZl = '',
  onWartoscNettoSumChange,
  onPozycjeStatsChange,
  onEstimateSaved,
  onTesPrzedmiarSourceChange,
  onNumericColumnSelectionSumChange,
  layoutVariant = 'default',
  externalPrzedmiarSnapshot,
  onExternalScratchBundleChange,
  onExternalCenaJednChange,
  externalSearch,
  onExternalSearchChange,
  onColumnsPanelHandleReady,
  onMenuEditBranza,
}) => {
  const isExternalSearch = externalSearch !== undefined;
  const skipServerPrzedmiar = Boolean(externalPrzedmiarSnapshot);
  const isVerifiedListLayout = layoutVariant === 'verifiedList';
  const layoutVariantReadOnlyRef = React.useRef(isVerifiedListLayout);
  layoutVariantReadOnlyRef.current = isVerifiedListLayout;
  const { user } = useAuth();
  const { othersPresent: kosztorysOthersPresent, remoteSheetCells: eeRemoteSheetCells } = useEasyEstimateKosztorysPresence(
    estimate.id,
    kosztorysTabActive,
    user?.user_id,
    user?.display_name || user?.first_name || user?.email,
    user?.avatar_url ?? undefined
  );

  const [tableEntries, setTableEntries] = React.useState<PrzedmiarTableEntry[]>([]);
  const [showDzialRows, setShowDzialRows] = React.useState(true);
  const [internalSearch, setInternalSearch] = React.useState('');
  const search = isExternalSearch ? (externalSearch as string) : internalSearch;
  const setSearch = React.useCallback(
    (next: string | ((prev: string) => string)) => {
      if (isExternalSearch) {
        const value = typeof next === 'function' ? (next as (p: string) => string)(externalSearch as string) : next;
        onExternalSearchChange?.(value);
      } else {
        setInternalSearch(next);
      }
    },
    [isExternalSearch, externalSearch, onExternalSearchChange]
  );
  const [dzial, setDzial] = React.useState<string>('wszystkie');
  /** Kolejność wierszy w tabeli kosztorysu: dokument (domyślnie) albo wartość netto (↑/↓) — bez zmiany `tableEntries`. */
  const [kosztorysRowSort, setKosztorysRowSort] = React.useState<KosztorysRowSortMode>('default');
  const [przedmiarServerVersions, setPrzedmiarServerVersions] = React.useState<{ id: number; label: string }[]>([]);
  const [versionListTick, setVersionListTick] = React.useState(0);
  const [versionLoadBusy, setVersionLoadBusy] = React.useState(false);
  const [versionMenuAnchor, setVersionMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuTargetEntryId, setMenuTargetEntryId] = React.useState<string | null>(null);
  const [wbsDialogOpen, setWbsDialogOpen] = React.useState(false);
  const [wbsDialogEntryId, setWbsDialogEntryId] = React.useState<string | null>(null);
  const [wbsDialogDraft, setWbsDialogDraft] = React.useState('');
  const { showNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gemmaPdfDialogOpen, setGemmaPdfDialogOpen] = React.useState(false);
  const [gemmaHubView, setGemmaHubView] = React.useState<GemmaHubView>('menu');
  const [gemmaPdfFiles, setGemmaPdfFiles] = React.useState<File[]>([]);
  const [gemmaPdfExtraPrompt, setGemmaPdfExtraPrompt] = React.useState('');
  const GEMMA_PDF_EXTRA_PROMPT_MAX = 4000;
  const [gemmaPdfStreamText, setGemmaPdfStreamText] = React.useState('');
  const [gemmaPdfBusy, setGemmaPdfBusy] = React.useState(false);
  const [googleSheetBusy, setGoogleSheetBusy] = React.useState(false);
  const gemmaPdfInputRef = React.useRef<HTMLInputElement | null>(null);
  const [sheetCursor, setSheetCursor] = React.useState<SheetCursor | null>(null);
  const sheetCursorRef = React.useRef<SheetCursor | null>(null);
  /** Nie ustawiaj `ref` synchronicznie w ciele renderu — przy szybkich strzałkach inny re-render (np. `editDraft`) nadpisywałby ref starym `sheetCursor` zanim `setSheetCursor` się zacommituje. */
  React.useLayoutEffect(() => {
    sheetCursorRef.current = sheetCursor;
  }, [sheetCursor]);
  /** Panel mini-arkusza (obok tabeli, bez nakładki) — id pozycji `pozycja`. */
  const [calcPanelEntryId, setCalcPanelEntryId] = React.useState<string | null>(null);
  const calcPanelEntryIdRef = React.useRef<string | null>(null);
  React.useLayoutEffect(() => {
    calcPanelEntryIdRef.current = calcPanelEntryId;
  }, [calcPanelEntryId]);
  /** Siatka mini-arkusza (50×5) na pozycję — tylko w pamięci sesji. */
  const [calcScratchBundles, setCalcScratchBundles] = React.useState<Record<string, CalcScratchBundle>>({});
  const calcScratchBundlesRef = React.useRef(calcScratchBundles);
  calcScratchBundlesRef.current = calcScratchBundles;
  /** Stały róg prostokąta zaznaczenia przy Shift+klik / Shift+strzałka; przy zwykłym kliknięciu = komórka aktywna */
  const [sheetSelectionAnchor, setSheetSelectionAnchor] = React.useState<SheetCursor | null>(null);
  const sheetSelectionAnchorRef = React.useRef<SheetCursor | null>(null);
  React.useLayoutEffect(() => {
    sheetSelectionAnchorRef.current = sheetSelectionAnchor;
  }, [sheetSelectionAnchor]);
  /** Śledzenie komórki zdalnego użytkownika (klik w avatar); własne zaznaczenie jest wtedy wyłączone. */
  const [sheetFollowRemoteUserId, setSheetFollowRemoteUserId] = React.useState<string | null>(null);
  const sheetFollowRemoteUserIdRef = React.useRef<string | null>(null);
  const lastFollowScrollSigRef = React.useRef<string | null>(null);
  React.useLayoutEffect(() => {
    sheetFollowRemoteUserIdRef.current = sheetFollowRemoteUserId;
  }, [sheetFollowRemoteUserId]);
  const sheetCellRefs = React.useRef<Map<string, HTMLTableCellElement>>(new Map());
  /** Po przeciągnięciu — pominąć syntetyczny `click`, żeby nie zwijał zaznaczenia. */
  const sheetSuppressNextClickRef = React.useRef(false);
  /** Nadpisania komórek (łączone z zapisem do estimate_easy_przedmiar.extras_json). */
  const [cellOverrides, setCellOverrides] = React.useState<Record<string, string>>({});
  const cellOverridesRef = React.useRef(cellOverrides);
  cellOverridesRef.current = cellOverrides;
  /** Nie wysyłaj snapshotu po wczytaniu z API / zastosowaniu zdalnym (echo). */
  const suppressOutgoingPrzedmiarBroadcastRef = React.useRef(false);
  const przedmiarSkipBroadcastSigRef = React.useRef<string | null>(null);
  const tableDataForBroadcastRef = React.useRef<PrzedmiarEditorSnapshot>({
    tableEntries: [],
    cellOverrides: {},
    calcScratchBundles: {},
  });
  React.useLayoutEffect(() => {
    tableDataForBroadcastRef.current = { tableEntries, cellOverrides, calcScratchBundles };
  }, [tableEntries, cellOverrides, calcScratchBundles]);

  /** Kolejność dokumentu przy aktualnych filtrach (bez sortowania po necie) — stabilne numery w kolumnie „Nr”. */
  const rowsFilteredBase = React.useMemo(() => {
    const base = filterPrzedmiarTable(tableEntries, dzial, search, !showDzialRows);
    return !isVerifiedListLayout ? base : filterEntriesToApprovedPozycjeOnly(base);
  }, [search, dzial, tableEntries, showDzialRows, isVerifiedListLayout]);

  /**
   * Bezwzględne numery wierszy (stabilne niezależnie od filtra/sortowania/ukrycia działów).
   * Dzięki temu formuły `=F4` zawsze wskazują ten sam wiersz dokumentu, nawet po „Szukaj”.
   */
  const documentGutterNrByEntryId = React.useMemo(() => {
    const m = new Map<string, number>();
    tableEntries.forEach((e, i) => {
      m.set(e.id, i + 1);
    });
    return m;
  }, [tableEntries]);

  /** Odwrotna mapa nr gutter → entryId — podstawa rozwiązywania formuł `=F4` itp. */
  const docRowNrToEntryId = React.useMemo(() => {
    const m = new Map<number, string>();
    tableEntries.forEach((e, i) => {
      m.set(i + 1, e.id);
    });
    return m;
  }, [tableEntries]);

  /**
   * Wartości „Cena jedn.” po rozwinięciu formuł (np. `=F4`) dla wszystkich pozycji.
   * Zmiana w komórce źródłowej → nowa mapa → wszystkie komórki z formułami przeliczą się w UI.
   */
  const resolvedCenaJednByEntryId = React.useMemo(
    () => buildPrzedmiarCenaJednResolver(tableEntries, cellOverrides, docRowNrToEntryId),
    [tableEntries, cellOverrides, docRowNrToEntryId]
  );

  const przedmiarWartoscNettoSum = React.useMemo(
    () => sumWartoscNettoPozycji(tableEntries, cellOverrides, resolvedCenaJednByEntryId),
    [tableEntries, cellOverrides, resolvedCenaJednByEntryId]
  );
  React.useEffect(() => {
    onWartoscNettoSumChange?.(przedmiarWartoscNettoSum);
  }, [przedmiarWartoscNettoSum, onWartoscNettoSumChange]);
  const przedmiarPozycjeStats = React.useMemo(
    () => pozycjeStatsFromEntries(tableEntries, cellOverrides, resolvedCenaJednByEntryId),
    [tableEntries, cellOverrides, resolvedCenaJednByEntryId]
  );
  React.useEffect(() => {
    onPozycjeStatsChange?.(przedmiarPozycjeStats);
  }, [przedmiarPozycjeStats, onPozycjeStatsChange]);
  React.useEffect(() => {
    onTesPrzedmiarSourceChange?.({ entries: tableEntries, cellOverrides, resolvedCenaJednByEntryId });
  }, [tableEntries, cellOverrides, resolvedCenaJednByEntryId, onTesPrzedmiarSourceChange]);
  const [editingCell, setEditingCell] = React.useState<{ entryId: string; col: number } | null>(null);
  const editingCellRef = React.useRef<{ entryId: string; col: number } | null>(null);
  React.useLayoutEffect(() => {
    editingCellRef.current = editingCell;
  }, [editingCell]);
  const [editDraft, setEditDraft] = React.useState('');
  const editInputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const onEditDraftChange = React.useCallback((value: string) => {
    setEditDraft(value);
    requestAnimationFrame(() => resizeSheetEditingTextareaFromRef(editInputRef.current));
  }, []);
  const skipBlurCommitRef = React.useRef(false);
  /** Po Shift+strzałce layout effect ustawia fokus na nowej komórce — bez tego `onFocus` wywołałby collapse i skasowałby zaznaczenie. */
  const suppressSelectionCollapseOnFocusRef = React.useRef(false);
  /** Trwa przeciąganie zaznaczenia (pointer) — wiele komórek dostaje fokus po kolei; nie wolno wtedy wywołać `collapseSheetSelectionTo`. */
  const sheetPointerDragActiveRef = React.useRef(false);
  const tableScrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const pendingScrollTableToBottomRef = React.useRef(false);

  const undoStackRef = React.useRef<PrzedmiarEditorSnapshot[]>([]);
  const redoStackRef = React.useRef<PrzedmiarEditorSnapshot[]>([]);
  const lastCommittedRef = React.useRef<PrzedmiarEditorSnapshot | null>(null);
  const suppressNextHistoryRef = React.useRef(true);
  const applyingHistoryRef = React.useRef(false);
  const [canUndo, setCanUndo] = React.useState(false);
  const [canRedo, setCanRedo] = React.useState(false);

  const estimateId = estimate.id;
  /** Po udanym GET — włącza debounced PUT (brak tabeli w DB → false, bez nadpisywania). */
  const [persistEasyPrzedmiar, setPersistEasyPrzedmiar] = React.useState(false);

  /** Przy zmianie kosztorysu czyść stan — osobno od fetcha, żeby nie gubić importu Gemmy przy wolny GET (pusta odpowiedź nie nadpisuje tabeli). */
  React.useEffect(() => {
    suppressNextHistoryRef.current = true;
    suppressOutgoingPrzedmiarBroadcastRef.current = true;
    undoStackRef.current = [];
    redoStackRef.current = [];
    lastCommittedRef.current = null;
    setTableEntries([]);
    setCellOverrides({});
    setCalcScratchBundles({});
    setCalcPanelEntryId(null);
    setKosztorysRowSort('default');
    setSheetFollowRemoteUserId(null);
  }, [estimateId]);

  React.useEffect(() => {
    if (skipServerPrzedmiar) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await listEasyPrzedmiarVersions(estimateId);
        if (cancelled) return;
        if (!data.success && data.message) {
          showNotification(data.message, 'warning');
        }
        setPrzedmiarServerVersions((data.versions || []).map((v) => ({ id: v.id, label: v.label })));
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Nie udało się pobrać listy wersji przedmiaru.';
          showNotification(msg, 'error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId, versionListTick, showNotification, skipServerPrzedmiar]);

  React.useEffect(() => {
    if (skipServerPrzedmiar) {
      suppressOutgoingPrzedmiarBroadcastRef.current = false;
      return;
    }
    suppressOutgoingPrzedmiarBroadcastRef.current = true;
    let cancelled = false;
    setPersistEasyPrzedmiar(false);
    (async () => {
      try {
        const data = await getEstimateEasyPrzedmiar(estimateId);
        if (cancelled) return;
        if (!data.success) {
          if (data.message) showNotification(data.message, 'warning');
          return;
        }
        if (data.rows.length > 0) {
          const parsed = parseEasyPrzedmiarApiRows(data.rows);
          suppressNextHistoryRef.current = true;
          setTableEntries(parsed.entries);
          setCellOverrides(parsed.cellOverrides);
          setCalcScratchBundles(parsed.calcScratchBundles);
        } else {
          suppressNextHistoryRef.current = true;
        }
        if (!cancelled) setPersistEasyPrzedmiar(!isVerifiedListLayout);
      } catch (e) {
        if (!cancelled) {
          showNotification((e as Error)?.message || 'Nie udało się wczytać przedmiaru z serwera.', 'error');
        }
      } finally {
        if (!cancelled) {
          window.setTimeout(() => {
            suppressOutgoingPrzedmiarBroadcastRef.current = false;
          }, 750);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId, showNotification, isVerifiedListLayout, skipServerPrzedmiar]);

  React.useEffect(() => {
    if (!externalPrzedmiarSnapshot) return;
    suppressOutgoingPrzedmiarBroadcastRef.current = true;
    suppressNextHistoryRef.current = true;
    setTableEntries(externalPrzedmiarSnapshot.entries);
    setCellOverrides(externalPrzedmiarSnapshot.cellOverrides ?? {});
    setCalcScratchBundles(externalPrzedmiarSnapshot.calcScratchBundles ?? {});
    setPersistEasyPrzedmiar(false);
    const t = window.setTimeout(() => {
      suppressOutgoingPrzedmiarBroadcastRef.current = false;
    }, 750);
    return () => window.clearTimeout(t);
  }, [externalPrzedmiarSnapshot]);

  React.useEffect(() => {
    if (isVerifiedListLayout) setPersistEasyPrzedmiar(false);
  }, [isVerifiedListLayout]);

  React.useEffect(() => {
    if (!persistEasyPrzedmiar || isVerifiedListLayout) return;
    const timer = window.setTimeout(() => {
      putEstimateEasyPrzedmiar(
        estimateId,
        buildEasyPrzedmiarRowsPayload(tableEntries, cellOverrides, calcScratchBundles)
      )
        .then((res) => {
          if (!res.success && res.message) showNotification(res.message, 'error');
        })
        .catch((err: Error) => {
          showNotification(err?.message || 'Zapis przedmiaru nie powiódł się.', 'error');
        });
    }, 1300);
    return () => window.clearTimeout(timer);
  }, [
    tableEntries,
    cellOverrides,
    calcScratchBundles,
    estimateId,
    persistEasyPrzedmiar,
    showNotification,
    isVerifiedListLayout,
  ]);

  React.useLayoutEffect(() => {
    const cur: PrzedmiarEditorSnapshot = {
      tableEntries: structuredClone(tableEntries),
      cellOverrides: { ...cellOverrides },
      calcScratchBundles: structuredClone(calcScratchBundles),
    };

    if (suppressNextHistoryRef.current) {
      suppressNextHistoryRef.current = false;
      lastCommittedRef.current = cur;
      setCanUndo(undoStackRef.current.length > 0);
      setCanRedo(redoStackRef.current.length > 0);
      return;
    }

    if (applyingHistoryRef.current) {
      applyingHistoryRef.current = false;
      lastCommittedRef.current = cur;
      setCanUndo(undoStackRef.current.length > 0);
      setCanRedo(redoStackRef.current.length > 0);
      return;
    }

    const prev = lastCommittedRef.current;
    if (!prev) {
      lastCommittedRef.current = cur;
      setCanUndo(undoStackRef.current.length > 0);
      setCanRedo(redoStackRef.current.length > 0);
      return;
    }

    if (przedmiarEditorSnapshotSig(prev) === przedmiarEditorSnapshotSig(cur)) {
      setCanUndo(undoStackRef.current.length > 0);
      setCanRedo(redoStackRef.current.length > 0);
      return;
    }

    undoStackRef.current = [...undoStackRef.current.slice(-(MAX_PRZEDMIAR_UNDO - 1)), prev];
    redoStackRef.current = [];
    lastCommittedRef.current = cur;
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(false);
  }, [tableEntries, cellOverrides, calcScratchBundles]);

  const handleUndo = React.useCallback(() => {
    const stack = undoStackRef.current;
    if (stack.length === 0) return;
    const snap = stack.pop()!;
    const cur = lastCommittedRef.current;
    if (cur) redoStackRef.current.push(cur);
    applyingHistoryRef.current = true;
    setEditingCell(null);
    setTableEntries(structuredClone(snap.tableEntries));
    setCellOverrides({ ...snap.cellOverrides });
    setCalcScratchBundles(structuredClone(snap.calcScratchBundles));
  }, []);

  const handleRedo = React.useCallback(() => {
    const stack = redoStackRef.current;
    if (stack.length === 0) return;
    const snap = stack.pop()!;
    const cur = lastCommittedRef.current;
    if (cur) undoStackRef.current.push(cur);
    applyingHistoryRef.current = true;
    setEditingCell(null);
    setTableEntries(structuredClone(snap.tableEntries));
    setCellOverrides({ ...snap.cellOverrides });
    setCalcScratchBundles(structuredClone(snap.calcScratchBundles));
  }, []);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable) {
          if (editInputRef.current && (t === editInputRef.current || editInputRef.current.contains(t))) {
            return;
          }
          return;
        }
      }
      if (e.key === 'z' || e.key === 'Z') {
        if (e.shiftKey) {
          e.preventDefault();
          handleRedo();
        } else {
          e.preventDefault();
          handleUndo();
        }
        return;
      }
      if (e.key === 'y' || e.key === 'Y') {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [handleUndo, handleRedo]);

  /**
   * Auto-snapshot wersji — uruchamia się co 20 zmian w polu cenaJedn (w cellOverrides)
   * LUB co 5 min od pierwszej z nich (co zajdzie pierwsze). Zapis nie blokuje UI; w razie
   * błędu po prostu ponowi się przy kolejnym tik-u timera.
   */
  const autoSnapshotRef = React.useRef({
    prevCenaSig: '',
    pendingChanges: 0,
    firstChangeAt: 0,
    lastAutoSnapshotAt: 0,
    inFlight: false,
    initialized: false,
  });

  React.useEffect(() => {
    const st = autoSnapshotRef.current;
    const entries: string[] = [];
    for (const k of Object.keys(cellOverrides)) {
      if (!k.endsWith(':cenaJedn')) continue;
      entries.push(`${k}=${cellOverrides[k] ?? ''}`);
    }
    entries.sort();
    const sig = entries.join('|');
    if (!st.initialized) {
      st.prevCenaSig = sig;
      st.initialized = true;
      return;
    }
    if (sig !== st.prevCenaSig) {
      const prevSet = new Set(st.prevCenaSig ? st.prevCenaSig.split('|') : []);
      let diff = 0;
      for (const e of entries) if (!prevSet.has(e)) diff += 1;
      const prevKeys = new Set(
        (st.prevCenaSig ? st.prevCenaSig.split('|') : []).map((s) => s.split('=')[0])
      );
      const nextKeys = new Set(entries.map((s) => s.split('=')[0]));
      prevKeys.forEach((k) => {
        if (!nextKeys.has(k)) diff += 1;
      });
      st.pendingChanges += Math.max(diff, 1);
      if (st.firstChangeAt === 0) st.firstChangeAt = Date.now();
      st.prevCenaSig = sig;
    }
  }, [cellOverrides]);

  React.useEffect(() => {
    const AUTO_COUNT_THRESHOLD = 20;
    const AUTO_TIME_THRESHOLD_MS = 5 * 60 * 1000;
    const MIN_SPACING_MS = 30 * 1000;
    const TICK_MS = 30 * 1000;

    const tick = async () => {
      const st = autoSnapshotRef.current;
      if (st.inFlight || st.pendingChanges <= 0) return;
      const now = Date.now();
      if (now - st.lastAutoSnapshotAt < MIN_SPACING_MS) return;
      const many = st.pendingChanges >= AUTO_COUNT_THRESHOLD;
      const fiveMin =
        st.firstChangeAt > 0 && now - st.firstChangeAt >= AUTO_TIME_THRESHOLD_MS;
      if (!many && !fiveMin) return;

      const src = tableDataForBroadcastRef.current;
      const payload: EasyPrzedmiarSnapshotPayload = {
        tableEntries: structuredClone(src.tableEntries) as unknown[],
        cellOverrides: { ...src.cellOverrides },
        calcScratchBundles: structuredClone(src.calcScratchBundles) as Record<string, unknown>,
      };
      st.inFlight = true;
      try {
        const res = await createEasyPrzedmiarVersionSnapshot(estimateId, {
          payload,
          origin: 'auto',
          estimateMeta: {
            estimate_number: estimate.estimate_number,
            name: estimate.name,
            object_name: estimate.object_name,
          },
        });
        if (res.success) {
          setVersionListTick((t) => t + 1);
        }
      } catch {
        // Nie alarmujemy usera — następny tick spróbuje ponownie.
      } finally {
        st.lastAutoSnapshotAt = Date.now();
        st.pendingChanges = 0;
        st.firstChangeAt = 0;
        st.inFlight = false;
      }
    };

    const interval = setInterval(() => {
      void tick();
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [estimateId, estimate.estimate_number, estimate.name, estimate.object_name]);

  const handleSavePrzedmiarVersionAs = React.useCallback(async () => {
    if (versionLoadBusy) return;
    const payload: EasyPrzedmiarSnapshotPayload = {
      tableEntries: structuredClone(tableEntries) as unknown[],
      cellOverrides: { ...cellOverrides },
      calcScratchBundles: structuredClone(calcScratchBundles) as Record<string, unknown>,
    };
    try {
      const data = await createEasyPrzedmiarVersionSnapshot(estimateId, {
        payload,
        origin: 'manual',
        estimateMeta: {
          estimate_number: estimate.estimate_number,
          name: estimate.name,
          object_name: estimate.object_name,
        },
      });
      if (!data.success || !data.file) {
        showNotification(data.message || 'Nie udało się zapisać wersji na serwerze.', 'error');
        return;
      }
      showNotification(`Zapisano wersję ${data.file.label} na serwerze.`, 'success');
      setVersionListTick((t) => t + 1);
    } catch (e) {
      showNotification((e as Error)?.message || 'Nie udało się zapisać wersji.', 'error');
    }
  }, [
    versionLoadBusy,
    estimateId,
    tableEntries,
    cellOverrides,
    calcScratchBundles,
    estimate.estimate_number,
    estimate.name,
    estimate.object_name,
    showNotification,
  ]);

  const handleWczytajVersionById = React.useCallback(async (targetId: number) => {
    if (versionLoadBusy) return;
    if (!Number.isFinite(targetId) || targetId <= 0) return;

    setVersionLoadBusy(true);
    try {
      const backupPayload: EasyPrzedmiarSnapshotPayload = {
        tableEntries: structuredClone(tableEntries) as unknown[],
        cellOverrides: { ...cellOverrides },
        calcScratchBundles: structuredClone(calcScratchBundles) as Record<string, unknown>,
      };
      const backupRes = await createEasyPrzedmiarVersionSnapshot(estimateId, {
        payload: backupPayload,
        origin: 'auto_before_restore',
        estimateMeta: {
          estimate_number: estimate.estimate_number,
          name: estimate.name,
          object_name: estimate.object_name,
        },
      });
      if (!backupRes.success) {
        showNotification(backupRes.message || 'Nie udało się zapisać kopii bieżącego stanu na serwerze.', 'error');
        return;
      }

      const snap = await getEasyPrzedmiarVersionSnapshot(estimateId, targetId);
      if (!snap.success || !snap.file) {
        showNotification(snap.message || 'Nie znaleziono wybranej wersji na serwerze.', 'warning');
        return;
      }
      const file = snap.file;
      const entries = file.payload.tableEntries as PrzedmiarTableEntry[];
      const ov = { ...file.payload.cellOverrides };
      const bundles = structuredClone(file.payload.calcScratchBundles) as Record<string, CalcScratchBundle>;
      suppressNextHistoryRef.current = true;
      setTableEntries(entries);
      setCellOverrides(ov);
      setCalcScratchBundles(bundles);

      const res = await putEstimateEasyPrzedmiar(
        estimateId,
        buildEasyPrzedmiarRowsPayload(entries, ov, bundles)
      );
      setVersionListTick((t) => t + 1);
      if (!res.success && res.message) showNotification(res.message, 'error');
      else showNotification('Wczytano wersję i zapisano przedmiar w bazie.', 'success');
    } catch (e) {
      showNotification((e as Error)?.message || 'Nie udało się wczytać wersji.', 'error');
    } finally {
      setVersionLoadBusy(false);
    }
  }, [versionLoadBusy, estimateId, tableEntries, cellOverrides, calcScratchBundles, estimate.estimate_number, estimate.name, estimate.object_name, showNotification]);

  /** Klucz sortowania filtra działów: WBS, potem Lp. z importu, potem numer 1…n w widoku. */
  const dzialLabelToSortKey = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const rv of withDzialLp(tableEntries)) {
      if (rv.kind !== 'dzial') continue;
      const label = sectionFilterLabel(rv.entry);
      const on = normalizeWbsOutlineString(rv.entry.outlineNr);
      const key =
        on ||
        (rv.entry.lpZrodlowy != null && String(rv.entry.lpZrodlowy).trim() !== ''
          ? String(rv.entry.lpZrodlowy).trim()
          : String(rv.lpDzialu));
      if (!m.has(label)) m.set(label, key);
    }
    return m;
  }, [tableEntries]);

  /** Lista działów w filtrze — te same etykiety co nagłówki; sortowanie po WBS / Lp., potem po nazwie. */
  const dzialSelectOptions = React.useMemo(() => {
    const fromData = new Set<string>();
    let lastHeader: PrzedmiarDzialRow | null = null;
    for (const e of tableEntries) {
      if (e.kind === 'dzial') {
        lastHeader = e;
        fromData.add(sectionFilterLabel(e));
      } else {
        const lab = lastHeader ? sectionFilterLabel(lastHeader) : e.dzial.trim() || 'Inne';
        if (lab) fromData.add(lab);
      }
    }
    return Array.from(fromData).sort((a, b) => {
      const la = dzialLabelToSortKey.get(a) ?? '';
      const lb = dzialLabelToSortKey.get(b) ?? '';
      const byKey = la.localeCompare(lb, 'pl', { numeric: true });
      if (byKey !== 0) return byKey;
      return a.localeCompare(b, 'pl');
    });
  }, [tableEntries, dzialLabelToSortKey]);

  const dzialOptionsForSelect = React.useMemo(() => {
    if (dzial !== 'wszystkie' && !dzialSelectOptions.includes(dzial)) {
      return [...dzialSelectOptions, dzial];
    }
    return dzialSelectOptions;
  }, [dzialSelectOptions, dzial]);

  const maxDzialHierarchyDepth = React.useMemo(
    () => maxDzialHierarchyDepthInEntries(tableEntries),
    [tableEntries]
  );

  /** Ta sama paleta co wiersz działu w arkuszu — w menu tylko gdy jest hierarchia WBS (1.1…); przy samym 1,2,3 bez podpoziomów neutralnie jak TES. */
  const dzialFilterMenuItemVizByLabel = React.useMemo(() => {
    const maxD = maxDzialHierarchyDepth;
    const flat = przedmiarWbsHierarchyIsFlat(tableEntries);
    const m = new Map<string, DzialHierarchyVisual>();
    for (const e of tableEntries) {
      if (e.kind !== 'dzial') continue;
      const label = sectionFilterLabel(e);
      if (!m.has(label)) {
        const d = dzialHierarchyDepth(e);
        m.set(
          label,
          flat || d > 1 ? dzialSublevelNeutralVisual() : dzialHierarchyVisual(1, maxD)
        );
      }
    }
    return m;
  }, [tableEntries, maxDzialHierarchyDepth]);

  const dzialLabelToWartoscNettoFormatted = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const d of dzialOptionsForSelect) {
      m.set(d, formatKwotaPl2(nettoForDzialFilterLabel(tableEntries, d, cellOverrides, resolvedCenaJednByEntryId)));
    }
    return m;
  }, [tableEntries, cellOverrides, dzialOptionsForSelect, resolvedCenaJednByEntryId]);

  const [colResizeMode, setColResizeMode] = React.useState(false);
  const [dragCol, setDragCol] = React.useState<{
    key: PrzedmiarColKey;
    startX: number;
    startW: number;
    rightKey?: PrzedmiarColKey;
    startWRight?: number;
  } | null>(null);

  const [columnWidths, setColumnWidths] = React.useState<Record<PrzedmiarColKey, number>>(() => ({ ...DEFAULT_PRZEDMIAR_COL_WIDTHS }));
  const getWidthNum = React.useCallback(
    (key: PrzedmiarColKey) => columnWidths[key] ?? DEFAULT_PRZEDMIAR_COL_WIDTHS[key] ?? 40,
    [columnWidths]
  );
  const getColW = React.useCallback((key: PrzedmiarColKey) => `${getWidthNum(key)}px`, [getWidthNum]);

  /** Lista kolumn dostępnych w aktualnym widoku — dla „Zweryfikowanych" obejmuje dodatkowo `branza`. */
  const effectiveAllCols = React.useMemo<PrzedmiarColKey[]>(
    () => (isVerifiedListLayout ? [...ALL_PRZEDMIAR_COLS_VERIFIED] : [...ALL_PRZEDMIAR_COLS]),
    [isVerifiedListLayout]
  );
  const effectiveOrderStorageKey = isVerifiedListLayout
    ? PRZEDMIAR_ORDER_KEY_VERIFIED
    : PRZEDMIAR_ORDER_KEY;
  const effectiveVisibleStorageKey = isVerifiedListLayout
    ? PRZEDMIAR_VISIBLE_KEY_VERIFIED
    : PRZEDMIAR_VISIBLE_KEY;

  const [columnOrder, setColumnOrder] = React.useState<PrzedmiarColKey[]>(() => {
    try {
      const raw = localStorage.getItem(effectiveOrderStorageKey);
      if (raw) {
        const ord = JSON.parse(raw) as string[];
        if (Array.isArray(ord) && ord.length) {
          return ord.filter((k): k is PrzedmiarColKey => effectiveAllCols.includes(k as PrzedmiarColKey)).concat(
            effectiveAllCols.filter((k) => !ord.includes(k))
          );
        }
      }
    } catch {
      /* ignore */
    }
    return [...effectiveAllCols];
  });

  const [columnsVisible, setColumnsVisible] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(effectiveVisibleStorageKey);
      if (raw) {
        const obj = JSON.parse(raw) as Record<string, boolean>;
        if (obj && typeof obj === 'object') return obj;
      }
    } catch {
      /* ignore */
    }
    return {};
  });

  const [colsOpen, setColsOpen] = React.useState(false);
  const [draftColumnsVisible, setDraftColumnsVisible] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = React.useState<PrzedmiarColKey[] | null>(null);
  const [draftWidths, setDraftWidths] = React.useState<Partial<Record<PrzedmiarColKey, number>>>({});
  const [colsPosition, setColsPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = React.useState<{
    startX: number;
    startY: number;
    top: number;
    left: number;
  } | null>(null);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(PRZEDMIAR_STORAGE_WIDTHS);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, number>;
        if (parsed && typeof parsed === 'object') {
          setColumnWidths((prev) => ({ ...DEFAULT_PRZEDMIAR_COL_WIDTHS, ...prev, ...parsed }));
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  React.useEffect(() => {
    try {
      localStorage.setItem(effectiveOrderStorageKey, JSON.stringify(columnOrder));
    } catch {
      /* ignore */
    }
  }, [columnOrder, effectiveOrderStorageKey]);

  React.useEffect(() => {
    try {
      localStorage.setItem(effectiveVisibleStorageKey, JSON.stringify(columnsVisible));
    } catch {
      /* ignore */
    }
  }, [columnsVisible, effectiveVisibleStorageKey]);

  React.useEffect(() => {
    try {
      localStorage.setItem(PRZEDMIAR_STORAGE_WIDTHS, JSON.stringify(columnWidths));
    } catch {
      /* ignore */
    }
  }, [columnWidths]);

  React.useEffect(() => {
    if (!dragCol) return;
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const minW = 24;
      const newW = Math.max(minW, dragCol.startW + dx);
      if (dragCol.rightKey && typeof dragCol.startWRight === 'number') {
        const newRight = Math.max(minW, dragCol.startWRight - dx);
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey!]: newRight }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey!]: newRight }));
      } else {
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
      }
    };
    const onUp = () => setDragCol(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true } as { once: boolean });
    return () => window.removeEventListener('mousemove', onMove);
  }, [dragCol]);

  React.useEffect(() => {
    if (!colsDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - colsDragging.startX;
      const dy = e.clientY - colsDragging.startY;
      const nextTop = Math.max(
        0,
        Math.min((window?.innerHeight || 0) - PRZEDMIAR_COLUMNS_DIALOG.height, colsDragging.top + dy)
      );
      const nextLeft = Math.max(
        0,
        Math.min((window?.innerWidth || 0) - PRZEDMIAR_COLUMNS_DIALOG.width, colsDragging.left + dx)
      );
      setColsPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => setColsDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [colsDragging]);

  React.useEffect(() => {
    if (!colsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        setColsOpen(false);
        setColResizeMode(false);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
        setDraftWidths({});
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [colsOpen]);

  const colV = React.useCallback(
    (key: PrzedmiarColKey) => ({ ...columnsVisible, ...(draftColumnsVisible || {}) })[key] !== false,
    [columnsVisible, draftColumnsVisible]
  );

  const visibleKeysEffective = React.useMemo(() => {
    const ord = draftOrder ?? columnOrder;
    const v = ord.filter((k) => effectiveAllCols.includes(k) && colV(k));
    if (v.length > 0) return v;
    return [...effectiveAllCols];
  }, [draftOrder, columnOrder, colV, effectiveAllCols]);

  const openColumnsPanel = React.useCallback(() => {
    setDraftColumnsVisible({ ...columnsVisible });
    setDraftOrder([...columnOrder]);
    setDraftWidths({ ...columnWidths });
    setColResizeMode(true);
    setColsOpen(true);
    setColsPosition({ top: 0, left: 0 });
  }, [columnsVisible, columnOrder, columnWidths]);

  const closeColumnsPanel = React.useCallback(() => {
    setColsOpen(false);
    setColResizeMode(false);
    setDraftColumnsVisible(null);
    setDraftOrder(null);
    setDraftWidths({});
  }, []);

  /* Rejestruj uchwyt panelu kolumn dla rodzica (np. przycisk „Dostosuj kolumny" w toolbar'ze strony
   * `verifiedList`), żeby z zewnątrz można było otworzyć/zamknąć panel i zaznaczyć przycisk. */
  React.useEffect(() => {
    if (!onColumnsPanelHandleReady) return;
    onColumnsPanelHandleReady({
      open: openColumnsPanel,
      close: closeColumnsPanel,
      isOpen: colsOpen,
    });
    return () => onColumnsPanelHandleReady(null);
  }, [onColumnsPanelHandleReady, openColumnsPanel, closeColumnsPanel, colsOpen]);

  const rows = React.useMemo(() => {
    if (kosztorysRowSort === 'default') return rowsFilteredBase;
    return sortPrzedmiarTableEntriesByNetto(
      rowsFilteredBase,
      cellOverrides,
      kosztorysRowSort === 'nettoAsc' ? 'asc' : 'desc',
      resolvedCenaJednByEntryId
    );
  }, [rowsFilteredBase, kosztorysRowSort, cellOverrides, resolvedCenaJednByEntryId]);

  const handleAddPozycja = React.useCallback(() => {
    pendingScrollTableToBottomRef.current = true;
    setTableEntries((prev) => {
      const row: PrzedmiarPozycjaRow = {
        kind: 'pozycja',
        id: newPrzedmiarEntryId('poz'),
        lp: nextPozycjaLp(prev),
        podstawa: '',
        opis: '',
        jm: '',
        ilosc: '',
        cenaJedn: '',
        uwagi: '',
        dzial: inferDzialForNewPozycja(prev),
      };
      return [...prev, row];
    });
  }, []);

  const handleAddDzial = React.useCallback(() => {
    pendingScrollTableToBottomRef.current = true;
    setTableEntries((prev) => {
      const name = uniqueDzialName(prev, 'Nowy dział');
      return [
        ...prev,
        {
          kind: 'dzial',
          id: newPrzedmiarEntryId('dzial'),
          tytul: name,
          dzialKey: name,
        },
      ];
    });
  }, []);

  const rowsView = React.useMemo(() => withDzialLp(rows), [rows]);

  /** Raz na render: max indeks kolumny na wiersz — nawigacja / zaznaczenie bez powtarzania `colRolesForRow` × komórki. */
  const sheetMaxColByRow = React.useMemo(
    () => rowsView.map((rv) => maxColIndex(rv, visibleKeysEffective)),
    [rowsView, visibleKeysEffective]
  );

  /** Rôle kolumn per wiersz — jeden raz zamiast wywołań w każdym `handleSheetCellKeyDown` / wierszu. */
  const rolesPerRow = React.useMemo(
    () => rowsView.map((rv) => colRolesForRow(rv, visibleKeysEffective)),
    [rowsView, visibleKeysEffective]
  );

  const rowsViewRef = React.useRef(rowsView);
  const visibleKeysEffectiveRef = React.useRef(visibleKeysEffective);
  React.useLayoutEffect(() => {
    rowsViewRef.current = rowsView;
    visibleKeysEffectiveRef.current = visibleKeysEffective;
  }, [rowsView, visibleKeysEffective]);

  const applyRemotePrzedmiarFromSocket = React.useCallback(
    (raw: { tableEntries: unknown; cellOverrides: Record<string, string>; calcScratchBundles: unknown }) => {
      if (!isLikelyRemotePrzedmiarBroadcastShape(raw)) return;
      suppressNextHistoryRef.current = true;

      const ec = editingCellRef.current;
      const entries: PrzedmiarTableEntry[] = structuredClone(raw.tableEntries);
      let overrides = { ...raw.cellOverrides };
      let bundles = structuredClone(raw.calcScratchBundles) as Record<string, CalcScratchBundle>;

      if (ec) {
        const stillThere = entries.some((e: PrzedmiarTableEntry) => e.id === ec.entryId);
        if (!stillThere) {
          setEditingCell(null);
        } else {
          const rv = withDzialLp(entries).find((row) => row.entry.id === ec.entryId);
          if (rv) {
            const roles = colRolesForRow(rv, visibleKeysEffectiveRef.current);
            const role = roles[ec.col];
            const k = role ? makeCellOverrideKeyForRole(rv.entry.id, rv, role) : null;
            if (k != null && cellOverridesRef.current[k] !== undefined) {
              overrides[k] = cellOverridesRef.current[k];
            }
          }
        }
      }

      const pid = calcPanelEntryIdRef.current;
      if (pid && calcScratchBundlesRef.current[pid]) {
        bundles = { ...bundles, [pid]: structuredClone(calcScratchBundlesRef.current[pid]) };
      }

      przedmiarSkipBroadcastSigRef.current = przedmiarEditorSnapshotSig({
        tableEntries: entries,
        cellOverrides: overrides,
        calcScratchBundles: bundles,
      });

      setTableEntries(entries);
      setCellOverrides(overrides);
      setCalcScratchBundles(bundles);
    },
    []
  );

  React.useEffect(() => {
    const eid = estimateId != null ? String(estimateId) : '';
    if (!eid || !kosztorysTabActive || !user?.user_id) return;
    const self = String(user.user_id);
    const off = onEasyEstimatePrzedmiarBroadcast((p) => {
      if (p.estimateId !== eid) return;
      if (!p.fromUserId || p.fromUserId === self) return;
      applyRemotePrzedmiarFromSocket({
        tableEntries: p.tableEntries,
        cellOverrides: p.cellOverrides,
        calcScratchBundles: p.calcScratchBundles,
      });
    });
    return off;
  }, [estimateId, kosztorysTabActive, user?.user_id, applyRemotePrzedmiarFromSocket]);

  React.useEffect(() => {
    const eid = estimateId != null ? String(estimateId) : '';
    if (!eid || !kosztorysTabActive || !user?.user_id || isVerifiedListLayout) return;
    if (kosztorysOthersPresent.length === 0) return;
    const t = window.setTimeout(() => {
      if (suppressOutgoingPrzedmiarBroadcastRef.current) return;
      const snap: PrzedmiarEditorSnapshot = {
        tableEntries,
        cellOverrides,
        calcScratchBundles,
      };
      const sig = przedmiarEditorSnapshotSig(snap);
      const skip = przedmiarSkipBroadcastSigRef.current;
      if (skip != null) {
        if (skip === sig) {
          przedmiarSkipBroadcastSigRef.current = null;
          return;
        }
        przedmiarSkipBroadcastSigRef.current = null;
      }
      emitEasyEstimatePrzedmiarBroadcast(eid, snap);
    }, 320);
    return () => window.clearTimeout(t);
  }, [
    tableEntries,
    cellOverrides,
    calcScratchBundles,
    estimateId,
    kosztorysTabActive,
    user?.user_id,
    isVerifiedListLayout,
    kosztorysOthersPresent.length,
  ]);

  const prevKosztorysOthersLenRef = React.useRef(0);
  React.useEffect(() => {
    const n = kosztorysOthersPresent.length;
    const prev = prevKosztorysOthersLenRef.current;
    prevKosztorysOthersLenRef.current = n;
    if (prev !== 0 || n === 0) return;
    if (!kosztorysTabActive || !user?.user_id || isVerifiedListLayout) return;
    const eid = estimateId != null ? String(estimateId) : '';
    if (!eid) return;
    const t = window.setTimeout(() => {
      if (suppressOutgoingPrzedmiarBroadcastRef.current) return;
      const snap = tableDataForBroadcastRef.current;
      przedmiarSkipBroadcastSigRef.current = null;
      emitEasyEstimatePrzedmiarBroadcast(eid, snap);
    }, 80);
    return () => window.clearTimeout(t);
  }, [
    kosztorysOthersPresent.length,
    kosztorysTabActive,
    user?.user_id,
    isVerifiedListLayout,
    estimateId,
  ]);

  const insertPozycjaBelowViewRow = React.useCallback(
    (rowIdx: number) => {
      const rv = rowsView[rowIdx];
      if (!rv) return;
      setTableEntries((prev) => insertPozycjaRowBelowAnchor(prev, rv.entry.id));
      const nextR = rowIdx + 1;
      const nextCur = { r: nextR, c: SHEET_COL_GUTTER as number };
      sheetCursorRef.current = nextCur;
      setSheetSelectionAnchor(nextCur);
      setSheetCursor(nextCur);
    },
    [rowsView]
  );

  const deletePozycjaViewRow = React.useCallback(
    (rowIdx: number) => {
      const rv = rowsView[rowIdx];
      if (!rv || rv.kind !== 'pozycja') {
        showNotification('Usuwać można tylko wiersze pozycji (nagłówek działu — z menu „…”).', 'info');
        return;
      }
      const id = rv.entry.id;
      setTableEntries((prev) => prev.filter((e) => e.id !== id));
      setCellOverrides((o) => {
        const next = { ...o };
        Object.keys(next).forEach((k) => {
          if (k.startsWith(`${id}:`)) delete next[k];
        });
        return next;
      });
      setCalcPanelEntryId((open) => (open === id ? null : open));
      setCalcScratchBundles((g) => {
        if (!g[id]) return g;
        const next = { ...g };
        delete next[id];
        return next;
      });
      setEditingCell((ec) => (ec?.entryId === id ? null : ec));
      const nextR = Math.max(0, rowIdx - 1);
      const nextCur = { r: nextR, c: SHEET_COL_GUTTER as number };
      sheetCursorRef.current = nextCur;
      setSheetSelectionAnchor(nextCur);
      setSheetCursor(nextCur);
    },
    [rowsView, showNotification]
  );

  const lastEmittedSheetCellSigRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const eid = estimate.id != null ? String(estimate.id) : '';
    if (!eid || !kosztorysTabActive || !user?.user_id) {
      if (eid && lastEmittedSheetCellSigRef.current !== null) {
        emitEasyEstimateSheetCell(
          eid,
          null,
          user?.display_name || user?.first_name || user?.email,
          user?.avatar_url ?? undefined
        );
        lastEmittedSheetCellSigRef.current = null;
      }
      return;
    }
    let cellId: string | null = null;
    if (sheetCursor !== null && sheetCursor.c !== SHEET_COL_GUTTER) {
      const rv = rowsView[sheetCursor.r];
      const roles = rolesPerRow[sheetCursor.r];
      if (rv && roles && sheetCursor.c >= 0 && sheetCursor.c < roles.length) {
        cellId = stableSheetCellId(rv, roles[sheetCursor.c]);
      }
    }
    const sig = `${eid}|${cellId ?? ''}`;
    if (lastEmittedSheetCellSigRef.current === sig) return;
    lastEmittedSheetCellSigRef.current = sig;
    emitEasyEstimateSheetCell(
      eid,
      cellId,
      user.display_name || user.first_name || user.email || undefined,
      user.avatar_url ?? undefined
    );
  }, [
    estimate.id,
    kosztorysTabActive,
    user?.user_id,
    user?.display_name,
    user?.first_name,
    user?.email,
    user?.avatar_url,
    sheetCursor,
    rowsView,
    rolesPerRow,
  ]);

  const sheetSelBounds = React.useMemo(() => {
    const rangeCornerA = sheetSelectionAnchor ?? sheetCursor;
    const rangeIsMultiple =
      sheetCursor !== null &&
      rangeCornerA !== null &&
      (rangeCornerA.r !== sheetCursor.r || rangeCornerA.c !== sheetCursor.c);
    const bounds =
      sheetCursor !== null && rangeCornerA !== null
        ? {
            r0: Math.min(rangeCornerA.r, sheetCursor.r),
            r1: Math.max(rangeCornerA.r, sheetCursor.r),
            c0: Math.min(rangeCornerA.c, sheetCursor.c),
            c1: Math.max(rangeCornerA.c, sheetCursor.c),
          }
        : null;
    return { rangeCornerA, rangeIsMultiple, bounds };
  }, [sheetCursor, sheetSelectionAnchor]);

  React.useEffect(() => {
    if (!kosztorysTabActive) {
      onNumericColumnSelectionSumChange?.({ show: false, sum: 0, selectionKind: 'currency' });
      return;
    }
    const payload = computePrzedmiarNumericColumnSelectionSum(
      sheetSelBounds.bounds,
      rowsView,
      rolesPerRow,
      cellOverrides,
      resolvedCenaJednByEntryId
    );
    onNumericColumnSelectionSumChange?.(payload);
  }, [
    kosztorysTabActive,
    sheetSelBounds.bounds,
    rowsView,
    rolesPerRow,
    cellOverrides,
    resolvedCenaJednByEntryId,
    onNumericColumnSelectionSumChange,
  ]);

  const colWidthsSig = React.useMemo(() => JSON.stringify(columnWidths), [columnWidths]);

  const rowSelectionSigs = React.useMemo(() => {
    const { rangeCornerA, rangeIsMultiple } = sheetSelBounds;
    return rowsView.map((_, rowIndex) => {
      const rolesLen = rolesPerRow[rowIndex]?.length ?? 0;
      return computeRowSelectionSig(
        rowIndex,
        rolesLen,
        sheetCursor,
        rangeCornerA,
        rangeIsMultiple,
        rowsView,
        visibleKeysEffective,
        sheetMaxColByRow
      );
    });
  }, [rowsView, rolesPerRow, sheetSelBounds, sheetCursor, visibleKeysEffective, sheetMaxColByRow]);

  React.useLayoutEffect(() => {
    if (!pendingScrollTableToBottomRef.current) return;
    pendingScrollTableToBottomRef.current = false;
    const el = tableScrollContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [rowsView]);

  React.useEffect(() => {
    if (rowsView.length === 0) {
      setSheetCursor(null);
      setSheetSelectionAnchor(null);
      return;
    }
    setSheetCursor((prev) => {
      if (prev === null) return prev;
      const r = Math.min(prev.r, rowsView.length - 1);
      const c = clampSheetCol(rowsView[r], prev.c, visibleKeysEffective);
      if (r === prev.r && c === prev.c) return prev;
      return { r, c };
    });
    setSheetSelectionAnchor((prev) => {
      if (prev === null) return null;
      const r = Math.min(prev.r, rowsView.length - 1);
      const c = clampSheetCol(rowsView[r], prev.c, visibleKeysEffective);
      if (r === prev.r && c === prev.c) return prev;
      return { r, c };
    });
  }, [rowsView, visibleKeysEffective]);

  React.useEffect(() => {
    if (!editingCell) return;
    const exists = rowsView.some((x) => x.entry.id === editingCell.entryId);
    if (!exists) setEditingCell(null);
  }, [rowsView, editingCell]);

  React.useLayoutEffect(() => {
    if (editingCell) return;
    if (sheetCursor === null) return;
    const el = sheetCellRefs.current.get(sheetCellDomKey(sheetCursor.r, sheetCursor.c));
    if (el && document.activeElement !== el) {
      el.focus({ preventScroll: true });
      scrollSheetCellIntoViewIfNeeded(tableScrollContainerRef.current, el);
    }
  }, [sheetCursor, editingCell]);

  React.useLayoutEffect(() => {
    if (!sheetFollowRemoteUserId) {
      lastFollowScrollSigRef.current = null;
      return;
    }
    const remote = eeRemoteSheetCells[sheetFollowRemoteUserId];
    const cellId = remote?.cellId;
    if (!cellId) return;
    const pos = sheetCursorFromStableCellId(cellId, rowsView, rolesPerRow);
    if (!pos) return;
    const sig = `${sheetFollowRemoteUserId}|${cellId}|${pos.r}|${pos.c}`;
    if (lastFollowScrollSigRef.current === sig) return;
    lastFollowScrollSigRef.current = sig;
    const el = sheetCellRefs.current.get(sheetCellDomKey(pos.r, pos.c));
    if (el) scrollSheetCellIntoViewIfNeeded(tableScrollContainerRef.current, el);
  }, [sheetFollowRemoteUserId, eeRemoteSheetCells, rowsView, rolesPerRow]);

  const beginFollowRemoteSheetUser = React.useCallback((userId: string) => {
    const prev = sheetFollowRemoteUserIdRef.current;
    if (prev === userId) {
      flushSync(() => {
        sheetFollowRemoteUserIdRef.current = null;
        setSheetFollowRemoteUserId(null);
      });
      return;
    }
    flushSync(() => {
      sheetFollowRemoteUserIdRef.current = userId;
      setSheetFollowRemoteUserId(userId);
      setSheetCursor(null);
      setSheetSelectionAnchor(null);
      sheetCursorRef.current = null;
      lastFollowScrollSigRef.current = null;
    });
    const ae = document.activeElement;
    if (ae instanceof HTMLElement && ae.closest('[data-przedmiar-sheet-cell]')) {
      ae.blur();
    }
  }, []);

  /** Start edycji wielowierszowej — jeden raz; dalsze zmiany treści przez `onEditDraftChange` + rAF (bez layout effectu na każdy znak). */
  React.useLayoutEffect(() => {
    if (!editingCell) return;
    requestAnimationFrame(() => resizeSheetEditingTextareaFromRef(editInputRef.current));
  }, [editingCell]);

  React.useLayoutEffect(() => {
    if (!editingCell) return;
    const t = window.setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [editingCell]);

  const setSheetCellRef = React.useCallback((r: number, c: number, el: unknown) => {
    const k = sheetCellDomKey(r, c);
    if (el instanceof HTMLTableCellElement) sheetCellRefs.current.set(k, el);
    else sheetCellRefs.current.delete(k);
  }, []);

  const applySheetCellPointer = React.useCallback((r: number, c: number, e: { shiftKey: boolean }) => {
    if (sheetSuppressNextClickRef.current) {
      sheetSuppressNextClickRef.current = false;
      return;
    }
    if (sheetFollowRemoteUserIdRef.current) {
      flushSync(() => {
        sheetFollowRemoteUserIdRef.current = null;
        setSheetFollowRemoteUserId(null);
      });
    }
    if (e.shiftKey && sheetCursorRef.current !== null) {
      const next = { r, c };
      setSheetCursor((prev) => (prev && prev.r === r && prev.c === c ? prev : next));
      sheetCursorRef.current = next;
      return;
    }
    setSheetSelectionAnchor((prev) => (prev && prev.r === r && prev.c === c ? prev : { r, c }));
    setSheetCursor((prev) => (prev && prev.r === r && prev.c === c ? prev : { r, c }));
  }, []);

  /**
   * Klik / dotyk / przeciągnięcie: ustala kotwicę i drugi róg prostokąta.
   * `pointermove` na `window` — działa przy przeciąganiu poza komórkę startową (touchpad, dotyk).
   */
  const handleSheetCellPointerDown = React.useCallback(
    (r: number, c: number, e: React.PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (editingCell) return;

      sheetPointerDragActiveRef.current = true;

      let anchor: SheetCursor = { r, c };
      if (e.shiftKey) {
        const fixed = sheetSelectionAnchorRef.current ?? sheetCursorRef.current;
        if (fixed) anchor = fixed;
      }

      const endCell: SheetCursor = { r, c };

      flushSync(() => {
        if (sheetFollowRemoteUserIdRef.current) {
          sheetFollowRemoteUserIdRef.current = null;
          setSheetFollowRemoteUserId(null);
        }
        setSheetSelectionAnchor(anchor);
        setSheetCursor(endCell);
        sheetCursorRef.current = endCell;
      });
      sheetSelectionAnchorRef.current = anchor;
      suppressSelectionCollapseOnFocusRef.current = true;

      let moved = false;
      const pid = e.pointerId;

      const cleanup = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);
        sheetPointerDragActiveRef.current = false;
        suppressSelectionCollapseOnFocusRef.current = false;
        if (moved) {
          sheetSuppressNextClickRef.current = true;
        }
      };

      const onPointerMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pid) return;
        const hit = trySheetCellHitFromClientPoint(
          ev.clientX,
          ev.clientY,
          rowsViewRef.current,
          visibleKeysEffectiveRef.current
        );
        if (!hit) return;
        if (hit.r === sheetCursorRef.current?.r && hit.c === sheetCursorRef.current?.c) return;
        moved = true;
        ev.preventDefault();
        sheetCursorRef.current = hit;
        setSheetCursor(hit);
        setSheetSelectionAnchor(anchor);
      };

      const onPointerUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pid) return;
        cleanup();
      };

      window.addEventListener('pointermove', onPointerMove, { passive: false });
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    },
    [editingCell]
  );

  /** Bez nowego obiektu przy tych samych (r,c) — inaczej onFocus + layout effect dają pętlę „Maximum update depth”. */
  const collapseSheetSelectionTo = React.useCallback((r: number, c: number) => {
    setSheetSelectionAnchor((prev) => (prev && prev.r === r && prev.c === c ? prev : { r, c }));
    setSheetCursor((prev) => (prev && prev.r === r && prev.c === c ? prev : { r, c }));
  }, []);

  const handleSheetCellFocus = React.useCallback(
    (r: number, c: number) => {
      if (sheetFollowRemoteUserIdRef.current) {
        sheetFollowRemoteUserIdRef.current = null;
        setSheetFollowRemoteUserId(null);
      }
      if (sheetPointerDragActiveRef.current) {
        return;
      }
      if (suppressSelectionCollapseOnFocusRef.current) {
        suppressSelectionCollapseOnFocusRef.current = false;
        return;
      }
      collapseSheetSelectionTo(r, c);
    },
    [collapseSheetSelectionTo]
  );

  const commitEditValue = React.useCallback(
    (rv: PrzedmiarRowView, role: PrzedmiarColRole, value: string) => {
      if (rv.kind === 'dzial' && role.t === 'dl') {
        const trimmed = value.trim();
        const parsed = parseWbsFromLooseUserInput(value);
        const id = rv.entry.id;
        setTableEntries((prev) =>
          prev.map((e) => {
            if (e.id !== id || e.kind !== 'dzial') return e;
            if (parsed === '') return { ...e, outlineNr: undefined };
            if (parsed !== null) return { ...e, outlineNr: parsed };
            return e;
          })
        );
        setCellOverrides((o) => {
          const next = { ...o };
          if (parsed !== null) {
            delete next[`${id}:dzialLp`];
          } else {
            next[`${id}:dzialLp`] = trimmed;
          }
          return next;
        });
        if (trimmed !== '' && parsed === null) {
          showNotification(
            'Nie udało się odczytać WBS. Podaj jeden numer (np. 1.2.1) albo listę rozdzieloną przecinkami — użyty zostanie ostatni poprawny fragment.',
            'warning'
          );
        }
        return;
      }
      if (rv.kind === 'dzial' && role.t === 'dt') {
        const trimmed = value.trim();
        const newTitle = trimmed || 'Nowy dział';
        setTableEntries((prev) => updateDzialSectionTitle(prev, rv.entry.id, newTitle));
        setCellOverrides((o) => {
          const next = { ...o };
          delete next[`${rv.entry.id}:dzialTitle`];
          return next;
        });
        return;
      }
      if (rv.kind === 'pozycja' && role.t === 'p' && role.key === 'wartoscNetto') {
        const e = rv.entry;
        const co = cellOverridesRef.current;
        const trimmed = value.trim();
        if (!trimmed) {
          setCellOverrides((o) => {
            const next = { ...o };
            next[`${e.id}:cenaJedn`] = '';
            delete next[`${e.id}:wartoscKalkZ`];
            return next;
          });
          return;
        }
        const w = parseLoosePlDecimal(trimmed);
        const iloscRaw = effectivePozycjaField(e, 'ilosc', co).trim();
        const iq = parseLoosePlDecimal(iloscRaw);
        if (w === null) return;
        if (iq === null || iq === 0) {
          showNotification('Podaj ilość większą od zera, aby wyliczyć cenę jedn. z wartości netto.', 'warning');
          return;
        }
        const cenaRounded = cenaJednFromWartoscIlosc(w, iq);
        if (!Number.isFinite(cenaRounded)) return;
        const cenaStr = formatCenaJednCommitted(String(cenaRounded));
        setCellOverrides((o) => ({
          ...o,
          [`${e.id}:cenaJedn`]: cenaStr,
          [`${e.id}:wartoscKalkZ`]: 'wartosc',
        }));
        return;
      }
      const k = makeCellOverrideKeyForRole(rv.entry.id, rv, role);
      if (!k) return;
      const v =
        rv.kind === 'pozycja' && role.t === 'p' && role.key === 'cenaJedn'
          ? formatCenaJednCommitted(value)
          : rv.kind === 'pozycja' && role.t === 'p' && role.key === 'ilosc'
            ? formatIloscCommitted(value)
            : value;
      setCellOverrides((o) => {
        const next = { ...o, [k]: v };
        if (rv.kind === 'pozycja' && role.t === 'p' && (role.key === 'cenaJedn' || role.key === 'ilosc')) {
          next[`${rv.entry.id}:wartoscKalkZ`] = 'cena';
        }
        return next;
      });
    },
    [showNotification]
  );

  const handleEditInputKeyDown = React.useCallback(
    (e: React.KeyboardEvent, rowIdx: number) => {
      if (!editingCell) return;
      const { entryId, col } = editingCell;
      const rv = rowsView[rowIdx];
      if (!rv || rv.entry.id !== entryId) return;
      const roles = colRolesForRow(rv, visibleKeysEffective);
      const role = roles[col];
      if (!role) return;

      if (e.key === 'Enter') {
        if (isMultilinePrzedmiarEditRole(rv, role) && e.shiftKey) {
          return;
        }
        e.preventDefault();
        commitEditValue(rv, role, editDraft);
        setEditingCell(null);
        const next = navigateSheet(rowsView, visibleKeysEffective, { r: rowIdx, c: col }, 'ArrowDown');
        setSheetSelectionAnchor(next);
        setSheetCursor(next);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        skipBlurCommitRef.current = true;
        setEditingCell(null);
        window.setTimeout(() => {
          skipBlurCommitRef.current = false;
        }, 0);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        commitEditValue(rv, role, editDraft);
        setEditingCell(null);
        const dir = e.shiftKey ? 'ArrowLeft' : 'ArrowRight';
        const next = navigateSheet(rowsView, visibleKeysEffective, { r: rowIdx, c: col }, dir);
        setSheetSelectionAnchor(next);
        setSheetCursor(next);
      }
    },
    [editingCell, editDraft, rowsView, visibleKeysEffective, commitEditValue]
  );

  const handleEditBlur = React.useCallback(() => {
    if (!editingCell) return;
    if (skipBlurCommitRef.current) return;
    const rv = rowsView.find((x) => x.entry.id === editingCell.entryId);
    if (!rv) {
      setEditingCell(null);
      return;
    }
    const roles = colRolesForRow(rv, visibleKeysEffective);
    const role = roles[editingCell.col];
    if (role) commitEditValue(rv, role, editDraft);
    setEditingCell(null);
  }, [editingCell, editDraft, rowsView, visibleKeysEffective, commitEditValue]);

  const handleSheetCellKeyDown = React.useCallback(
    (e: React.KeyboardEvent, rowIdx: number, colIdx: number) => {
      if (editingCell) return;
      if (sheetFollowRemoteUserIdRef.current) {
        sheetFollowRemoteUserIdRef.current = null;
        setSheetFollowRemoteUserId(null);
      }

      if (colIdx === SHEET_COL_GUTTER) {
        const rvG = rowsView[rowIdx];
        if (!rvG) return;
        if (e.key === 'Insert' || e.key === '+' || (e.key === '=' && e.shiftKey)) {
          e.preventDefault();
          e.stopPropagation();
          insertPozycjaBelowViewRow(rowIdx);
          return;
        }
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          e.stopPropagation();
          deletePozycjaViewRow(rowIdx);
          return;
        }
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        const fromG: SheetCursor = sheetCursorRef.current ?? { r: rowIdx, c: SHEET_COL_GUTTER };
        const nextG = navigateSheet(rowsView, visibleKeysEffective, fromG, e.key);
        if (nextG.r === fromG.r && nextG.c === fromG.c) return;
        e.preventDefault();
        e.stopPropagation();
        sheetCursorRef.current = nextG;
        flushSync(() => {
          if (e.shiftKey) {
            suppressSelectionCollapseOnFocusRef.current = true;
            setSheetCursor(nextG);
          } else {
            setSheetSelectionAnchor(nextG);
            setSheetCursor(nextG);
          }
        });
        return;
      }

      const rv = rowsView[rowIdx];
      if (!rv) return;
      const roles = rolesPerRow[rowIdx];
      const role = roles[colIdx];
      if (!role) return;

      if (e.key === 'Enter' && isCellEditableForRole(rv, role)) {
        if (layoutVariantReadOnlyRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        const id = rv.entry.id;
        const co = cellOverridesRef.current;
        const k = makeCellOverrideKeyForRole(id, rv, role);
        const base = k ? co[k] : undefined;
        let draft = base !== undefined ? base : getCellRawValueFromRole(rv, role, co);
        if (rv.kind === 'pozycja' && role.t === 'p' && role.key === 'cenaJedn' && draft.trim() !== '') {
          draft = formatCenaJednDisplayed(draft);
        }
        if (rv.kind === 'pozycja' && role.t === 'p' && role.key === 'ilosc' && draft.trim() !== '') {
          draft = formatIloscDisplayed(draft);
        }
        setEditDraft(draft);
        setEditingCell({ entryId: id, col: colIdx });
        return;
      }

      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      /** `sheetCursorRef` + `flushSync` — szybkie strzałki widzą od razu nową komórkę i fokus z layout effectu. */
      const from: SheetCursor = sheetCursorRef.current ?? { r: rowIdx, c: colIdx };
      const next = navigateSheet(rowsView, visibleKeysEffective, from, e.key);
      if (next.r === from.r && next.c === from.c) return;

      e.preventDefault();
      e.stopPropagation();
      sheetCursorRef.current = next;
      flushSync(() => {
        if (e.shiftKey) {
          suppressSelectionCollapseOnFocusRef.current = true;
          setSheetCursor(next);
        } else {
          setSheetSelectionAnchor(next);
          setSheetCursor(next);
        }
      });
    },
    [rowsView, editingCell, visibleKeysEffective, rolesPerRow, insertPozycjaBelowViewRow, deletePozycjaViewRow]
  );

  const handleMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>, entryId: string) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuTargetEntryId(entryId);
  }, []);

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTargetEntryId(null);
  };

  const handleOpenWbsDialogFromMenu = React.useCallback(() => {
    const id = menuTargetEntryId;
    setMenuAnchor(null);
    setMenuTargetEntryId(null);
    if (!id) return;
    const e = tableEntries.find((x) => x.id === id);
    if (!e || e.kind !== 'dzial') {
      showNotification('Numer WBS można ustawić tylko dla nagłówka działu.', 'info');
      return;
    }
    setWbsDialogEntryId(id);
    setWbsDialogDraft(normalizeWbsOutlineString(e.outlineNr) || '');
    setWbsDialogOpen(true);
  }, [menuTargetEntryId, tableEntries, showNotification]);

  const handleCloseWbsDialog = React.useCallback(() => {
    setWbsDialogOpen(false);
    setWbsDialogEntryId(null);
    setWbsDialogDraft('');
  }, []);

  const handleSaveWbsDialog = React.useCallback(() => {
    const id = wbsDialogEntryId;
    if (!id) return;
    const trimmed = wbsDialogDraft.trim();
    const parsed = parseWbsFromLooseUserInput(wbsDialogDraft);
    if (trimmed !== '' && parsed === null) {
      showNotification(
        'Nie udało się odczytać WBS. Podaj jeden numer (np. 1.2.1) albo listę rozdzieloną przecinkami — zapisany zostanie ostatni poprawny fragment (np. z „1, 1.1, 1.2” → 1.2).',
        'warning'
      );
      return;
    }
    const outlineSave: string | undefined = parsed ? parsed : undefined;
    setTableEntries((prev) =>
      prev.map((e) => {
        if (e.id !== id || e.kind !== 'dzial') return e;
        return { ...e, outlineNr: outlineSave };
      })
    );
    setCellOverrides((o) => {
      const next = { ...o };
      delete next[`${id}:dzialLp`];
      return next;
    });
    handleCloseWbsDialog();
  }, [wbsDialogEntryId, wbsDialogDraft, showNotification, handleCloseWbsDialog]);

  const handleDeleteMenuRow = React.useCallback(() => {
    const id = menuTargetEntryId;
    if (!id) return;
    setTableEntries((prev) => prev.filter((e) => e.id !== id));
    setCellOverrides((o) => {
      const next = { ...o };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`${id}:`)) delete next[k];
      });
      return next;
    });
    setCalcPanelEntryId((open) => (open === id ? null : open));
    setCalcScratchBundles((g) => {
      if (!g[id]) return g;
      const next = { ...g };
      delete next[id];
      return next;
    });
    setEditingCell((ec) => (ec?.entryId === id ? null : ec));
    handleMenuClose();
  }, [menuTargetEntryId]);

  const menuTargetEntry = React.useMemo(() => {
    if (!menuTargetEntryId) return null;
    return tableEntries.find((e) => e.id === menuTargetEntryId) ?? null;
  }, [menuTargetEntryId, tableEntries]);

  const handleApprovePozycjaFromMenu = React.useCallback(() => {
    const id = menuTargetEntryId;
    setMenuAnchor(null);
    setMenuTargetEntryId(null);
    if (!id) return;
    if (!user) {
      showNotification('Zaloguj się, aby zatwierdzić pozycję.', 'warning');
      return;
    }
    const displayName =
      [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
      user.display_name?.trim() ||
      user.email?.trim() ||
      'Użytkownik';
    const today = localIsoDateOnly();
    setTableEntries((prev) =>
      prev.map((e) =>
        e.id === id && e.kind === 'pozycja'
          ? { ...e, approvedAt: today, approvedByName: displayName, approvedByUserId: user.user_id }
          : e
      )
    );
  }, [menuTargetEntryId, user, showNotification]);

  const handleRevokePozycjaApprovalFromMenu = React.useCallback(() => {
    const id = menuTargetEntryId;
    setMenuAnchor(null);
    setMenuTargetEntryId(null);
    if (!id) return;
    setTableEntries((prev) =>
      prev.map((e) =>
        e.id === id && e.kind === 'pozycja'
          ? { ...e, approvedAt: undefined, approvedByName: undefined, approvedByUserId: undefined }
          : e
      )
    );
  }, [menuTargetEntryId]);

  const openCalcPanelForPozycja = React.useCallback((entryId: string) => {
    setCalcPanelEntryId((cur) => (cur === entryId ? null : entryId));
    setCalcScratchBundles((prev) => {
      if (prev[entryId]) return prev;
      return { ...prev, [entryId]: makeEmptyScratchBundle() };
    });
  }, []);

  const calcPanelPozycja = React.useMemo(() => {
    if (!calcPanelEntryId) return null;
    for (const e of tableEntries) {
      if (e.kind === 'pozycja' && e.id === calcPanelEntryId) return e;
    }
    return null;
  }, [calcPanelEntryId, tableEntries]);

  React.useEffect(() => {
    if (calcPanelEntryId && !calcPanelPozycja) setCalcPanelEntryId(null);
  }, [calcPanelEntryId, calcPanelPozycja]);

  const openGemmaPdfDialog = React.useCallback(() => {
    setGemmaHubView('menu');
    setGemmaPdfFiles([]);
    setGemmaPdfExtraPrompt('');
    setGemmaPdfStreamText('');
    setGemmaPdfDialogOpen(true);
  }, []);

  const closeGemmaPdfDialog = React.useCallback(() => {
    if (gemmaPdfBusy) return;
    setGemmaHubView('menu');
    setGemmaPdfExtraPrompt('');
    setGemmaPdfDialogOpen(false);
  }, [gemmaPdfBusy]);

  const goGemmaHubBack = React.useCallback(() => {
    if (gemmaPdfBusy) return;
    setGemmaHubView('menu');
    setGemmaPdfFiles([]);
    setGemmaPdfExtraPrompt('');
  }, [gemmaPdfBusy]);

  /**
   * Otwiera w nowych kartach PDF-y (Google Drive webViewLink), które trafiły do Gemmy
   * dla bieżącego kosztorysu — bierze najnowsze zadanie z niepustym linkiem. Blokuje podczas
   * pobierania listy zadań, ale nie czeka na kolejne kliknięcia.
   */
  const [openSourcePdfBusy, setOpenSourcePdfBusy] = React.useState(false);
  const handleOpenSourceGemmaPdf = React.useCallback(async () => {
    if (openSourcePdfBusy) return;
    const eid = Number(estimate.id);
    if (!Number.isFinite(eid) || eid <= 0) {
      showNotification('Brak identyfikatora kosztorysu — nie mogę znaleźć źródłowego PDF.', 'warning');
      return;
    }
    setOpenSourcePdfBusy(true);
    try {
      const jobs = await fetchGemmaQueueJobs();
      const mine = jobs
        .filter((j) => Number(j.estimateId) === eid)
        .filter((j) => Array.isArray(j.pdfFiles) && j.pdfFiles.some((f) => !!f.driveWebViewLink))
        .sort((a, b) => {
          const ta = a.createdAt ? Date.parse(a.createdAt) : 0;
          const tb = b.createdAt ? Date.parse(b.createdAt) : 0;
          return tb - ta;
        });
      const latest = mine[0];
      const links = (latest?.pdfFiles ?? [])
        .map((f) => (f.driveWebViewLink ? String(f.driveWebViewLink) : null))
        .filter((v): v is string => !!v);
      if (links.length === 0) {
        showNotification(
          'Nie znaleziono źródłowego PDF na Google Drive dla tego kosztorysu. Wgraj plik przez „Dodaj przedmiar z PDF (Gemma)”.',
          'info'
        );
        return;
      }
      let blocked = 0;
      for (const href of links) {
        const w = window.open(href, '_blank', 'noopener,noreferrer');
        if (!w) blocked++;
      }
      if (blocked > 0) {
        showNotification(
          `Przeglądarka zablokowała ${blocked} ${blocked === 1 ? 'okno' : 'okien'}. Zezwól na wyskakujące okienka dla tej strony.`,
          'warning'
        );
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      showNotification(`Nie udało się pobrać listy zadań Gemmy: ${msg}`, 'error');
    } finally {
      setOpenSourcePdfBusy(false);
    }
  }, [openSourcePdfBusy, estimate.id, showNotification]);

  /**
   * Automatycznie powiąż duplikaty pozycji (po „Podstawa” + „Opis”) formułą `=F{nr}` do pierwszego wystąpienia.
   * Porównanie niewrażliwe na wielkość liter i białe znaki; używa wartości z `cellOverrides`, jeśli są.
   * Nadpisuje istniejące wartości „Cena jedn.” w duplikatach (pierwsze wystąpienie pozostaje nietknięte).
   */
  const handleAutoFillDuplicateCenaJedn = React.useCallback(() => {
    const norm = (s: string) =>
      (s ?? '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('pl-PL');
    const groups = new Map<string, { firstGutterNr: number; others: string[] }>();
    tableEntries.forEach((e, idx) => {
      if (e.kind !== 'pozycja') return;
      const podstawa = norm(cellOverrides[`${e.id}:podstawa`] ?? e.podstawa);
      const opis = norm(cellOverrides[`${e.id}:opis`] ?? e.opis);
      if (!podstawa && !opis) return;
      const key = `${podstawa}\u001f${opis}`;
      const gutterNr = idx + 1;
      const g = groups.get(key);
      if (!g) groups.set(key, { firstGutterNr: gutterNr, others: [] });
      else g.others.push(e.id);
    });

    const next = { ...cellOverrides };
    let changes = 0;
    let groupsAffected = 0;
    groups.forEach((g) => {
      if (g.others.length === 0) return;
      groupsAffected += 1;
      const formula = `=F${g.firstGutterNr}`;
      g.others.forEach((oid) => {
        const k = `${oid}:cenaJedn`;
        if (next[k] !== formula) {
          next[k] = formula;
          changes += 1;
        }
      });
    });

    if (changes === 0) {
      showNotification(
        groupsAffected > 0
          ? 'Duplikaty już mają ustawione formuły — nic do uzupełnienia.'
          : 'Nie znaleziono powtarzających się pozycji (po „Podstawa” + „Opis”).',
        'info'
      );
      return;
    }
    setCellOverrides(next);
    const komorki =
      changes === 1 ? '1 komórkę' : changes % 10 >= 2 && changes % 10 <= 4 && !(changes % 100 >= 12 && changes % 100 <= 14) ? `${changes} komórki` : `${changes} komórek`;
    const grupy =
      groupsAffected === 1 ? '1 grupie' : groupsAffected % 10 >= 2 && groupsAffected % 10 <= 4 && !(groupsAffected % 100 >= 12 && groupsAffected % 100 <= 14) ? `${groupsAffected} grupach` : `${groupsAffected} grupach`;
    showNotification(
      `Uzupełniono ${komorki} „Cena jedn.” formułami w ${grupy} duplikatów.`,
      'success'
    );
  }, [tableEntries, cellOverrides, showNotification]);

  /**
   * Automatyczne wstawianie „Cena jedn." z biblioteki pozycji „zweryfikowanych".
   * Reguły:
   * - dopasowanie po „Podstawie" tolerancyjne na różnice zapisu KNR (myślniki, „KNR-W ↔ KNR …W",
   *   krótka forma „NNNN-NN" ↔ pełna „NNNN-NN00") — `canonicalizeKnrPodstawaForMatch`,
   * - wstawiamy cenę TYLKO gdy pole „Cena jedn." jest puste lub `0`,
   * - pomijamy pola z formułą (zaczynające się od `=`),
   * - dla pasującej pozycji „Auto cena" trafia do „Uwagi" jedynie gdy „Uwagi" są puste lub już mają „Auto cena",
   * - jeśli w bibliotece kilka pozycji ma tę samą Podstawę — otwieramy okno dialogowe
   *   z wyborem ceny (każda grupa osobno; domyślnie zaznaczona najnowsza po `updated_at`).
   */
  const [verifiedFillBusy, setVerifiedFillBusy] = React.useState(false);

  type VerifiedFillCandidate = {
    libId: number;
    cena: number;
    formattedCena: string;
    opis: string;
    podstawa: string;
    branza: string | null;
    filename: string | null;
    updatedAt: string;
  };
  type VerifiedFillConflict = {
    canonicalKey: string;
    displayPodstawa: string;
    candidates: VerifiedFillCandidate[];
    affectedKosztorysIds: string[];
  };
  type VerifiedFillDialogState = {
    conflicts: VerifiedFillConflict[];
    /** libId wybrany w danej grupie albo `'skip'` (nie wstawiaj). */
    selections: Record<string, number | 'skip'>;
    /** Już przygotowane nadpisania dla unikatowych dopasowań (jedyny kandydat). */
    uniqueOverrides: Record<string, string>;
    uniquePriceChanges: number;
    uniqueUwagiChanges: number;
    skippedNonEmpty: number;
    skippedFormula: number;
  };
  const [verifiedFillDialog, setVerifiedFillDialog] =
    React.useState<VerifiedFillDialogState | null>(null);

  const handleAutoFillCenaJednFromVerified = React.useCallback(async () => {
    if (verifiedFillBusy) return;
    setVerifiedFillBusy(true);
    try {
      const library = await listVerifiedLibrary();

      const norm = (s: string) =>
        (s ?? '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('pl-PL');
      const canonicalKey = (podstawa: string): string | null => {
        const knrKey = canonicalizeKnrPodstawaForMatch(podstawa);
        if (knrKey) return `knr|${knrKey}`;
        const t = norm(podstawa);
        return t ? `txt|${t}` : null;
      };

      const byKey = new Map<string, VerifiedFillCandidate[]>();
      for (const dto of library) {
        const cena = Number(dto.cena_jedn);
        if (!Number.isFinite(cena) || cena <= 0) continue;
        const podstawa = String(dto.podstawa ?? '');
        const key = canonicalKey(podstawa);
        if (!key) continue;
        const cand: VerifiedFillCandidate = {
          libId: Number(dto.id) || 0,
          cena,
          formattedCena: formatCenaJednCommitted(String(cena)),
          opis: String(dto.opis ?? ''),
          podstawa,
          branza: dto.branza ?? null,
          filename: dto.source_ath_filename ?? null,
          updatedAt: String(dto.updated_at ?? dto.created_at ?? ''),
        };
        const list = byKey.get(key);
        if (list) list.push(cand);
        else byKey.set(key, [cand]);
      }

      // Stabilne sortowanie kandydatów: najnowsze po updated_at, następnie po id desc.
      byKey.forEach((list) => {
        list.sort((a: VerifiedFillCandidate, b: VerifiedFillCandidate) => {
          if (b.updatedAt !== a.updatedAt) return b.updatedAt < a.updatedAt ? -1 : 1;
          return b.libId - a.libId;
        });
      });

      if (byKey.size === 0) {
        showNotification(
          'Brak pozycji w bibliotece „zweryfikowanych" z prawidłową Ceną jedn. — najpierw dodaj pozycje na stronie „Zweryfikowane".',
          'info'
        );
        return;
      }

      const cur = cellOverridesRef.current;
      const uniqueOverrides: Record<string, string> = {};
      const conflictsMap = new Map<string, VerifiedFillConflict>();
      const AUTO_UWAGI = 'Auto cena';
      let matched = 0;
      let uniquePriceChanges = 0;
      let uniqueUwagiChanges = 0;
      let skippedNonEmpty = 0;
      let skippedFormula = 0;

      for (const e of tableEntries) {
        if (e.kind !== 'pozycja') continue;
        const podstawa = cur[`${e.id}:podstawa`] ?? e.podstawa ?? '';
        const key = canonicalKey(String(podstawa));
        if (!key) continue;
        const candidates = byKey.get(key);
        if (!candidates || candidates.length === 0) continue;
        matched += 1;

        const cenaKey = `${e.id}:cenaJedn`;
        const cenaExisting = String(cur[cenaKey] ?? e.cenaJedn ?? '').trim();
        if (cenaExisting.startsWith('=')) {
          skippedFormula += 1;
          continue;
        }
        const numericExisting =
          cenaExisting === '' ? 0 : parseLoosePlDecimal(cenaExisting);
        const isEmptyOrZero =
          cenaExisting === '' || numericExisting === 0;
        if (!isEmptyOrZero && numericExisting !== null) {
          skippedNonEmpty += 1;
          continue;
        }

        if (candidates.length === 1) {
          const cand = candidates[0];
          if ((uniqueOverrides[cenaKey] ?? cur[cenaKey] ?? e.cenaJedn ?? '') !== cand.formattedCena) {
            uniqueOverrides[cenaKey] = cand.formattedCena;
            uniquePriceChanges += 1;
          }
          const uwagiKey = `${e.id}:uwagi`;
          const uwagiExisting = String(uniqueOverrides[uwagiKey] ?? cur[uwagiKey] ?? e.uwagi ?? '').trim();
          if (uwagiExisting === '' || uwagiExisting === AUTO_UWAGI) {
            if (uwagiExisting !== AUTO_UWAGI) {
              uniqueOverrides[uwagiKey] = AUTO_UWAGI;
              uniqueUwagiChanges += 1;
            }
          }
        } else {
          let conflict = conflictsMap.get(key);
          if (!conflict) {
            conflict = {
              canonicalKey: key,
              displayPodstawa: String(podstawa).trim() || candidates[0].podstawa,
              candidates,
              affectedKosztorysIds: [],
            };
            conflictsMap.set(key, conflict);
          }
          conflict.affectedKosztorysIds.push(e.id);
        }
      }

      if (matched === 0) {
        showNotification(
          'Nie znaleziono w kosztorysie pozycji o tej samej Podstawie co w bibliotece „zweryfikowanych".',
          'info'
        );
        return;
      }

      const conflicts = Array.from(conflictsMap.values());

      if (conflicts.length > 0) {
        // Domyślny wybór: najnowszy kandydat (pierwszy po sortowaniu).
        const selections: Record<string, number | 'skip'> = {};
        for (const c of conflicts) selections[c.canonicalKey] = c.candidates[0].libId;
        setVerifiedFillDialog({
          conflicts,
          selections,
          uniqueOverrides,
          uniquePriceChanges,
          uniqueUwagiChanges,
          skippedNonEmpty,
          skippedFormula,
        });
        return;
      }

      // Bez konfliktów — komitujemy od razu.
      if (uniquePriceChanges === 0 && uniqueUwagiChanges === 0) {
        const skipNote =
          skippedFormula + skippedNonEmpty > 0
            ? ` Pominięto ${skippedNonEmpty} z istniejącą ceną i ${skippedFormula} z formułą =.`
            : '';
        showNotification(
          `Wszystkie dopasowane pozycje (${matched}) mają już cenę lub formułę — nic do wstawienia.${skipNote}`,
          'info'
        );
        return;
      }
      setCellOverrides({ ...cur, ...uniqueOverrides });
      const komorkiCena =
        uniquePriceChanges === 1
          ? '1 komórkę'
          : uniquePriceChanges % 10 >= 2 && uniquePriceChanges % 10 <= 4 && !(uniquePriceChanges % 100 >= 12 && uniquePriceChanges % 100 <= 14)
            ? `${uniquePriceChanges} komórki`
            : `${uniquePriceChanges} komórek`;
      const skipParts: string[] = [];
      if (skippedNonEmpty > 0) skipParts.push(`${skippedNonEmpty} z istniejącą ceną`);
      if (skippedFormula > 0) skipParts.push(`${skippedFormula} z formułą =`);
      const skipNote = skipParts.length > 0 ? ` Pominięto: ${skipParts.join(', ')}.` : '';
      showNotification(
        `Uzupełniono ${komorkiCena} „Cena jedn." z biblioteki „zweryfikowanych" (dopasowano ${matched} po Podstawie).${skipNote}`,
        'success'
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showNotification(
        `Nie udało się pobrać biblioteki pozycji „zweryfikowanych": ${msg}`,
        'error'
      );
    } finally {
      setVerifiedFillBusy(false);
    }
  }, [verifiedFillBusy, tableEntries, showNotification]);

  /** Zatwierdzenie wyborów w oknie konfliktów — komituje unikatowe + wybrane przez użytkownika. */
  const confirmVerifiedFillDialog = React.useCallback(() => {
    setVerifiedFillDialog((dlg) => {
      if (!dlg) return dlg;
      const cur = cellOverridesRef.current;
      const next = { ...cur, ...dlg.uniqueOverrides };
      const AUTO_UWAGI = 'Auto cena';
      const entryById = new Map<string, PrzedmiarPozycjaRow>();
      for (const e of tableEntries) if (e.kind === 'pozycja') entryById.set(e.id, e);
      let conflictPriceChanges = 0;
      let conflictUwagiChanges = 0;
      let conflictSkipped = 0;
      for (const c of dlg.conflicts) {
        const pick = dlg.selections[c.canonicalKey];
        if (pick === 'skip' || pick === undefined) {
          conflictSkipped += c.affectedKosztorysIds.length;
          continue;
        }
        const cand = c.candidates.find((x) => x.libId === pick);
        if (!cand) {
          conflictSkipped += c.affectedKosztorysIds.length;
          continue;
        }
        for (const eid of c.affectedKosztorysIds) {
          const e = entryById.get(eid);
          if (!e) continue;
          const cenaKey = `${eid}:cenaJedn`;
          const existing = next[cenaKey] ?? e.cenaJedn ?? '';
          if (existing !== cand.formattedCena) {
            next[cenaKey] = cand.formattedCena;
            conflictPriceChanges += 1;
          }
          const uwagiKey = `${eid}:uwagi`;
          const uwagiExisting = String(next[uwagiKey] ?? e.uwagi ?? '').trim();
          if ((uwagiExisting === '' || uwagiExisting === AUTO_UWAGI) && uwagiExisting !== AUTO_UWAGI) {
            next[uwagiKey] = AUTO_UWAGI;
            conflictUwagiChanges += 1;
          }
        }
      }
      const totalPriceChanges = dlg.uniquePriceChanges + conflictPriceChanges;
      const totalUwagiChanges = dlg.uniqueUwagiChanges + conflictUwagiChanges;
      if (totalPriceChanges === 0 && totalUwagiChanges === 0) {
        showNotification('Anulowano — żadna pozycja nie została zaktualizowana.', 'info');
        return null;
      }
      setCellOverrides(next);
      const komorki = (n: number) =>
        n === 1
          ? '1 komórkę'
          : n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14)
            ? `${n} komórki`
            : `${n} komórek`;
      const parts: string[] = [];
      if (totalPriceChanges > 0) parts.push(`Uzupełniono ${komorki(totalPriceChanges)} „Cena jedn."`);
      if (totalUwagiChanges > 0)
        parts.push(`oznaczono „Auto cena" w ${totalUwagiChanges === 1 ? '1 wierszu' : `${totalUwagiChanges} wierszach`}`);
      const skipDetails: string[] = [];
      if (dlg.skippedNonEmpty > 0) skipDetails.push(`${dlg.skippedNonEmpty} z istniejącą ceną`);
      if (dlg.skippedFormula > 0) skipDetails.push(`${dlg.skippedFormula} z formułą =`);
      if (conflictSkipped > 0) skipDetails.push(`${conflictSkipped} z grup oznaczonych „Pomiń"`);
      const skipNote = skipDetails.length > 0 ? ` Pominięto: ${skipDetails.join(', ')}.` : '';
      showNotification(`${parts.join(' i ')} z biblioteki „zweryfikowanych".${skipNote}`, 'success');
      return null;
    });
  }, [tableEntries, showNotification]);

  /* ========== „Znajdź i zamień” (drawer z prawej) ================================ */
  const [findReplaceOpen, setFindReplaceOpen] = React.useState(false);
  const [frFindText, setFrFindText] = React.useState('');
  const [frReplaceText, setFrReplaceText] = React.useState('');
  const [frColumn, setFrColumn] = React.useState<'all' | PrzedmiarFindReplaceCol>('all');
  const [frCaseSensitive, setFrCaseSensitive] = React.useState(false);
  const [frWholeCell, setFrWholeCell] = React.useState(false);
  const [frMatchIdx, setFrMatchIdx] = React.useState<number>(-1);

  const frRawCellValue = React.useCallback(
    (entryId: string, col: PrzedmiarFindReplaceCol, entry: PrzedmiarPozycjaRow): string => {
      const ov = cellOverrides[`${entryId}:${col}`];
      if (ov !== undefined) return ov;
      return String(entry[col] ?? '');
    },
    [cellOverrides]
  );

  const frCellMatches = React.useCallback(
    (raw: string): boolean => {
      if (!frFindText) return false;
      const hay = frCaseSensitive ? raw : raw.toLocaleLowerCase('pl-PL');
      const needle = frCaseSensitive ? frFindText : frFindText.toLocaleLowerCase('pl-PL');
      return frWholeCell ? hay === needle : hay.includes(needle);
    },
    [frFindText, frCaseSensitive, frWholeCell]
  );

  /** Lista dopasowań w aktualnym widoku (po filtrach/sortowaniu), z indeksami do przewinięcia. */
  const frMatches = React.useMemo(() => {
    if (!frFindText) return [] as Array<{ entryId: string; col: PrzedmiarFindReplaceCol; rowIdx: number; colIdx: number }>;
    const cols: PrzedmiarFindReplaceCol[] =
      frColumn === 'all' ? [...PRZEDMIAR_FIND_REPLACE_COLS] : [frColumn];
    const out: Array<{ entryId: string; col: PrzedmiarFindReplaceCol; rowIdx: number; colIdx: number }> = [];
    rowsView.forEach((rv, rowIdx) => {
      if (rv.kind !== 'pozycja') return;
      const roles = rolesPerRow[rowIdx];
      for (const col of cols) {
        const raw = frRawCellValue(rv.entry.id, col, rv.entry);
        if (!raw) continue;
        if (!frCellMatches(raw)) continue;
        const colIdx = roles ? roles.findIndex((r) => r.t === 'p' && r.key === col) : -1;
        out.push({ entryId: rv.entry.id, col, rowIdx, colIdx });
      }
    });
    return out;
  }, [frFindText, frColumn, rowsView, rolesPerRow, frRawCellValue, frCellMatches]);

  React.useEffect(() => {
    if (frMatches.length === 0) {
      if (frMatchIdx !== -1) setFrMatchIdx(-1);
      return;
    }
    if (frMatchIdx >= frMatches.length) setFrMatchIdx(frMatches.length - 1);
  }, [frMatches, frMatchIdx]);

  /** Gdy zmienia się aktywne dopasowanie, ustaw kursor na tę komórkę — LayoutEffect z `sheetCursor` sam przewinie widok. */
  React.useEffect(() => {
    if (!findReplaceOpen) return;
    if (frMatchIdx < 0 || frMatchIdx >= frMatches.length) return;
    const m = frMatches[frMatchIdx];
    if (m.colIdx < 0) return;
    setSheetCursor({ r: m.rowIdx, c: m.colIdx });
    setSheetSelectionAnchor({ r: m.rowIdx, c: m.colIdx });
  }, [frMatchIdx, frMatches, findReplaceOpen]);

  const frFindStep = React.useCallback(
    (dir: 1 | -1) => {
      if (frMatches.length === 0) {
        showNotification('Brak dopasowań w widocznym zakresie tabeli.', 'info');
        return;
      }
      setFrMatchIdx((i) => {
        if (i < 0) return dir === 1 ? 0 : frMatches.length - 1;
        return (i + dir + frMatches.length) % frMatches.length;
      });
    },
    [frMatches.length, showNotification]
  );

  const frFormatForCol = React.useCallback((col: PrzedmiarFindReplaceCol, v: string): string => {
    if (col === 'cenaJedn') return formatCenaJednCommitted(v);
    if (col === 'ilosc') return formatIloscCommitted(v);
    return v;
  }, []);

  const frReplaceCurrent = React.useCallback(() => {
    if (!frFindText) {
      showNotification('Podaj frazę w polu „Znajdź”.', 'info');
      return;
    }
    if (frMatches.length === 0) {
      showNotification('Brak dopasowań do zamiany.', 'info');
      return;
    }
    if (frMatchIdx < 0) {
      frFindStep(1);
      return;
    }
    const m = frMatches[frMatchIdx];
    setCellOverrides((prev) => {
      const entry = tableEntries.find((e) => e.id === m.entryId);
      if (!entry || entry.kind !== 'pozycja') return prev;
      const raw = prev[`${entry.id}:${m.col}`] ?? String(entry[m.col] ?? '');
      const replaced = frWholeCell
        ? frReplaceText
        : replaceAllInString(raw, frFindText, frReplaceText, frCaseSensitive);
      const finalValue = frFormatForCol(m.col, replaced);
      return { ...prev, [`${entry.id}:${m.col}`]: finalValue };
    });
    showNotification('Zamieniono 1 komórkę.', 'success');
  }, [
    frFindText,
    frReplaceText,
    frMatches,
    frMatchIdx,
    frWholeCell,
    frCaseSensitive,
    tableEntries,
    frFormatForCol,
    frFindStep,
    showNotification,
  ]);

  const frReplaceAll = React.useCallback(() => {
    if (!frFindText) {
      showNotification('Podaj frazę w polu „Znajdź”.', 'info');
      return;
    }
    if (frMatches.length === 0) {
      showNotification('Brak dopasowań do zamiany.', 'info');
      return;
    }
    let changes = 0;
    setCellOverrides((prev) => {
      const next = { ...prev };
      for (const m of frMatches) {
        const entry = tableEntries.find((e) => e.id === m.entryId);
        if (!entry || entry.kind !== 'pozycja') continue;
        const k = `${entry.id}:${m.col}`;
        const raw = next[k] ?? String(entry[m.col] ?? '');
        const replaced = frWholeCell
          ? frReplaceText
          : replaceAllInString(raw, frFindText, frReplaceText, frCaseSensitive);
        const finalValue = frFormatForCol(m.col, replaced);
        if (next[k] !== finalValue) {
          next[k] = finalValue;
          changes += 1;
        }
      }
      return changes > 0 ? next : prev;
    });
    if (changes === 0) {
      showNotification('Nic nie zostało zmienione (wartości już były po zamianie).', 'info');
      return;
    }
    const komorki =
      changes === 1
        ? '1 komórkę'
        : changes % 10 >= 2 && changes % 10 <= 4 && !(changes % 100 >= 12 && changes % 100 <= 14)
          ? `${changes} komórki`
          : `${changes} komórek`;
    showNotification(`Zamieniono ${komorki}.`, 'success');
  }, [
    frFindText,
    frReplaceText,
    frMatches,
    frWholeCell,
    frCaseSensitive,
    tableEntries,
    frFormatForCol,
    showNotification,
  ]);

  const toggleFindReplace = React.useCallback(() => {
    setFindReplaceOpen((v) => !v);
  }, []);
  const closeFindReplace = React.useCallback(() => {
    setFindReplaceOpen(false);
  }, []);

  const handleGoogleSheetClick = React.useCallback(async () => {
    if (googleSheetBusy) return;
    setGoogleSheetBusy(true);
    try {
      const res = await postEasyEstimatePrzedmiarGoogleSheet(estimateId);
      if (res.success && res.url) {
        window.open(res.url, '_blank', 'noopener,noreferrer');
        onEstimateSaved?.();
      } else {
        showNotification(res.message || 'Nie udało się przygotować arkusza Google.', 'error');
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      showNotification(
        ax?.response?.data?.message || ax?.message || 'Błąd podczas tworzenia arkusza Google.',
        'error'
      );
    } finally {
      setGoogleSheetBusy(false);
    }
  }, [estimateId, googleSheetBusy, onEstimateSaved, showNotification]);

  const setGemmaPdfFilesFromList = React.useCallback((list: FileList | File[]) => {
    const arr = Array.from(list).filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    if (arr.length > 0) setGemmaPdfFiles(arr.slice(0, 4));
  }, []);

  const runGemmaPdfImport = React.useCallback(async () => {
    if (gemmaPdfFiles.length === 0 || gemmaPdfBusy) return;
    const filesToSend = [...gemmaPdfFiles];
    const extraPromptToSend = gemmaPdfExtraPrompt.trim();
    setGemmaHubView('menu');
    setGemmaPdfDialogOpen(false);
    setGemmaPdfFiles([]);
    setGemmaPdfExtraPrompt('');
    setGemmaPdfStreamText('');
    setGemmaPdfBusy(true);
    try {
      const { message } = await enqueuePrzedmiarPdf(estimate.id, filesToSend, {
        extraPrompt: extraPromptToSend || null,
      });
      setGemmaPdfBusy(false);
      showNotification(message, 'success');
      window.dispatchEvent(new CustomEvent('gemma-queue-changed'));
    } catch (e) {
      setGemmaPdfBusy(false);
      showNotification((e as Error)?.message || 'Nie udało się dodać zadania do kolejki Gemmy', 'error');
    }
  }, [gemmaPdfFiles, gemmaPdfExtraPrompt, gemmaPdfBusy, estimate.id, showNotification]);

  /** Import PDF z kolejki Gemmy — praca na serwerze (worker), UI tylko polling podglądu. */
  const runGemmaPdfImportFromQueueFiles = React.useCallback(
    async (queueJobId: string) => {
      await ensureGemmaPrzedmiarJobRunning(queueJobId);
      setGemmaPdfStreamText('— Przetwarzanie na serwerze (Gemma). Fragment odpowiedzi pojawi się poniżej…\n');

      const terminal = await pollGemmaPrzedmiarJobUntilTerminal(
        queueJobId,
        (p) => {
          setGemmaPdfStreamText(p.lastGemmaOutput || '');
        },
        { intervalMs: 1500, maxWaitMs: 3 * 60 * 60 * 1000 }
      );

      if (terminal.status === 'failed') {
        setGemmaPdfStreamText(terminal.lastGemmaOutput || '');
        showNotification(terminal.errorMessage || 'Import PDF z kolejki Gemmy nie powiódł się.', 'error');
        window.dispatchEvent(new CustomEvent('gemma-queue-changed'));
        return;
      }

      setGemmaPdfStreamText('');
      setSearch('');
      setDzial('wszystkie');
      setShowDzialRows(true);
      pendingScrollTableToBottomRef.current = true;
      try {
        const data = await getEstimateEasyPrzedmiar(estimateId);
        if (data.success && data.rows.length > 0) {
          const parsed = parseEasyPrzedmiarApiRows(data.rows);
          suppressNextHistoryRef.current = true;
          setTableEntries(parsed.entries);
          setCellOverrides(parsed.cellOverrides);
          setCalcScratchBundles(parsed.calcScratchBundles);
        }
      } catch {
        /* tabela może zostać odświeżona ręcznie */
      }
      showNotification('Przedmiar uzupełniony z PDF (Gemma) — zapisano na serwerze.', 'success');
      window.dispatchEvent(new CustomEvent('gemma-queue-changed'));
    },
    [estimateId, showNotification]
  );

  const gemmaRunJobParam = searchParams.get('gemmaRunJob');
  const gemmaQueueRunDoneRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const jobId = gemmaRunJobParam;
    if (!jobId || gemmaPdfBusy) return;
    if (gemmaQueueRunDoneRef.current === jobId) return;
    gemmaQueueRunDoneRef.current = jobId;
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('gemmaRunJob');
        return n;
      },
      { replace: true }
    );

    let cancelled = false;
    (async () => {
      setGemmaPdfBusy(true);
      setGemmaPdfStreamText('');
      try {
        if (cancelled) return;
        await runGemmaPdfImportFromQueueFiles(jobId);
      } catch (e) {
        if (!cancelled) {
          showNotification((e as Error)?.message || 'Błąd importu z kolejki Gemmy', 'error');
          window.dispatchEvent(new CustomEvent('gemma-queue-changed'));
        }
      } finally {
        if (!cancelled) setGemmaPdfBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gemmaRunJobParam, setSearchParams, gemmaPdfBusy, runGemmaPdfImportFromQueueFiles, showNotification]);

  const visibleColCount = visibleKeysEffective.length;
  const tableMinWidthPx =
    SHEET_ROW_GUTTER_PX + visibleKeysEffective.reduce((s, k) => s + getWidthNum(k), 0);

  return (
    <Box
      sx={{
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        pt: 0,
        pb: 0,
        ...pageGutterSx,
      }}
    >
      {/* Cały pasek narzędzi ukrywamy dla `verifiedList + externalSearch` — pasek własny jest w stronie nadrzędnej. */}
      {isVerifiedListLayout && isExternalSearch ? null : (
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: 1,
          mb: 0.5,
          rowGap: 1,
        }}
      >
        {!isVerifiedListLayout ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="Zapisz bieżący stan jako wersję na serwerze (widoczne dla wszystkich użytkowników)">
            <span>
              <IconButton
                size="small"
                aria-label="Zapisz wersję kosztorysu jako JSON"
                disabled={versionLoadBusy}
                sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
                onClick={handleSavePrzedmiarVersionAs}
              >
                <MaterialSymbol name="save_as" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cofnij zmianę w tabeli (Ctrl+Z)">
            <span>
              <IconButton
                size="small"
                aria-label="Cofnij"
                disabled={!canUndo || versionLoadBusy}
                onClick={handleUndo}
                sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
              >
                <MaterialSymbol name="undo" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Ponów (Ctrl+Y lub Ctrl+Shift+Z)">
            <span>
              <IconButton
                size="small"
                aria-label="Ponów"
                disabled={!canRedo || versionLoadBusy}
                onClick={handleRedo}
                sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
              >
                <MaterialSymbol name="redo" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            size="small"
            aria-label="Dodaj nową pozycję do tabeli"
            title="Dodaj pozycję"
            onClick={handleAddPozycja}
            sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
          >
            <MaterialSymbol name="post_add" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Dodaj dział do tabeli"
            title="Dodaj dział"
            onClick={handleAddDzial}
            sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
          >
            <MaterialSymbol name="create_new_folder" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={showDzialRows ? 'Przełącz na widok bez działów' : 'Przełącz na widok z działami'}
            aria-pressed={showDzialRows}
            title={showDzialRows ? 'Ukryj działy (tylko wiersze pozycji)' : 'Pokaż działy (nagłówki sekcji)'}
            onClick={() => setShowDzialRows((v) => !v)}
            sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
          >
            <MaterialSymbol name={showDzialRows ? 'view_list' : 'account_tree'} />
          </IconButton>
          <IconButton
            size="small"
            title="Dostosuj kolumny"
            aria-label="Dostosuj kolumny"
            sx={{
              ...PRZEDMIAR_TOOLBAR_ICON_BTN_SX,
              bgcolor: colResizeMode ? '#E0E0F5' : 'transparent',
            }}
            onClick={openColumnsPanel}
          >
            <MaterialSymbol name="view_column" />
          </IconButton>
          <IconButton
            size="small"
            title="Zadania Gemmy — automatyzacje kosztorysu"
            aria-label="Zadania Gemmy — automatyzacje kosztorysu"
            disabled={gemmaPdfBusy}
            sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
            onClick={openGemmaPdfDialog}
          >
            <MaterialSymbol name="auto_awesome" sx={{ opacity: gemmaPdfBusy ? 0.5 : 1 }} />
          </IconButton>
          <Tooltip
            title={
              estimate.easy_przedmiar_google_sheet_id
                ? 'Otwórz Google Sheet (dane zostaną zsynchronizowane)'
                : 'Utwórz Google Sheet z szablonu w folderze oferty (4_OFERTA DO PRZETARGU)'
            }
          >
            <span>
              <IconButton
                size="small"
                aria-label="Google Sheet — kosztorys EasyEstimate"
                disabled={googleSheetBusy}
                sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
                onClick={handleGoogleSheetClick}
              >
                <MaterialSymbol name="table_view" sx={{ opacity: googleSheetBusy ? 0.5 : 1 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Otwórz źródłowy PDF (na podstawie którego Gemma przygotowała kosztorys) w nowej karcie">
            <span>
              <IconButton
                size="small"
                aria-label="Otwórz źródłowy PDF kosztorysu (Gemma) w nowej karcie"
                disabled={openSourcePdfBusy}
                sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
                onClick={handleOpenSourceGemmaPdf}
              >
                <MaterialSymbol name="picture_as_pdf" sx={{ opacity: openSourcePdfBusy ? 0.5 : 1 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Znajdź i zamień — edytuj masowo Podstawę/Opis/J.m./Ilość/Cenę jedn./Uwagi">
            <span>
              <IconButton
                size="small"
                aria-label="Znajdź i zamień"
                sx={{
                  ...PRZEDMIAR_TOOLBAR_ICON_BTN_SX,
                  bgcolor: findReplaceOpen ? '#E0E0F5' : 'transparent',
                }}
                onClick={toggleFindReplace}
              >
                <MaterialSymbol name="find_replace" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Powiąż duplikaty: w powtarzających się pozycjach (Podstawa + Opis) wstaw =F{nr} do pierwszego wystąpienia „Cena jedn.”">
            <span>
              <IconButton
                size="small"
                aria-label="Automatycznie powiąż duplikaty — formuły w „Cena jedn.”"
                sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
                onClick={handleAutoFillDuplicateCenaJedn}
              >
                <MaterialSymbol name="auto_fix_high" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Wstaw „Cena jedn.” z biblioteki pozycji „zweryfikowanych” — dopasowanie po „Podstawa” (KNR) tolerujące różne zapisy (np. „KNR-W ↔ KNR …W”, suffiksy „analogia”, „z.sz.…”).">
            <span>
              <IconButton
                size="small"
                aria-label="Wstaw Cena jedn. z biblioteki pozycji zweryfikowanych"
                disabled={verifiedFillBusy}
                sx={PRZEDMIAR_TOOLBAR_ICON_BTN_SX}
                onClick={handleAutoFillCenaJednFromVerified}
              >
                <MaterialSymbol name="price_check" sx={{ opacity: verifiedFillBusy ? 0.5 : 1 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            ml: { xs: 0, sm: isVerifiedListLayout ? 0 : 'auto' },
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            flex: isVerifiedListLayout ? 1 : undefined,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: { xs: '100%', sm: 'auto' },
              minWidth: 0,
              flex: isVerifiedListLayout ? 1 : undefined,
            }}
          >
            {!isVerifiedListLayout ? (
              <EasyEstimateKosztorysPresenceAvatars
                members={kosztorysOthersPresent}
                followedUserId={sheetFollowRemoteUserId}
                onMemberClick={beginFollowRemoteSheetUser}
              />
            ) : null}
            {!isExternalSearch && (
              <Box sx={{ flex: { xs: '1 1 auto', sm: isVerifiedListLayout ? '1 1 100%' : '0 0 280px' }, width: { xs: '100%', sm: isVerifiedListLayout ? '100%' : 280 }, minWidth: { xs: 0, sm: isVerifiedListLayout ? 0 : 260 } }}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Szukaj"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={filterTextFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#595966', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </Box>
          {!isVerifiedListLayout ? (
          <>
          <Box sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { xs: 0, sm: 260 } }}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              disabled={versionLoadBusy}
              onClick={(e) => setVersionMenuAnchor(e.currentTarget)}
              aria-haspopup="listbox"
              aria-expanded={Boolean(versionMenuAnchor)}
              aria-label="Wersje przedmiaru"
              endIcon={
                <Box
                  component="img"
                  alt=""
                  src="/assets/filters/keyboard_arrow_down.png"
                  sx={{ width: 18, height: 18, pointerEvents: 'none' }}
                />
              }
              sx={filterVersionMenuButtonSx}
            >
              Wersja: Bieżąca
            </Button>
            <Menu
              anchorEl={versionMenuAnchor}
              open={Boolean(versionMenuAnchor)}
              onClose={() => setVersionMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  minWidth: versionMenuAnchor ? Math.max(versionMenuAnchor.clientWidth, 280) : 280,
                  maxWidth: 480,
                },
              }}
            >
              <MenuItem
                onClick={() => setVersionMenuAnchor(null)}
                sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}
              >
                Bieżąca
              </MenuItem>
              {przedmiarServerVersions.map((v) => (
                <MenuItem
                  key={v.id}
                  disableRipple
                  sx={{ py: 1, px: 1.5, display: 'block', bgcolor: 'transparent' }}
                  onClick={(e) => e.preventDefault()}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                      flexWrap: 'nowrap',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        color: '#2D2D32',
                      }}
                    >
                      {v.label}
                    </Typography>
                    <Chip
                      label="Wczytaj"
                      size="small"
                      variant="outlined"
                      disabled={versionLoadBusy}
                      aria-label={`Wczytaj wersję ${v.label}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setVersionMenuAnchor(null);
                        void handleWczytajVersionById(v.id);
                      }}
                      sx={{
                        height: 28,
                        fontWeight: 600,
                        fontSize: '12px',
                        flexShrink: 0,
                        borderColor: '#d0d0d6',
                        color: '#202124',
                        '&:hover': versionLoadBusy
                          ? {}
                          : { borderColor: '#2020AF', bgcolor: 'rgba(32, 32, 175, 0.06)' },
                      }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Box sx={{ width: { xs: '100%', sm: 280 }, minWidth: { xs: 0, sm: 260 } }}>
            <FormControl fullWidth size="small" sx={filterSelectSx}>
              <Select
                value={dzial}
                onChange={(e) => setDzial(String(e.target.value))}
                inputProps={{ 'aria-label': 'Filtr działu' }}
                MenuProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  PaperProps: { sx: dzialFilterMenuListPaperSx },
                }}
                renderValue={(selected) => {
                  const s = String(selected);
                  if (s === 'wszystkie') return 'Wszystkie';
                  const lp = dzialLabelToSortKey.get(s);
                  const shortName = truncateDzialFilterName(s);
                  const line = lp ? `${lp} · ${shortName}` : shortName;
                  if (s.length <= DZIAL_FILTER_NAME_MAX_CHARS) return line;
                  return (
                    <Tooltip title={s} placement="top" enterDelay={400}>
                      <span
                        style={{
                          display: 'inline-block',
                          verticalAlign: 'bottom',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {line}
                      </span>
                    </Tooltip>
                  );
                }}
                IconComponent={(iconProps: Record<string, unknown>) => (
                  <Box
                    component="img"
                    alt=""
                    src="/assets/filters/keyboard_arrow_down.png"
                    {...iconProps}
                    sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                  />
                )}
              >
                <MenuItem value="wszystkie" sx={{ bgcolor: '#fff' }}>
                  Wszystkie
                </MenuItem>
                {dzialOptionsForSelect.map((d) => {
                  const lp = dzialLabelToSortKey.get(d);
                  const kw = dzialLabelToWartoscNettoFormatted.get(d) ?? formatKwotaPl2(0);
                  const viz = dzialFilterMenuItemVizByLabel.get(d) ?? dzialSublevelNeutralVisual();
                  return (
                    <MenuItem
                      key={d}
                      value={d}
                      sx={{
                        bgcolor: viz.bg,
                        color: viz.textPrimary,
                        py: 0.5,
                        '&.Mui-selected': {
                          bgcolor: viz.bg,
                          color: viz.textPrimary,
                          boxShadow: `inset 3px 0 0 ${viz.selectionStroke}`,
                        },
                        '&.Mui-selected:hover': { bgcolor: viz.bg, color: viz.textPrimary },
                        '&:hover': { bgcolor: viz.bg, color: viz.textPrimary, filter: 'brightness(0.97)' },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                          width: '100%',
                          minWidth: 0,
                          py: 0.25,
                          fontSize: '12px',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: viz.textPrimary,
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            flexShrink: 0,
                            minWidth: '1.5em',
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '12px',
                            lineHeight: 1.35,
                          }}
                        >
                          {lp ?? '—'}
                        </Box>
                        <Tooltip
                          title={d}
                          placement="left"
                          enterDelay={400}
                          disableHoverListener={d.length <= DZIAL_FILTER_NAME_MAX_CHARS}
                        >
                          <Box
                            component="span"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontFamily: 'Roboto, sans-serif',
                              fontSize: '12px',
                              fontWeight: 700,
                              color: viz.textPrimary,
                              lineHeight: 1.35,
                            }}
                          >
                            {truncateDzialFilterName(d)}
                          </Box>
                        </Tooltip>
                        <Box
                          component="span"
                          sx={{
                            flexShrink: 0,
                            marginLeft: 'auto',
                            pl: 1,
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '12px',
                            fontWeight: 700,
                            color: viz.textPrimary,
                            fontVariantNumeric: 'tabular-nums',
                            lineHeight: 1.35,
                            textAlign: 'right',
                          }}
                        >
                          {kw}
                        </Box>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
          </>
          ) : null}
        </Box>
      </Box>
      )}

      <Dialog open={gemmaPdfDialogOpen} onClose={closeGemmaPdfDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 500,
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pr: 2,
          }}
        >
          {gemmaHubView !== 'menu' ? (
            <IconButton
              aria-label="Wróć do listy zadań"
              size="small"
              onClick={goGemmaHubBack}
              disabled={gemmaPdfBusy}
              sx={{ mr: 0.5, color: '#020242' }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          ) : null}
          <span>
            {gemmaHubView === 'menu'
              ? 'Zadania Gemmy'
              : gemmaHubView === 'przedmiar_pdf'
                ? 'Dodaj przedmiar z PDF'
                : gemmaHubView === 'ceny_zatwierdzone'
                  ? 'Uzupełnij ceny z pozycji zatwierdzonych'
                  : 'Przygotuj pakiety prac'}
          </span>
        </DialogTitle>
        <DialogContent>
          {gemmaHubView === 'menu' ? (
            <>
              <Typography variant="body2" sx={{ color: '#5F6368', mb: 2, fontFamily: 'Roboto, sans-serif' }}>
                Wybierz zadanie. Kolejne opcje dodamy w przyszłości.
              </Typography>
              <List sx={{ py: 0 }}>
                <ListItem disablePadding sx={{ mb: 1.5 }}>
                  <ListItemButton
                    onClick={() => setGemmaHubView('przedmiar_pdf')}
                    sx={{
                      borderRadius: '8px',
                      border: '1px solid #e8eaed',
                      py: 1.25,
                      '&:hover': {
                        borderColor: '#3F3FDC',
                        bgcolor: 'rgba(63, 63, 220, 0.06)',
                      },
                    }}
                  >
                    <ListItemText
                      primary="1. Dodaj przedmiar"
                      secondary="Import pozycji z pliku PDF (kolejka Gemmy)"
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '15px', color: '#020242' }}
                      secondaryTypographyProps={{ fontSize: '13px', color: '#5F6368', mt: 0.5 }}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1.5 }}>
                  <ListItemButton
                    onClick={() => setGemmaHubView('ceny_zatwierdzone')}
                    sx={{
                      borderRadius: '8px',
                      border: '1px solid #e8eaed',
                      py: 1.25,
                      '&:hover': {
                        borderColor: '#3F3FDC',
                        bgcolor: 'rgba(63, 63, 220, 0.06)',
                      },
                    }}
                  >
                    <ListItemText
                      primary="2. Uzupełnij ceny z pozycji zatwierdzonych"
                      secondary="Automatyczne uzupełnianie cen (w przygotowaniu)"
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '15px', color: '#020242' }}
                      secondaryTypographyProps={{ fontSize: '13px', color: '#5F6368', mt: 0.5 }}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => setGemmaHubView('pakiety_prac')}
                    sx={{
                      borderRadius: '8px',
                      border: '1px solid #e8eaed',
                      py: 1.25,
                      '&:hover': {
                        borderColor: '#3F3FDC',
                        bgcolor: 'rgba(63, 63, 220, 0.06)',
                      },
                    }}
                  >
                    <ListItemText
                      primary="3. Przygotuj pakiety prac"
                      secondary="Automatyzacja pakietów robót (w przygotowaniu)"
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '15px', color: '#020242' }}
                      secondaryTypographyProps={{ fontSize: '13px', color: '#5F6368', mt: 0.5 }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </>
          ) : null}

          {gemmaHubView === 'przedmiar_pdf' ? (
            <>
              <Typography variant="body2" sx={{ color: '#5F6368', mb: 2, fontFamily: 'Roboto, sans-serif' }}>
                Wgraj PDF od Zamawiającego. Gemma używa{' '}
                <Box component="span" sx={{ fontWeight: 600, color: '#3F3FDC' }}>
                  osobnego promptu
                </Box>{' '}
                (nie tego z czatu wewnętrznego): wyciąga Lp., Podstawę, Opis, j.m., Ilość oraz działy i zwraca JSON
                dopasowany do tabeli przedmiaru.{' '}
                <Box component="span" sx={{ fontWeight: 600 }}>Czasem coś w pliku trzeba doprecyzować</Box> — w razie
                potrzeby wpisz dodatkowe wskazówki w polu poniżej (np. „pomiń sekcję X”, „weź tylko Lp. od 20 do 45”,
                „przepisz Lp. dosłownie razem z literami”). Jeśli coś tam dodasz, Gemma{' '}
                <Box component="span" sx={{ fontWeight: 600 }}>uwzględni to w prompcie</Box> jako uzupełnienie tej
                instrukcji. Po „Wyślij” zadanie trafia do{' '}
                <Box component="span" sx={{ fontWeight: 600 }}>kolejki Zadań Gemmy</Box> — domyślnie pierwsze zadanie ma plan
                startu tego samego dnia o 23:15; kolejne realizowane są jedno po drugim. Uruchomienie od razu: przycisk
                odtwarzania w panelu kolejki (przejście do tego kosztorysu i import).
              </Typography>
              <input
                type="file"
                accept=".pdf,application/pdf"
                multiple
                hidden
                ref={gemmaPdfInputRef}
                onChange={(e) => {
                  setGemmaPdfFilesFromList(e.target.files || []);
                  e.target.value = '';
                }}
              />
              <Box
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!gemmaPdfBusy) gemmaPdfInputRef.current?.click();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setGemmaPdfFilesFromList(e.dataTransfer.files);
                }}
                onClick={() => {
                  if (!gemmaPdfBusy) gemmaPdfInputRef.current?.click();
                }}
                sx={{
                  border: '2px dashed #dadce0',
                  borderRadius: '8px',
                  py: 3,
                  px: 2,
                  textAlign: 'center',
                  cursor: gemmaPdfBusy ? 'default' : 'pointer',
                  mb: 2,
                  bgcolor: 'rgba(0,0,0,0.02)',
                  '&:hover': { bgcolor: gemmaPdfBusy ? 'rgba(0,0,0,0.02)' : 'rgba(63, 63, 220, 0.06)' },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 40, color: '#5F6368', mb: 1 }} />
                <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: '#2D2D32' }}>
                  Upuść plik PDF tutaj lub kliknij, aby wybrać
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#5F6368' }}>
                  Maksymalnie 4 pliki; tylko PDF (tekstowa warstwa — skany bez OCR mogą być puste)
                </Typography>
                {gemmaPdfFiles.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#202124', fontWeight: 500 }}>
                    {gemmaPdfFiles.map((f) => f.name).join(', ')}
                  </Typography>
                )}
              </Box>
              <TextField
                label="Dodatkowe wskazówki dla Gemmy (opcjonalnie)"
                placeholder="Np.: pomiń dział „Roboty dodatkowe”, weź tylko Lp. 1–45, Lp. przepisz dosłownie z literami (1.d.1.1)."
                value={gemmaPdfExtraPrompt}
                onChange={(e) => {
                  const v = e.target.value;
                  setGemmaPdfExtraPrompt(v.length > GEMMA_PDF_EXTRA_PROMPT_MAX ? v.slice(0, GEMMA_PDF_EXTRA_PROMPT_MAX) : v);
                }}
                disabled={gemmaPdfBusy}
                multiline
                minRows={3}
                maxRows={8}
                fullWidth
                size="small"
                helperText={`${gemmaPdfExtraPrompt.length}/${GEMMA_PDF_EXTRA_PROMPT_MAX} znaków — tekst zostanie doklejony do promptu Gemmy jako uzupełnienie stałej instrukcji.`}
                sx={{ fontFamily: 'Roboto, sans-serif' }}
                inputProps={{ maxLength: GEMMA_PDF_EXTRA_PROMPT_MAX }}
              />
            </>
          ) : null}

          {gemmaHubView === 'ceny_zatwierdzone' ? (
            <Typography variant="body2" sx={{ color: '#5F6368', fontFamily: 'Roboto, sans-serif', lineHeight: 1.5 }}>
              Ta funkcja jest w przygotowaniu. Wróć do listy zadań strzałką lub zamknij okno.
            </Typography>
          ) : null}

          {gemmaHubView === 'pakiety_prac' ? (
            <Typography variant="body2" sx={{ color: '#5F6368', fontFamily: 'Roboto, sans-serif', lineHeight: 1.5 }}>
              Przygotowanie pakietów prac — funkcja w przygotowaniu. Wróć do listy zadań strzałką lub zamknij okno.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {gemmaHubView === 'menu' ? (
            <Button onClick={closeGemmaPdfDialog} sx={{ textTransform: 'none' }}>
              Zamknij
            </Button>
          ) : gemmaHubView === 'przedmiar_pdf' ? (
            <>
              <Button onClick={closeGemmaPdfDialog} disabled={gemmaPdfBusy} sx={{ textTransform: 'none' }}>
                Anuluj
              </Button>
              <Button
                variant="contained"
                onClick={runGemmaPdfImport}
                disabled={gemmaPdfBusy || gemmaPdfFiles.length === 0}
                sx={{ textTransform: 'none', bgcolor: '#2020AF', '&:hover': { bgcolor: '#18188c' } }}
              >
                Wyślij do Gemmy
              </Button>
            </>
          ) : (
            <Button onClick={closeGemmaPdfDialog} sx={{ textTransform: 'none' }}>
              Zamknij
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {!isVerifiedListLayout && (gemmaPdfBusy || gemmaPdfStreamText.length > 0) && (
        <Box
          sx={{
            flexShrink: 0,
            mb: 1,
            border: '1px solid #dadce0',
            borderRadius: '4px',
            bgcolor: gemmaPdfBusy ? '#e8f0fe' : '#fff8e1',
            overflow: 'hidden',
          }}
        >
          {gemmaPdfBusy && <LinearProgress />}
          <Box sx={{ px: 1.5, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 500,
                  color: gemmaPdfBusy ? '#174ea6' : '#b06000',
                }}
              >
                {gemmaPdfBusy
                  ? 'Gemma przetwarza PDF — odpowiedź na żywo; po zakończeniu wiersze dopiszą się do tabeli poniżej.'
                  : 'Ostatnia odpowiedź Gemmy (niepoprawny JSON lub do weryfikacji).'}
              </Typography>
              {!gemmaPdfBusy && (
                <Button size="small" onClick={() => setGemmaPdfStreamText('')} sx={{ textTransform: 'none', flexShrink: 0, minWidth: 0 }}>
                  Zamknij podgląd
                </Button>
              )}
            </Box>
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                maxHeight: 120,
                overflow: 'auto',
                fontFamily: 'Roboto Mono, monospace',
                fontSize: '11px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                bgcolor: '#fff',
                borderColor: '#dadce0',
              }}
            >
              {gemmaPdfStreamText || 'Oczekiwanie na pierwszy fragment odpowiedzi…'}
            </Paper>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          gap: 0,
          minWidth: 0,
          border: '1px solid #dadce0',
          borderRadius: '4px',
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
      <TableContainer
        ref={tableScrollContainerRef}
        sx={{
          flex: 1,
          minWidth: 0,
          width: '100%',
          minHeight: 0,
          border: 'none',
          borderRadius: 0,
          bgcolor: '#fff',
          overflow: 'auto',
        }}
      >
        <Table
          stickyHeader
          size="small"
          role="grid"
          aria-label="Tabela przedmiaru — strzałki i zaznaczenie zakresu z klawiszem Shift (jak w arkuszu)"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            minWidth: Math.max(400, tableMinWidthPx),
            '& thead .MuiTableCell-head': {
              position: 'sticky',
              backgroundClip: 'padding-box',
            },
            '& tbody .MuiTableCell-root': { borderRadius: 0 },
          }}
        >
          <colgroup>
            <col style={{ width: SHEET_ROW_GUTTER_PX }} />
            {visibleKeysEffective.map((k) => (
              <col key={k} style={{ width: getColW(k) }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell component="th" scope="col" sx={sheetGutterCornerSx} aria-sort={kosztorysRowSort === 'default' ? 'none' : 'other'}>
                <Tooltip
                  title={
                    kosztorysRowSort === 'default'
                      ? 'Kolejność dokumentu. Numery przy wierszach (Nr) = ta kolejność.'
                      : 'Przywróć kolejność dokumentu — wiersze ułożą się jak numery w kolumnie Nr (1, 2, 3…).'
                  }
                >
                  <TableSortLabel
                    active={kosztorysRowSort === 'default'}
                    direction="asc"
                    hideSortIcon
                    sx={{
                      width: '100%',
                      justifyContent: 'center',
                      m: 0,
                      '& .MuiTableSortLabel-icon': { display: 'none' },
                    }}
                    aria-label="Kolejność dokumentu"
                    onClick={(ev) => {
                      ev.preventDefault();
                      setKosztorysRowSort('default');
                    }}
                  >
                    <Box component="span" sx={{ display: 'inline-block', width: 8, height: 1 }} />
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              {visibleKeysEffective.map((keyId, idx) => (
                <TableCell
                  key={`col-letter-${keyId}`}
                  component="th"
                  scope="col"
                  align="center"
                  sx={{
                    ...sheetLettersHeaderCellSx,
                    width: getColW(keyId),
                    borderRight:
                      idx === visibleKeysEffective.length - 1 ? 'none' : sheetLettersHeaderCellSx.borderRight,
                  }}
                >
                  {sheetColumnLetter(idx)}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell
                component="th"
                scope="col"
                sx={sheetGutterHeaderRowNumSx}
                aria-sort={kosztorysRowSort === 'default' ? 'none' : 'other'}
              >
                <Tooltip
                  title={
                    kosztorysRowSort === 'default'
                      ? 'Numer wiersza w dokumencie (nie zmienia się przy sortowaniu po wartości netto). Kliknij po sortowaniu po necie, aby wrócić do tej kolejności.'
                      : 'Przywróć kolejność dokumentu — wiersze ułożą się rosnąco wg tej kolumny Nr.'
                  }
                >
                  <TableSortLabel
                    active={kosztorysRowSort === 'default'}
                    direction="asc"
                    hideSortIcon
                    sx={{
                      width: '100%',
                      justifyContent: 'center',
                      m: 0,
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#80868b',
                      '& .MuiTableSortLabel-icon': { display: 'none' },
                    }}
                    aria-label="Nr — kolejność dokumentu"
                    onClick={(ev) => {
                      ev.preventDefault();
                      setKosztorysRowSort('default');
                    }}
                  >
                    Nr
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              {visibleKeysEffective.map((keyId, idx) => {
                const ord = visibleKeysEffective;
                const rightKey = idx < ord.length - 1 ? ord[idx + 1] : undefined;
                const isLast = idx === ord.length - 1;
                const alignRight =
                  keyId === 'lp' || keyId === 'ilosc' || keyId === 'cenaJedn' || keyId === 'wartoscNetto';
                return (
                  <TableCell
                    key={keyId}
                    align={keyId === 'actions' ? 'center' : alignRight ? 'right' : 'left'}
                    sx={{
                      ...sheetHeaderCellSx,
                      width: getColW(keyId),
                      maxWidth: 'none',
                      userSelect: 'none',
                      borderRight: isLast ? 'none' : sheetHeaderCellSx.borderRight,
                      py: 1,
                      px: 1,
                      top: SHEET_LETTERS_HEADER_ROW_PX,
                      zIndex: 5,
                      boxShadow: '0 1px 0 #dadce0',
                    }}
                  >
                    {keyId === 'actions' ? null : keyId === 'wartoscNetto' ? (
                      <TableSortLabel
                        active={kosztorysRowSort !== 'default'}
                        direction={kosztorysRowSort === 'nettoAsc' ? 'asc' : 'desc'}
                        sx={{
                          width: '100%',
                          justifyContent: 'flex-end',
                          whiteSpace: 'nowrap',
                        }}
                        onClick={(ev) => {
                          ev.preventDefault();
                          setKosztorysRowSort((prev) => {
                            if (prev === 'default' || prev === 'nettoAsc') return 'nettoDesc';
                            return 'nettoAsc';
                          });
                        }}
                      >
                        {PRZEDMIAR_COL_LABELS.wartoscNetto}
                      </TableSortLabel>
                    ) : (
                      PRZEDMIAR_COL_LABELS[keyId]
                    )}
                    {colResizeMode ? (
                      <Box
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragCol({
                            key: keyId,
                            startX: e.clientX,
                            startW: getWidthNum(keyId),
                            rightKey,
                            startWRight: rightKey ? getWidthNum(rightKey) : undefined,
                          });
                        }}
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          width: '12px',
                          height: '100%',
                          cursor: 'col-resize',
                          zIndex: 6,
                          touchAction: 'none',
                          backgroundImage:
                            'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
                        }}
                      />
                    ) : null}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={Math.max(visibleColCount + 1, 1)}
                  sx={{ ...sheetBodyCellSx, borderRight: 'none', py: 4, textAlign: 'center', color: '#5F6368' }}
                >
                  Brak pozycji przy wybranych filtrach.
                </TableCell>
              </TableRow>
            ) : (
              rowsView.map((rv, index) => {
                const roles = rolesPerRow[index];
                if (!roles) return null;
                const gutterDocumentNr = documentGutterNrByEntryId.get(rv.entry.id) ?? index + 1;
                const selSig = rowSelectionSigs[index] ?? '';
                if (rv.kind === 'dzial') {
                  const curSig = sheetCursor?.r === index ? String(sheetCursor.c) : 'x';
                  const rowRenderStamp = `${index}\u001f${selSig}\u001f${curSig}\u001f${przedmiarSheetEditRowSig(rv.entry.id, editingCell, editDraft)}\u001f${colWidthsSig}`;
                  return (
                    <PrzedmiarSheetDzialBodyRowMemo
                      key={rv.entry.id}
                      rowRenderStamp={rowRenderStamp}
                      rv={rv}
                      index={index}
                      gutterDocumentNr={gutterDocumentNr}
                      roles={roles}
                      tableEntriesFull={tableEntries}
                      rowsView={rowsView}
                      visibleKeysEffective={visibleKeysEffective}
                      sheetMaxColByRow={sheetMaxColByRow}
                      sheetCursor={sheetCursor}
                      sheetSelBounds={sheetSelBounds}
                      cellOverrides={cellOverrides}
                      resolvedCenaJednByEntryId={resolvedCenaJednByEntryId}
                      eeRemoteSheetCells={eeRemoteSheetCells}
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={onEditDraftChange}
                      getColW={getColW}
                      getWidthNum={getWidthNum}
                      setSheetCellRef={setSheetCellRef}
                      editInputRef={editInputRef}
                      handleSheetCellKeyDown={handleSheetCellKeyDown}
                      applySheetCellPointer={applySheetCellPointer}
                      handleSheetCellPointerDown={handleSheetCellPointerDown}
                      handleSheetCellFocus={handleSheetCellFocus}
                      handleEditInputKeyDown={handleEditInputKeyDown}
                      handleEditBlur={handleEditBlur}
                      handleMenuOpen={handleMenuOpen}
                      onGutterInsertBelow={insertPozycjaBelowViewRow}
                      onGutterDeleteRow={deletePozycjaViewRow}
                      maxDzialHierarchyDepth={maxDzialHierarchyDepth}
                      hideGutterRowActions={isVerifiedListLayout}
                    />
                  );
                }
                const calcRowSig =
                  calcPanelEntryId != null && calcPanelEntryId === rv.entry.id ? '1' : '0';
                const curSig = sheetCursor?.r === index ? String(sheetCursor.c) : 'x';
                const rowRenderStamp = `${index}\u001f${selSig}\u001f${curSig}\u001f${przedmiarSheetEditRowSig(rv.entry.id, editingCell, editDraft)}\u001f${calcRowSig}\u001f${colWidthsSig}`;
                return (
                  <PrzedmiarSheetPozycjaBodyRowMemo
                    key={rv.entry.id}
                    rowRenderStamp={rowRenderStamp}
                    rv={rv}
                    index={index}
                    gutterDocumentNr={gutterDocumentNr}
                    stripeBg={STRIPE_BG[index % 2]}
                    roles={roles}
                    rowsView={rowsView}
                    visibleKeysEffective={visibleKeysEffective}
                    sheetMaxColByRow={sheetMaxColByRow}
                    sheetCursor={sheetCursor}
                    sheetSelBounds={sheetSelBounds}
                    cellOverrides={cellOverrides}
                    resolvedCenaJednByEntryId={resolvedCenaJednByEntryId}
                    eeRemoteSheetCells={eeRemoteSheetCells}
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={onEditDraftChange}
                    calcPanelEntryId={calcPanelEntryId}
                    getColW={getColW}
                    setSheetCellRef={setSheetCellRef}
                    editInputRef={editInputRef}
                    handleSheetCellKeyDown={handleSheetCellKeyDown}
                    applySheetCellPointer={applySheetCellPointer}
                    handleSheetCellPointerDown={handleSheetCellPointerDown}
                    handleSheetCellFocus={handleSheetCellFocus}
                    handleEditInputKeyDown={handleEditInputKeyDown}
                    handleEditBlur={handleEditBlur}
                    handleMenuOpen={handleMenuOpen}
                    openCalcPanelForPozycja={openCalcPanelForPozycja}
                    onGutterInsertBelow={insertPozycjaBelowViewRow}
                    onGutterDeleteRow={deletePozycjaViewRow}
                    hideGutterRowActions={isVerifiedListLayout}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {calcPanelEntryId && calcPanelPozycja && calcScratchBundles[calcPanelEntryId] && (
        <PozycjaCalcScratchpad
          lpLabel={String(calcPanelPozycja.lp)}
          podstawaLine={
            cellOverrides[`${calcPanelEntryId}:podstawa`] ?? calcPanelPozycja.podstawa
          }
          roboczogodzinaZl={roboczogodzinaZl}
          scratchSheets={calcScratchBundles[calcPanelEntryId]}
          onScratchSheetsChange={(next) => {
            const id = calcPanelEntryId;
            setCalcScratchBundles((g) => ({ ...g, [id]: next }));
            /* Pozycje z biblioteki ATH mają id z prefiksem `lib:` — parent persystuje zmiany po API. */
            if (id.startsWith('lib:') && onExternalScratchBundleChange) {
              onExternalScratchBundleChange(id, next);
            }
          }}
          onClose={() => setCalcPanelEntryId(null)}
          onApplyCenaJedn={(formatted) => {
            const id = calcPanelEntryId;
            if (!id) return;
            setCellOverrides((o) => ({
              ...o,
              [`${id}:cenaJedn`]: formatted,
              [`${id}:wartoscKalkZ`]: 'cena',
            }));
            if (id.startsWith('lib:') && onExternalCenaJednChange) {
              onExternalCenaJednChange(id, formatted);
            }
          }}
        />
      )}
      </Box>

      <Backdrop
        open={colsOpen}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(0,0,0,0.35)',
          pointerEvents: 'none',
        }}
        onClick={() => {
          setColsOpen(false);
          setColResizeMode(false);
          setDraftColumnsVisible(null);
          setDraftOrder(null);
          setDraftWidths({});
        }}
      >
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: `${PRZEDMIAR_COLUMNS_DIALOG.height}px`,
            width: `${PRZEDMIAR_COLUMNS_DIALOG.width}px`,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            top: colsPosition.top ? `${colsPosition.top}px` : undefined,
            left: colsPosition.left ? `${colsPosition.left}px` : undefined,
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flex: '0 0 auto',
              position: 'sticky',
              top: 0,
              bgcolor: '#FFFFFF',
              zIndex: 1,
              cursor: 'move',
              userSelect: 'none',
            }}
            onMouseDown={(e) =>
              setColsDragging({
                startX: e.clientX,
                startY: e.clientY,
                top: colsPosition.top,
                left: colsPosition.left,
              })
            }
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>
              Dostosuj kolumny
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setColsOpen(false);
                setColResizeMode(false);
                setDraftColumnsVisible(null);
                setDraftOrder(null);
                setDraftWidths({});
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(draftOrder ?? columnOrder).map((key) => (
              <Box
                key={key}
                sx={{ py: 1, px: 2 }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', key);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const src = e.dataTransfer.getData('text/plain') as PrzedmiarColKey;
                  if (!src || src === key || !effectiveAllCols.includes(src)) return;
                  setDraftOrder((prev) => {
                    const cur = [...(prev ?? columnOrder)];
                    const from = cur.indexOf(src);
                    const to = cur.indexOf(key);
                    if (from < 0 || to < 0) return cur;
                    const [removed] = cur.splice(from, 1);
                    cur.splice(to, 0, removed);
                    return cur;
                  });
                }}
              >
                <Box
                  sx={{
                    border: '1px solid #d4d4d9',
                    borderRadius: '4px',
                    height: 36,
                    width: 257,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.25,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <IconButton
                      onClick={() =>
                        setDraftColumnsVisible((prev) => {
                          const merged = { ...columnsVisible, ...prev };
                          const isVisible = merged[key] !== false;
                          return { ...merged, [key]: isVisible ? false : true };
                        })
                      }
                      size="small"
                      sx={{ p: 0 }}
                      aria-label="Przełącz widoczność kolumny"
                    >
                      <Box
                        component="img"
                        src={
                          { ...columnsVisible, ...(draftColumnsVisible || {}) }[key] !== false
                            ? '/assets/icons/checkbox-on.svg'
                            : '/assets/icons/checkbox-off.svg'
                        }
                        alt="check"
                        sx={{ width: 23, height: 24 }}
                      />
                    </IconButton>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                      {PRZEDMIAR_COL_LABELS[key]}
                    </Typography>
                  </Box>
                  <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                </Box>
              </Box>
            ))}
          </Box>
          <Divider />
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '24px',
              flex: '0 0 auto',
              boxShadow: '0px 4px 4px rgba(0,0,0,0.05)',
              position: 'sticky',
              bottom: 0,
              bgcolor: '#FFFFFF',
              zIndex: 1,
            }}
          >
            <Button
              variant="text"
              onClick={() => {
                setColsOpen(false);
                setColResizeMode(false);
                setDraftColumnsVisible(null);
                setDraftOrder(null);
                setDraftWidths({});
              }}
              sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}
            >
              Anuluj
            </Button>
            <Button
              variant="contained"
              sx={{
                width: 123,
                height: 40,
                borderRadius: '4px',
                textTransform: 'none',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 600,
                fontSize: 14,
                backgroundColor: '#020242',
                '&:hover': { backgroundColor: '#020242' },
              }}
              onClick={() => {
                if (draftColumnsVisible) {
                  setColumnsVisible(draftColumnsVisible);
                  try {
                    localStorage.setItem(effectiveVisibleStorageKey, JSON.stringify(draftColumnsVisible));
                  } catch {
                    /* ignore */
                  }
                }
                if (draftOrder) {
                  const normalized = draftOrder
                    .filter((k) => effectiveAllCols.includes(k))
                    .concat(effectiveAllCols.filter((k) => !draftOrder.includes(k)));
                  setColumnOrder(normalized);
                  try {
                    localStorage.setItem(effectiveOrderStorageKey, JSON.stringify(normalized));
                  } catch {
                    /* ignore */
                  }
                }
                const widthsToSave = { ...columnWidths };
                const finalOrder = draftOrder ?? columnOrder;
                finalOrder.forEach((k) => {
                  const draftW = draftWidths[k];
                  if (draftW && typeof draftW === 'number' && draftW >= 24) {
                    widthsToSave[k] = draftW;
                  }
                });
                setColumnWidths(widthsToSave);
                try {
                  localStorage.setItem(PRZEDMIAR_STORAGE_WIDTHS, JSON.stringify(widthsToSave));
                } catch {
                  /* ignore */
                }
                setColsOpen(false);
                setColResizeMode(false);
                setDraftColumnsVisible(null);
                setDraftOrder(null);
                setDraftWidths({});
              }}
            >
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {menuTargetEntry?.kind === 'dzial' ? (
          <MenuItem
            onClick={() => {
              handleOpenWbsDialogFromMenu();
            }}
            sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14 }}
          >
            Numeracja WBS (rozdział)…
          </MenuItem>
        ) : null}
        {menuTargetEntry?.kind === 'pozycja' && onMenuEditBranza && menuTargetEntry.id.startsWith('lib:') ? (
          <MenuItem
            onClick={() => {
              const id = menuTargetEntry.id;
              handleMenuClose();
              onMenuEditBranza(id);
            }}
            sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14 }}
          >
            Edytuj branżę…
          </MenuItem>
        ) : null}
        {menuTargetEntry?.kind === 'pozycja' ? (
          <MenuItem
            disabled={Boolean(menuTargetEntry.approvedAt)}
            onClick={() => {
              handleApprovePozycjaFromMenu();
            }}
            sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14 }}
          >
            Zatwierdź
          </MenuItem>
        ) : null}
        {menuTargetEntry?.kind === 'pozycja' ? (
          <MenuItem
            disabled={!menuTargetEntry.approvedAt}
            onClick={() => {
              handleRevokePozycjaApprovalFromMenu();
            }}
            sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14 }}
          >
            Cofnij zatwierdzenie
          </MenuItem>
        ) : null}
        <MenuItem disabled onClick={handleMenuClose}>
          Edytuj (w przygotowaniu)
        </MenuItem>
        <MenuItem disabled onClick={handleMenuClose}>
          Duplikuj (w przygotowaniu)
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDeleteMenuRow();
          }}
          sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#c53030' }}
        >
          Usuń
        </MenuItem>
        <MenuItem disabled onClick={handleMenuClose}>
          Przenieś do działu… (w przygotowaniu)
        </MenuItem>
        <MenuItem disabled onClick={handleMenuClose}>
          Wstaw wiersz powyżej (w przygotowaniu)
        </MenuItem>
        <MenuItem disabled onClick={handleMenuClose}>
          Kopiuj numer Lp. (w przygotowaniu)
        </MenuItem>
      </Menu>

      <Dialog
        open={!!verifiedFillDialog}
        onClose={() => setVerifiedFillDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 18 }}>
          Wybierz cenę dla zduplikowanych Podstaw
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: '#5F6368', fontFamily: 'Roboto, sans-serif' }}>
            W bibliotece „zweryfikowanych" znaleziono kilka pozycji o tej samej Podstawie.
            Dla każdej grupy wybierz, którą cenę wstawić do kosztorysu — albo „Pomiń"
            (wtedy żadna z dotkniętych pozycji w tej grupie nie zostanie zmieniona).
            Domyślnie zaznaczona jest najnowsza pozycja.
          </Typography>
          {verifiedFillDialog?.conflicts.map((c) => (
            <Box
              key={c.canonicalKey}
              sx={{
                mb: 2,
                p: 2,
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 1,
                bgcolor: '#fafafa',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: 'Roboto, sans-serif' }}>
                Podstawa: <strong>{c.displayPodstawa}</strong>
                {' — '}
                {c.affectedKosztorysIds.length === 1
                  ? '1 pozycja w kosztorysie'
                  : `${c.affectedKosztorysIds.length} pozycji w kosztorysie`}
                {' • '}
                {c.candidates.length} warianty cen w bibliotece
              </Typography>
              <RadioGroup
                value={String(verifiedFillDialog?.selections[c.canonicalKey] ?? 'skip')}
                onChange={(e) => {
                  const v = e.target.value;
                  setVerifiedFillDialog((prev) => {
                    if (!prev) return prev;
                    const next = { ...prev.selections };
                    next[c.canonicalKey] = v === 'skip' ? 'skip' : Number(v);
                    return { ...prev, selections: next };
                  });
                }}
              >
                {c.candidates.map((cand) => (
                  <FormControlLabel
                    key={cand.libId}
                    value={String(cand.libId)}
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif' }}>
                          <strong>{cand.formattedCena} zł</strong>
                          {cand.opis ? ` — ${cand.opis}` : ' — (brak opisu)'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#5F6368', fontFamily: 'Roboto, sans-serif' }}
                        >
                          {cand.podstawa}
                          {cand.branza ? ` • ${cand.branza}` : ''}
                          {cand.filename ? ` • ${cand.filename}` : ''}
                          {cand.updatedAt ? ` • zaktualizowano ${cand.updatedAt.slice(0, 10)}` : ''}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', mr: 0, '& .MuiFormControlLabel-label': { mt: 0.25 } }}
                  />
                ))}
                <FormControlLabel
                  value="skip"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" sx={{ color: '#5F6368', fontFamily: 'Roboto, sans-serif' }}>
                      Pomiń (nie wstawiaj ceny w tej grupie)
                    </Typography>
                  }
                />
              </RadioGroup>
            </Box>
          ))}
          {verifiedFillDialog && (
            <Typography variant="caption" sx={{ color: '#5F6368', fontFamily: 'Roboto, sans-serif' }}>
              {verifiedFillDialog.uniquePriceChanges > 0
                ? `Bez konfliktów do uzupełnienia: ${verifiedFillDialog.uniquePriceChanges}.`
                : 'Bez konfliktów do uzupełnienia: 0.'}
              {verifiedFillDialog.skippedNonEmpty > 0
                ? ` Pomijam ${verifiedFillDialog.skippedNonEmpty} pozycji z istniejącą ceną.`
                : ''}
              {verifiedFillDialog.skippedFormula > 0
                ? ` Pomijam ${verifiedFillDialog.skippedFormula} pozycji z formułą =.`
                : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVerifiedFillDialog(null)} color="inherit">
            Anuluj
          </Button>
          <Button onClick={confirmVerifiedFillDialog} variant="contained">
            Zastosuj
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={wbsDialogOpen} onClose={handleCloseWbsDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 18 }}>Numeracja WBS</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, color: '#5F6368', fontFamily: 'Roboto, sans-serif' }}>
            Jeden numer WBS na ten wiersz (np. <strong>1.2.1</strong>). Możesz wkleić kilka wartości rozdzielonych przecinkiem — zapisany zostanie{' '}
            <strong>ostatni</strong> poprawny fragment (np. „1, 1.1, 1.2” → <strong>1.2</strong>). Puste pole wyłącza WBS (suma w nagłówku jak wcześniej — do następnego działu).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Numer WBS"
            placeholder="np. 1.2.1"
            value={wbsDialogDraft}
            onChange={(e) => setWbsDialogDraft(e.target.value)}
            size="small"
            inputProps={{ maxLength: DZIAL_OUTLINE_DB_MAX }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseWbsDialog} color="inherit">
            Anuluj
          </Button>
          <Button onClick={handleSaveWbsDialog} variant="contained">
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={findReplaceOpen}
        onClose={closeFindReplace}
        variant="persistent"
        PaperProps={{
          sx: {
            width: 360,
            maxWidth: '100vw',
            top: 0,
            bottom: 40,
            height: 'auto',
            borderLeft: '1px solid rgba(0,0,0,0.12)',
            boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
          },
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              closeFindReplace();
            } else if (e.key === 'Enter') {
              const tag = (e.target as HTMLElement | null)?.tagName;
              if (tag !== 'BUTTON') {
                e.preventDefault();
                if (e.shiftKey) frFindStep(-1);
                else frFindStep(1);
              }
            }
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Znajdź i zamień
          </Typography>
          <IconButton
            size="small"
            onClick={closeFindReplace}
            aria-label="Zamknij okno Znajdź i zamień"
            title="Zamknij (Esc)"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            px: 2,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
          }}
        >
          <TextField
            size="small"
            label="Znajdź"
            placeholder="Fraza do wyszukania"
            value={frFindText}
            onChange={(e) => setFrFindText(e.target.value)}
            autoFocus
            fullWidth
          />
          <TextField
            size="small"
            label="Zamień na"
            placeholder="Nowa wartość"
            value={frReplaceText}
            onChange={(e) => setFrReplaceText(e.target.value)}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="fr-column-label">Kolumna</InputLabel>
            <Select
              labelId="fr-column-label"
              label="Kolumna"
              value={frColumn}
              onChange={(e) => setFrColumn(e.target.value as typeof frColumn)}
            >
              <MenuItem value="all">Wszystkie wybrane kolumny</MenuItem>
              {PRZEDMIAR_FIND_REPLACE_COLS.map((c) => (
                <MenuItem key={c} value={c}>
                  {PRZEDMIAR_FIND_REPLACE_COL_LABEL[c]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={frCaseSensitive}
                  onChange={(e) => setFrCaseSensitive(e.target.checked)}
                />
              }
              label="Uwzględniaj wielkość liter"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={frWholeCell}
                  onChange={(e) => setFrWholeCell(e.target.checked)}
                />
              }
              label="Dopasuj całą zawartość komórki"
            />
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontSize: '0.8125rem' }}>
            {!frFindText
              ? 'Wpisz frazę, aby rozpocząć.'
              : frMatches.length === 0
                ? 'Brak dopasowań w widocznym zakresie.'
                : `Dopasowanie ${frMatchIdx >= 0 ? frMatchIdx + 1 : 1} z ${frMatches.length}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => frFindStep(-1)}
              disabled={!frFindText || frMatches.length === 0}
            >
              Poprzednie
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => frFindStep(1)}
              disabled={!frFindText || frMatches.length === 0}
            >
              Znajdź
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              onClick={frReplaceCurrent}
              disabled={!frFindText || frMatches.length === 0}
            >
              Zamień
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={frReplaceAll}
              disabled={!frFindText || frMatches.length === 0}
            >
              Zamień wszystko
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.55)', mt: 0.5 }}>
            Uwaga: wyszukiwanie działa w bieżącym widoku (po filtrach „Szukaj” / Działu). Dla pełnej tabeli
            wyczyść filtry przed zamianą.
          </Typography>
        </Box>
      </Drawer>
    </Box>
  );
};

export default EasyEstimatePrzedmiarTab;
